import React, { useState, useCallback, useMemo, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { DndContext, DragEndEvent, DragStartEvent, DragOverlay, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove, useSortable, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { motion, AnimatePresence } from 'framer-motion';
import { PanelGroup, Panel, PanelResizeHandle } from 'react-resizable-panels';
import { Rnd } from 'react-rnd';
import PropertyPanel from './PropertyPanel';
import ContextMenu from './ContextMenu';
import { SectionMarkerBlock } from './SectionMarkerBlock';
import { MarkdownBlock } from './MarkdownBlock';
import { AIRegenerationPanel } from './AIRegenerationPanel';
import { PageNavigatorPanel } from './PageNavigatorPanel';
import type { AIPreselection } from './PageNavigatorPanel';

// Import the configuration types
import type { TemplateEditorConfig, TemplateEditorProps } from '../../types/editor';
import { templateStorageService, type TemplateData } from '../../../services/templateStorageService';
import { supabase } from '../../../../../foundation/altzero_base/base/supabase/supabaseClient';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePermissions } from '../../../../../foundation/altzero_base/base/contextapi/PermissionsContext';
import SaveTemplateModal from '../../../components/ui/SaveTemplateModal';

// Import Page Template System
import { PageSettingsPanel } from '../PageSettings';
import { PageTemplateModal } from '../ui/PageTemplateModal';
import {
  PageTemplate,
  PageSettings,
  PAGE_TEMPLATES,
  getDefaultTemplate,
  getTemplateById,
} from '../../types/pageTemplates';

import ClipboardManager from '../../utils/ClipboardManager';
import { PAGE_LAYOUT_TEMPLATES, type LayoutTemplate } from '../../data/pageLayoutTemplates';
import { StableTextarea } from '../ui/StableInput';
import { TextFormattingWrapper } from '../ui/TextFormattingWrapper';
import { GlobalTypographyModal } from '../ui/GlobalTypographyModal';
import {
  ADVANCED_BLOCK_TEMPLATES,
  ImageUploadComponent,
  SimpleChart,
  SimpleTextEditor,
  AdvancedTableControls
} from './AdvancedBlocks';
import { ExportService } from '../../services/exportService';
import { ExportDialog } from './ExportDialog';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Line, Pie } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);
import {
  Type,
  Image,
  Square,
  Table,
  Hash,
  Download,
  Upload,
  Save,
  Undo,
  Redo,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Grid3X3,
  Layers,
  Settings,
  Palette,
  Copy,
  Clipboard,
  AlignLeft,
  AlignCenter,
  AlignRight,
  RotateCw,
  FlipHorizontal,
  FlipVertical,
  Trash2,
  ArrowRight,
  ArrowLeft,
  ArrowUp,
  ArrowDown,
  Star,
  Diamond,
  Hexagon,
  Heart,
  Plus,
  ChevronDown,
  BarChart3,
  LineChart,
  PieChart,
  TrendingUp,
  Activity,
  Gauge,
  Database,
  Award,
  Quote,
  AlertCircle,
  Frame,
  Clock,
  ChevronRight,
  Zap,
  CheckCircle,
  Info,
  User,
  Mail,
  Phone,
  Calendar,
  Edit3,
  Check,
  GripVertical,
  X,
  Eye,
  Sparkles,
  FileCode2,
  Divide,
  LayoutTemplate as LayoutTemplateIcon
} from 'lucide-react';
import { PageBackgroundSidebar } from '../ui/PageBackgroundSidebar';
import { substituteBlockPlaceholders } from '../../services/blockPlaceholders';

// Types
interface TextStyle {
  fontFamily?: string;
  fontSize?: number;
  fontWeight?: 'normal' | 'bold' | '100' | '200' | '300' | '400' | '500' | '600' | '700' | '800' | '900';
  fontStyle?: 'normal' | 'italic';
  textDecoration?: 'none' | 'underline' | 'line-through';
  textAlign?: 'left' | 'center' | 'right' | 'justify';
  color?: string;
  lineHeight?: number;
  letterSpacing?: number;
}

interface EditorBlock {
  id: string;
  type: 'text' | 'image' | 'shape' | 'table' | 'placeholder' | 'chart' | 'video' | 'button' | 'divider' | 'icon' | 'qrcode' | 'signature' | 'datetime' | 'progress' | 'list' | 'bar-chart' | 'line-chart' | 'pie-chart' | 'kpi-card' | 'progress-bar' | 'gauge-chart' | 'data-table' | 'badge' | 'quote' | 'callout' | 'gradient-bg' | 'border-frame' | 'timeline' | 'accordion' | 'icon-block' | 'heading1' | 'heading2' | 'heading3' | 'paragraph' | 'variable' | 'script';
  position: { x: number; y: number };
  size: { width: number; height: number };
  content: any;
  style: any;
  textStyle?: TextStyle; // New text styling properties
  zIndex: number;
  visible?: boolean;
  locked?: boolean;
}

interface PageBackground {
  type: 'solid' | 'gradient' | 'split';
  // Solid color
  color?: string;
  // Gradient
  gradient?: {
    type: 'linear' | 'radial' | 'conic';
    angle?: number; // for linear gradients (0-360 degrees)
    stops: Array<{
      color: string;
      position: number; // 0-100%
    }>;
    // Radial gradient specific
    centerX?: number; // 0-100%
    centerY?: number; // 0-100%
    shape?: 'circle' | 'ellipse';
  };
  // Split background
  split?: {
    direction: 'horizontal' | 'vertical';
    position: number; // 0-100% where to split
    first: {
      type: 'solid' | 'gradient';
      color?: string;
      gradient?: PageBackground['gradient'];
    };
    second: {
      type: 'solid' | 'gradient';
      color?: string;
      gradient?: PageBackground['gradient'];
    };
  };
}

interface EditorPage {
  id: string;
  name: string;
  blocks: EditorBlock[];
  canvasSize: { width: number; height: number };
  background: PageBackground;
}


interface GlobalTypography {
  fontFamily: string;
  fontSize: number;
  fontWeight: string;
  lineHeight: number;
  letterSpacing: number;
  color: string;
}

interface EditorState {
  pages: EditorPage[];
  currentPageId: string;
  selectedBlockId: string | null;
  zoom: number;
  showGrid: boolean;
  previewMode: boolean;
  variables: Array<{
    name: string;
    type: 'text' | 'dropdown' | 'date' | 'number';
    options?: string[];
    defaultValue?: string;
  }>;
  globalTypography: GlobalTypography;
  // Page template settings
  pageSettings: PageSettings;
  selectedPageTemplate: PageTemplate;
}

// Block Templates - Basic + Advanced
// Script Block Component
interface ScriptBlockProps {
  content: {
    html: string;
    css: string;
    javascript: string;
    mode: 'html' | 'css' | 'javascript' | 'preview';
    placeholderValues?: Record<string, string>;
  };
  onChange: (content: any) => void;
  blockId: string;
  previewMode?: boolean;
}

const ScriptBlock: React.FC<ScriptBlockProps> = ({ content, onChange, blockId, previewMode = false }) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  // Recovery counter — when the browser resets the iframe (e.g. DOM reparenting
  // during page reorder), React won't re-apply srcDoc because the value is the
  // same.  Incrementing this key destroys the old iframe and creates a fresh one.
  const [recoveryKey, setRecoveryKey] = useState(0);
  const lastRecoveryRef = useRef(0);

  // Memoize the preview HTML so the iframe only re-renders when content actually changes.
  const previewHTML = useMemo(() => {
    const hasContent = content.html || content.css || content.javascript;
    const phValues: Record<string, string> = content.placeholderValues || {};

    // The same substitution the PDF export performs — one rule, one module, so the portal and
    // the downloaded file cannot drift again. See substituteBlockPlaceholders.
    const renderedHtml = substituteBlockPlaceholders(
      content.html ||
      (hasContent ? '' : '<div style="padding: 20px; text-align: center; color: #999; font-size: 14px;">Select this block to add HTML, CSS, and JavaScript content</div>'),
      phValues,
    );

    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <style>
    body {
      margin: 0;
      padding: 0;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      overflow: hidden;
    }
    ${content.css || ''}
  </style>
</head>
<body>
  ${renderedHtml}
  <script>
    try {
      ${content.javascript || ''}
    } catch (error) {
      console.error('Script error:', error);
    }
  </script>
</body>
</html>`;
  }, [content.html, content.css, content.javascript, content.placeholderValues]);

  // Recovery: after every render, check if the iframe is blank (caused by DOM
  // reparenting during page reorder).  If blank, force iframe recreation by
  // incrementing the key.  Throttled to at most once per second.
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!iframeRef.current) return;
      // Throttle — don't recover more than once per second
      if (Date.now() - lastRecoveryRef.current < 1000) return;
      try {
        const doc = iframeRef.current.contentDocument;
        const bodyContent = doc?.body?.innerHTML?.trim() || '';
        if (!bodyContent) {
          lastRecoveryRef.current = Date.now();
          setRecoveryKey(k => k + 1);
        }
      } catch {
        // Sandbox / cross-origin error — force recreation
        lastRecoveryRef.current = Date.now();
        setRecoveryKey(k => k + 1);
      }
    }, 200);
    return () => clearTimeout(timer);
  });

  // Click outside handler to reset iframe interaction
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        // Reset iframe interaction state
        if (iframeRef.current) {
          iframeRef.current.style.pointerEvents = 'none';
          iframeRef.current.style.zIndex = '';
        }
        // Show overlay again
        if (overlayRef.current) {
          overlayRef.current.style.display = 'flex';
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className={`w-full h-full bg-white rounded-lg overflow-hidden relative ${previewMode ? '' : 'group border border-gray-200'}`}
    >
      {/* Drag handle - only in edit mode */}
      {!previewMode && (
        <div className="absolute top-1 right-1 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="bg-gray-600 text-white px-2 py-1 rounded text-xs cursor-move select-none">
            ⋮⋮ Drag
          </div>
        </div>
      )}

      <iframe
        key={`${blockId}-${recoveryKey}`}
        ref={iframeRef}
        srcDoc={previewHTML}
        className="w-full h-full border-none"
        sandbox="allow-scripts allow-same-origin"
        title={`Script Block Preview ${blockId}`}
        style={{
          pointerEvents: previewMode ? 'none' : 'none'
        }}
      />

      {/* Click-to-enable overlay - only in edit mode */}
      {!previewMode && (
        <div
          ref={overlayRef}
          className="absolute inset-0 bg-transparent flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity cursor-pointer"
          style={{
            pointerEvents: 'auto',
            zIndex: 2,
            backgroundColor: 'rgba(0,0,0,0.05)'
          }}
          onClick={(e) => {
            e.stopPropagation();
            if (iframeRef.current) {
              iframeRef.current.style.pointerEvents = 'auto';
              iframeRef.current.style.zIndex = '10';
            }
            e.currentTarget.style.display = 'none';
          }}
        >
          <div className="bg-blue-500 text-white px-3 py-2 rounded shadow-lg text-sm">
            Click to interact with content
          </div>
        </div>
      )}
    </div>
  );
};

const BASIC_BLOCK_TEMPLATES = [
  {
    id: 'text',
    type: 'text' as const,
    name: 'Text',
    icon: Type,
    defaultSize: { width: 200, height: 50 },
    defaultContent: { text: 'New Text Block', fontSize: 16, color: '#000000' },
  },
  {
    id: 'image',
    type: 'image' as const,
    name: 'Image',
    icon: Image,
    defaultSize: { width: 200, height: 150 },
    defaultContent: { src: '', alt: 'Image' },
  },
  {
    id: 'shape',
    type: 'shape' as const,
    name: 'Shape',
    icon: Square,
    defaultSize: { width: 100, height: 100 },
    defaultContent: { shapeType: 'rectangle', fillColor: '#3b82f6', strokeColor: '#1e40af', strokeWidth: 0 },
  },
  {
    id: 'table',
    type: 'table' as const,
    name: 'Table',
    icon: Table,
    defaultSize: { width: 400, height: 250 },
    defaultContent: {
      // Enhanced structure
      rows: [
        {
          id: 'row-0',
          cells: [
            { id: 'cell-0-0', content: 'Header 1', colspan: 1, rowspan: 1 },
            { id: 'cell-0-1', content: 'Header 2', colspan: 1, rowspan: 1 },
            { id: 'cell-0-2', content: 'Header 3', colspan: 1, rowspan: 1 }
          ],
          style: {
            backgroundColor: '#f3f4f6',
            textColor: '#1f2937',
            fontWeight: 'bold',
            fontSize: 14,
            textAlign: 'left'
          }
        },
        {
          id: 'row-1',
          cells: [
            { id: 'cell-1-0', content: 'Cell 1,1', colspan: 1, rowspan: 1 },
            { id: 'cell-1-1', content: 'Cell 1,2', colspan: 1, rowspan: 1 },
            { id: 'cell-1-2', content: 'Cell 1,3', colspan: 1, rowspan: 1 }
          ]
        },
        {
          id: 'row-2',
          cells: [
            { id: 'cell-2-0', content: 'Cell 2,1', colspan: 1, rowspan: 1 },
            { id: 'cell-2-1', content: 'Cell 2,2', colspan: 1, rowspan: 1 },
            { id: 'cell-2-2', content: 'Cell 2,3', colspan: 1, rowspan: 1 }
          ]
        }
      ],
      columns: [
        { id: 'col-0', width: 120 },
        { id: 'col-1', width: 120 },
        { id: 'col-2', width: 120 }
      ],
      hasHeader: true,
      headerStyle: {
        backgroundColor: '#f3f4f6',
        textColor: '#1f2937',
        fontWeight: 'bold',
        fontSize: 14,
        textAlign: 'left'
      },
      tableStyle: {
        borderColor: '#e5e7eb',
        borderWidth: 1,
        borderStyle: 'solid'
      }
    },
  },
  {
    id: 'placeholder',
    type: 'placeholder' as const,
    name: 'Variable',
    icon: Hash,
    defaultSize: { width: 150, height: 30 },
    defaultContent: { variable: 'variable_name', defaultValue: 'Default' },
  },
];

// Advanced Shape Templates
const ADVANCED_SHAPE_TEMPLATES = [
  {
    id: 'arrow-right',
    type: 'shape' as const,
    name: 'Arrow Right',
    icon: ArrowRight,
    defaultSize: { width: 120, height: 60 },
    defaultContent: { shapeType: 'arrow-right', fillColor: '#3b82f6', strokeColor: '#1e40af', strokeWidth: 0 },
  },
  {
    id: 'arrow-left',
    type: 'shape' as const,
    name: 'Arrow Left',
    icon: ArrowLeft,
    defaultSize: { width: 120, height: 60 },
    defaultContent: { shapeType: 'arrow-left', fillColor: '#3b82f6', strokeColor: '#1e40af', strokeWidth: 0 },
  },
  {
    id: 'arrow-up',
    type: 'shape' as const,
    name: 'Arrow Up',
    icon: ArrowUp,
    defaultSize: { width: 60, height: 120 },
    defaultContent: { shapeType: 'arrow-up', fillColor: '#3b82f6', strokeColor: '#1e40af', strokeWidth: 0 },
  },
  {
    id: 'arrow-down',
    type: 'shape' as const,
    name: 'Arrow Down',
    icon: ArrowDown,
    defaultSize: { width: 60, height: 120 },
    defaultContent: { shapeType: 'arrow-down', fillColor: '#3b82f6', strokeColor: '#1e40af', strokeWidth: 0 },
  },
  {
    id: 'star',
    type: 'shape' as const,
    name: 'Star',
    icon: Star,
    defaultSize: { width: 100, height: 100 },
    defaultContent: { shapeType: 'star', fillColor: '#fbbf24', strokeColor: '#f59e0b', strokeWidth: 0 },
  },
  {
    id: 'diamond',
    type: 'shape' as const,
    name: 'Diamond',
    icon: Diamond,
    defaultSize: { width: 100, height: 100 },
    defaultContent: { shapeType: 'diamond', fillColor: '#8b5cf6', strokeColor: '#7c3aed', strokeWidth: 0 },
  },
  {
    id: 'hexagon',
    type: 'shape' as const,
    name: 'Hexagon',
    icon: Hexagon,
    defaultSize: { width: 100, height: 100 },
    defaultContent: { shapeType: 'hexagon', fillColor: '#10b981', strokeColor: '#059669', strokeWidth: 0 },
  },
  {
    id: 'heart',
    type: 'shape' as const,
    name: 'Heart',
    icon: Heart,
    defaultSize: { width: 100, height: 100 },
    defaultContent: { shapeType: 'heart', fillColor: '#ef4444', strokeColor: '#dc2626', strokeWidth: 0 },
  },
  {
    id: 'cross',
    type: 'shape' as const,
    name: 'Cross',
    icon: Plus,
    defaultSize: { width: 100, height: 100 },
    defaultContent: { shapeType: 'cross', fillColor: '#6b7280', strokeColor: '#4b5563', strokeWidth: 0 },
  },
];

const ALL_BLOCK_TEMPLATES = [...BASIC_BLOCK_TEMPLATES, ...ADVANCED_BLOCK_TEMPLATES, ...ADVANCED_SHAPE_TEMPLATES];

// 🆕 Export handle type for imperative methods
export type ModernTemplateEditorHandle = {
  updateChunk: (chunkIndex: number, processedBlocks: any[]) => void;
};

// Use the props interface from the types file
type ModernTemplateEditorProps = TemplateEditorProps;

// Default variables for templates that don't have them
const DEFAULT_TEMPLATE_VARIABLES = [
  { name: 'client_name', type: 'text', defaultValue: 'Client Name' },
  { name: 'opportunity_name', type: 'text', defaultValue: 'Opportunity Name' },
  { name: 'contact_person', type: 'text', defaultValue: 'Contact Person' },
  { name: 'email', type: 'text', defaultValue: 'contact@example.com' },
  { name: 'retailer_name', type: 'text', defaultValue: 'Retailer Name' },
  { name: 'current_date', type: 'date', defaultValue: new Date().toLocaleDateString() },
  { name: 'proposal_id', type: 'text', defaultValue: 'PROP-001' },
  { name: 'total', type: 'text', defaultValue: '0' },
  { name: 'energy', type: 'text', defaultValue: '0' },
  { name: 'environ', type: 'text', defaultValue: '0' },
  { name: 'network', type: 'text', defaultValue: '0' },
  { name: 'others', type: 'text', defaultValue: '0' },
  { name: 'peryear', type: 'text', defaultValue: '0' },

  // Retailer-specific variables (based on offer.term_months from offers table)
  // AGL Energy examples (if AGL has offers with term_months = 12, 24, 48)
  { name: 'agl_12_total', type: 'text', defaultValue: '0' },
  { name: 'agl_12_energy', type: 'text', defaultValue: '0' },
  { name: 'agl_12_environ', type: 'text', defaultValue: '0' },
  { name: 'agl_12_network', type: 'text', defaultValue: '0' },
  { name: 'agl_12_others', type: 'text', defaultValue: '0' },
  { name: 'agl_12_peryear', type: 'text', defaultValue: '0' },
  { name: 'agl_24_total', type: 'text', defaultValue: '0' },
  { name: 'agl_48_total', type: 'text', defaultValue: '0' },

  // Origin Energy examples (if Origin has offers with term_months = 12, 36)
  { name: 'origin_12_total', type: 'text', defaultValue: '0' },
  { name: 'origin_12_energy', type: 'text', defaultValue: '0' },
  { name: 'origin_12_environ', type: 'text', defaultValue: '0' },
  { name: 'origin_12_network', type: 'text', defaultValue: '0' },
  { name: 'origin_12_others', type: 'text', defaultValue: '0' },
  { name: 'origin_12_peryear', type: 'text', defaultValue: '0' },
  { name: 'origin_36_total', type: 'text', defaultValue: '0' },

  // Energy Australia examples (if EA has offers with term_months = 24, 60)
  { name: 'energyaustralia_24_total', type: 'text', defaultValue: '0' },
  { name: 'energyaustralia_24_energy', type: 'text', defaultValue: '0' },
  { name: 'energyaustralia_60_total', type: 'text', defaultValue: '0' },

  // Generic contract term variables
  { name: '12m_total', type: 'text', defaultValue: '0' },
  { name: '12m_energy', type: 'text', defaultValue: '0' },
  { name: '12m_environ', type: 'text', defaultValue: '0' },
  { name: '12m_network', type: 'text', defaultValue: '0' },
  { name: '12m_others', type: 'text', defaultValue: '0' },
  { name: '12m_peryear', type: 'text', defaultValue: '0' },
  { name: '24m_total', type: 'text', defaultValue: '0' },
  { name: '24m_energy', type: 'text', defaultValue: '0' },
  { name: '24m_environ', type: 'text', defaultValue: '0' },
  { name: '24m_network', type: 'text', defaultValue: '0' },
  { name: '24m_others', type: 'text', defaultValue: '0' },
  { name: '24m_peryear', type: 'text', defaultValue: '0' },
  { name: '36m_total', type: 'text', defaultValue: '0' },
  { name: '36m_energy', type: 'text', defaultValue: '0' },
  { name: '36m_environ', type: 'text', defaultValue: '0' },
  { name: '36m_network', type: 'text', defaultValue: '0' },
  { name: '36m_others', type: 'text', defaultValue: '0' },
  { name: '36m_peryear', type: 'text', defaultValue: '0' },
  { name: '48m_total', type: 'text', defaultValue: '0' },
  { name: '48m_energy', type: 'text', defaultValue: '0' },
  { name: '48m_environ', type: 'text', defaultValue: '0' },
  { name: '48m_network', type: 'text', defaultValue: '0' },
  { name: '48m_others', type: 'text', defaultValue: '0' },
  { name: '48m_peryear', type: 'text', defaultValue: '0' }
];

// ─────────────────────────────────────────────────────────────────────────────
// Template helpers (module-level pure utilities)
// ─────────────────────────────────────────────────────────────────────────────

/** Recursively replace all occurrences of `from` hex color with `to` in a block. */
function recolorTemplateBlock<T>(obj: T, from: string, to: string): T {
  if (typeof obj === 'string') return (obj === from ? to : obj) as unknown as T;
  if (Array.isArray(obj)) return (obj as unknown[]).map(v => recolorTemplateBlock(v, from, to)) as unknown as T;
  if (obj !== null && typeof obj === 'object') {
    const result: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
      result[k] = recolorTemplateBlock(v, from, to);
    }
    return result as unknown as T;
  }
  return obj;
}

/** Generate an SVG wireframe thumbnail from a LayoutTemplate.
 *  @param template  The template to render
 *  @param width     Target pixel width (height auto-calculated for A4 aspect ratio)
 *  @param accent    Optional accent color to substitute #3790C5 throughout
 */
function renderTemplateSVG(
  template: import('../../data/pageLayoutTemplates').LayoutTemplate,
  width: number,
  accent?: string
): React.ReactElement {
  const TEMPLATE_ACCENT = '#3790C5';
  const height = Math.round((1123 / 794) * width);

  const recolor = (color: string): string => {
    if (!accent) return color;
    const lo = color.toLowerCase().replace(/\s/g, '');
    if (lo === TEMPLATE_ACCENT.toLowerCase()) return accent;
    if (lo === '#2c73a0') return accent + 'cc';
    if (lo === '#e0f0ff' || lo === '#d4e9f7' || lo === '#cfe8f8' || lo === '#a0cce0') return accent + '22';
    return color;
  };

  const TEXT_TYPES = ['heading1', 'heading2', 'heading3', 'text', 'paragraph'];

  const els: React.ReactElement[] = [];

  // Page background
  els.push(<rect key="bg" width={794} height={1123} fill="#f9fafb" />);

  template.blocks.forEach((b, i) => {
    const s = b.style as Record<string, unknown>;
    const c = b.content as Record<string, unknown>;
    const bx = b.position.x, by = b.position.y, bw = b.size.width, bh = b.size.height;
    const rx = typeof s?.borderRadius === 'string' ? parseInt(s.borderRadius as string) || 0 : 0;

    if (b.type === 'shape') {
      const fill = recolor((c?.fillColor as string) || (s?.backgroundColor as string) || '#e2e8f0');
      const op = typeof s?.opacity === 'number' ? s.opacity : 1;
      els.push(<rect key={i} x={bx} y={by} width={bw} height={bh} fill={fill} opacity={op} rx={rx} />);

    } else if (b.type === 'divider') {
      const fill = recolor((c?.color as string) || (s?.backgroundColor as string) || '#e5e7eb');
      els.push(<rect key={i} x={bx} y={by} width={bw} height={Math.max(1, bh)} fill={fill} />);

    } else if (TEXT_TYPES.includes(b.type)) {
      const textFill = recolor((s?.color as string) || '#374151');
      const lineH = b.type === 'heading1' ? 18 : b.type === 'heading2' ? 13 : b.type === 'heading3' ? 11 : 8;
      const gap = 5;
      const numLines = Math.min(b.type.startsWith('heading') ? 2 : 4, Math.max(1, Math.floor(bh / (lineH + gap))));
      const lineEls = Array.from({ length: numLines }, (_, li) => (
        <rect
          key={li}
          x={bx + 2}
          y={by + li * (lineH + gap) + 2}
          width={li === numLines - 1 && numLines > 1 ? bw * 0.6 : bw - 4}
          height={lineH}
          fill={textFill}
          opacity={b.type.startsWith('heading') ? 0.65 : 0.35}
          rx={2}
        />
      ));
      els.push(<g key={i}>{lineEls}</g>);

    } else if (b.type === 'table') {
      const headerFill = recolor((c?.headerStyle as Record<string, unknown>)?.backgroundColor as string || TEMPLATE_ACCENT);
      els.push(
        <g key={i}>
          <rect x={bx} y={by} width={bw} height={bh} fill="#f8f9fa" stroke="#e5e7eb" strokeWidth={0.5} rx={4} />
          <rect x={bx} y={by} width={bw} height={22} fill={headerFill} opacity={0.85} rx={4} />
          {Array.from({ length: 5 }, (_, ri) => (
            <line key={ri} x1={bx} y1={by + 22 + ri * 22} x2={bx + bw} y2={by + 22 + ri * 22} stroke="#e5e7eb" strokeWidth={0.5} />
          ))}
        </g>
      );

    } else if (b.type === 'kpi-card') {
      const bgFill = recolor((s?.backgroundColor as string) || '#e0f0ff');
      els.push(
        <g key={i}>
          <rect x={bx} y={by} width={bw} height={bh} fill={bgFill} rx={8} />
          <rect x={bx + 10} y={by + 14} width={bw * 0.45} height={8} fill="#9ca3af" opacity={0.5} rx={2} />
          <rect x={bx + 10} y={by + 30} width={bw * 0.65} height={16} fill={recolor(TEMPLATE_ACCENT)} opacity={0.5} rx={2} />
        </g>
      );

    } else if (b.type === 'callout') {
      const bgFill = recolor((s?.backgroundColor as string) || '#eff6ff');
      const borderColor = recolor(TEMPLATE_ACCENT);
      els.push(
        <g key={i}>
          <rect x={bx} y={by} width={bw} height={bh} fill={bgFill} rx={6} />
          <rect x={bx} y={by} width={4} height={bh} fill={borderColor} rx={2} />
          <rect x={bx + 14} y={by + 12} width={bw * 0.4} height={9} fill="#6b7280" opacity={0.4} rx={2} />
          <rect x={bx + 14} y={by + 28} width={bw * 0.7} height={7} fill="#6b7280" opacity={0.25} rx={2} />
        </g>
      );

    } else if (b.type === 'image' || b.type === 'placeholder') {
      els.push(
        <g key={i}>
          <rect x={bx} y={by} width={bw} height={bh} fill="#e5e7eb" rx={rx} />
          <line x1={bx} y1={by} x2={bx + bw} y2={by + bh} stroke="#d1d5db" strokeWidth={0.5} />
          <line x1={bx + bw} y1={by} x2={bx} y2={by + bh} stroke="#d1d5db" strokeWidth={0.5} />
        </g>
      );

    } else if (b.type === 'bar-chart' || b.type === 'line-chart') {
      els.push(
        <g key={i}>
          <rect x={bx} y={by} width={bw} height={bh} fill="#ffffff" stroke="#e5e7eb" strokeWidth={0.5} rx={6} />
          {[0.8, 1, 0.6, 0.9].map((h, bi) => {
            const barW = bw / 5.5;
            const bx2 = bx + barW * 0.5 + bi * (bw / 4.2);
            const barH = (bh - 20) * h;
            return <rect key={bi} x={bx2} y={by + bh - 10 - barH} width={barW} height={barH} fill={recolor(TEMPLATE_ACCENT)} opacity={0.6} rx={2} />;
          })}
        </g>
      );

    } else if (b.type === 'signature') {
      els.push(
        <g key={i}>
          <rect x={bx} y={by} width={bw} height={bh} fill="none" stroke="#d1d5db" strokeWidth={1} strokeDasharray="4 3" rx={4} />
        </g>
      );

    } else {
      // Generic fallback
      const bgFill = recolor((s?.backgroundColor as string) || '#f3f4f6');
      els.push(<rect key={i} x={bx} y={by} width={bw} height={bh} fill={bgFill} opacity={0.7} rx={rx || 4} />);
    }
  });

  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 794 1123"
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: 'block' }}
    >
      {els}
    </svg>
  );
}

// 🆕 Wrap component with forwardRef to support imperative updates
const ModernTemplateEditor = forwardRef<ModernTemplateEditorHandle, ModernTemplateEditorProps>(
  ({
    config = {},
    initialData,
    readOnly = false,
    context = 'template', // Default to template context
    customToolbarItems = [],
    customSidebarPanels = [],
    onChange,
    onSave,
    onExport,
    onBlockSelect,
    onPageChange,
    className,
    exportTrigger
  }, ref) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { can } = usePermissions();

  // Editor permission levels (hierarchical — each level includes lower levels)
  // editor.view:    preview + export/download only
  // editor.layout:  + page alignment, hide/show/reorder pages
  // editor.content: + edit block text/styling/properties, copy/paste
  // editor.full:    + add/delete blocks/pages, duplicate, AI regen, backgrounds, typography
  const hasEditorView = can('editor.view') || can('editor.layout') || can('editor.content') || can('editor.full');
  const hasEditorLayout = can('editor.layout') || can('editor.content') || can('editor.full');
  const hasEditorContent = can('editor.content') || can('editor.full');
  const hasEditorFull = can('editor.full');

  // Backward-compat aliases used by child components
  const canEditContent = hasEditorFull;
  const canEditLayout = hasEditorLayout;

  // Template tracking state
  const [currentTemplateId, setCurrentTemplateId] = useState<string | null>(null);
  const [currentTemplateName, setCurrentTemplateName] = useState<string>('');
  const [currentTemplateDescription, setCurrentTemplateDescription] = useState<string>('');
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  // Page template modal state
  const [showPageTemplateModal, setShowPageTemplateModal] = useState(false);

  // AI regeneration panel state
  const [aiPreselection, setAIPreselection] = useState<AIPreselection>(null);

  // Left sidebar tab: navigator, blocks, or ai
  const [sidebarTab, setSidebarTab] = useState<'navigator' | 'blocks' | 'ai'>('navigator');

  // Initialize editor state with config and initial data
  // Initialize default page template
  const defaultTemplate = getDefaultTemplate();
  const defaultPageSettings: PageSettings = {
    templateId: defaultTemplate.id,
    orientation: defaultTemplate.orientation,
    backgroundColor: '#ffffff',
    showGrid: true,
    gridSize: 20,
    snapToGrid: false,
  };

  const [editorState, setEditorState] = useState<EditorState>(() => {
    if (initialData) {
      // Ensure required fields exist in initialData
      return {
        ...initialData,
        pages: initialData.pages || [{
          id: 'page-1',
          name: 'Page 1',
          blocks: [],
          canvasSize: defaultTemplate.dimensions,
          background: { type: 'solid', color: '#ffffff' }
        }],
        currentPageId: initialData.currentPageId || 'page-1',
        previewMode: initialData.previewMode ?? false,
        variables: (initialData.variables && initialData.variables.length > 0)
          ? initialData.variables
          : (config.predefinedVariables || DEFAULT_TEMPLATE_VARIABLES),
        // Add page template settings if not present
        pageSettings: initialData.pageSettings || defaultPageSettings,
        selectedPageTemplate: initialData.selectedPageTemplate || defaultTemplate,
      };
    }

    return {
      pages: [{
        id: 'page-1',
        name: 'Page 1',
        blocks: [],
        canvasSize: defaultTemplate.dimensions,
        background: {
          type: 'solid',
          color: '#ffffff'
        }
      }],
      currentPageId: 'page-1',
      selectedBlockId: null,
      zoom: 1,
      showGrid: config.showGrid ?? true,
      showPageNumbers: true,
      showTemplatePicker: false,
      previewMode: false,
      variables: config.predefinedVariables || DEFAULT_TEMPLATE_VARIABLES,
      globalTypography: {
        fontFamily: 'Inter, sans-serif',
        fontSize: 16,
        fontWeight: '400',
        lineHeight: 1.4,
        letterSpacing: 0,
        color: '#000000'
      },
      pageSettings: defaultPageSettings,
      selectedPageTemplate: defaultTemplate,
    };
  });

  // 🆕 Expose imperative methods for live streaming updates
  useImperativeHandle(ref, () => ({
    updateChunk: (chunkIndex: number, processedBlocks: any[]) => {
      setEditorState((prev) => {
        // Calculate which blocks across all pages correspond to this chunk
        const BLOCKS_PER_CHUNK = 10;
        const startIndex = chunkIndex * BLOCKS_PER_CHUNK;

        // Flatten all blocks from pages
        const allBlocks: any[] = [];
        (prev.pages || []).forEach((page: any) => {
          allBlocks.push(...(page.blocks || []));
        });

        // Update blocks in the flattened array
        processedBlocks.forEach((block, idx) => {
          const blockIndex = startIndex + idx;
          if (blockIndex < allBlocks.length) {
            allBlocks[blockIndex] = block;
          }
        });

        // Reconstruct pages with updated blocks
        const updatedPages = [...(prev.pages || [])];
        let blockIndex = 0;
        updatedPages.forEach((page: any) => {
          const blockCount = page.blocks?.length || 0;
          page.blocks = allBlocks.slice(blockIndex, blockIndex + blockCount);
          blockIndex += blockCount;
        });

        return {
          ...prev,
          pages: updatedPages,
        };
      });
    },
  }));

  // Call onChange callback when editor state changes
  useEffect(() => {
    onChange?.(editorState);
  }, [editorState, onChange]);

  // Call onBlockSelect callback when selected block changes
  useEffect(() => {
    onBlockSelect?.(editorState.selectedBlockId);
  }, [editorState.selectedBlockId, onBlockSelect]);

  // Call onPageChange callback when current page changes
  useEffect(() => {
    onPageChange?.(editorState.currentPageId);
  }, [editorState.currentPageId, onPageChange]);

  // Load template data from navigation state (when coming from Templates page) - ONLY ONCE
  useEffect(() => {
    const loadTemplateFromState = () => {
      const state = location.state as any;
      if (state?.templateData && state?.templateId && !currentTemplateId) {
        console.log('📥 Loading template from navigation state:', state.templateData.name);

        // Set template tracking state
        setCurrentTemplateId(state.templateId);
        setCurrentTemplateName(state.templateData.name || '');
        setCurrentTemplateDescription(state.templateData.description || '');

        // Load the template data into the editor
        if (state.templateData.editorState) {
          // Ensure variables exist when loading from navigation state.
          // Also back-fill any runtime fields that old saved templates lack.
          const loadedState = {
            ...state.templateData.editorState,
            variables: (state.templateData.editorState.variables && state.templateData.editorState.variables.length > 0)
              ? state.templateData.editorState.variables
              : DEFAULT_TEMPLATE_VARIABLES,
            // Old templates were saved before showPageNumbers existed → default true
            showPageNumbers: state.templateData.editorState.showPageNumbers ?? true,
          };

          setEditorState(loadedState);
        }

        // Clear the navigation state to prevent reloading on refresh
        window.history.replaceState({}, document.title);
      }
    };

    loadTemplateFromState();
  }, []); // Only run once on mount, not on location.state changes to prevent tab switching issues

  // Auto-scroll selected block into view
  useEffect(() => {
    if (editorState.selectedBlockId) {
      // Small delay to ensure the block is rendered and selected
      setTimeout(() => {
        const blockElement = document.querySelector(`[data-block-id="${editorState.selectedBlockId}"]`);
        if (blockElement) {
          blockElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'center'
          });
        }
      }, 100);
    }
  }, [editorState.selectedBlockId]);

  // State for table variable dropdown
  const [tableVariableDropdown, setTableVariableDropdown] = useState<string | null>(null);
  const [selectedTableCell, setSelectedTableCell] = useState<string | null>(null);
  const [advancedTableEditor, setAdvancedTableEditor] = useState<string | null>(null);

  // State for page background panel
  const [showPageBackgroundPanel, setShowPageBackgroundPanel] = useState(false);

  // State for global typography modal
  const [showGlobalTypographyModal, setShowGlobalTypographyModal] = useState(false);

  // State for multi-block selection
  const [selectedBlockIds, setSelectedBlockIds] = useState<string[]>([]);

  // History management for undo/redo
  const [history, setHistory] = useState<EditorState[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  // State for advanced blocks toggle
  const [showAdvancedBlocks, setShowAdvancedBlocks] = useState<boolean>(false);

  // State for export dialog
  const [showExportDialog, setShowExportDialog] = useState(false);

  // Template picker local state
  const [tplCategory, setTplCategory] = useState<'all' | 'document' | 'data' | 'design'>('all');
  const [confirmReplace, setConfirmReplace] = useState<LayoutTemplate | null>(null);
  const [previewTemplate, setPreviewTemplate] = useState<LayoutTemplate | null>(null);

  // Context menu state
  const [contextMenu, setContextMenu] = useState<{
    isOpen: boolean;
    position: { x: number; y: number };
    targetBlockId?: string;
  }>({
    isOpen: false,
    position: { x: 0, y: 0 }
  });

  // Clipboard manager instance
  const clipboardManager = useMemo(() => ClipboardManager.getInstance(), []);

  // Helper functions for page management
  const getCurrentPage = useCallback(() => {
    if (!editorState.pages || editorState.pages.length === 0) return null;
    return editorState.pages.find(page => page.id === editorState.currentPageId) || editorState.pages[0];
  }, [editorState.pages, editorState.currentPageId]);

  // Memoize selectedBlock to prevent unnecessary re-renders and cursor jumping
  const selectedBlock = useMemo(() => {
    if (!editorState.selectedBlockId) return null;
    const currentPage = getCurrentPage();
    return currentPage.blocks.find(b => b.id === editorState.selectedBlockId) || null;
  }, [editorState.selectedBlockId, getCurrentPage]);

  // Helper function to generate CSS background styles from PageBackground object
  const getBackgroundStyle = useCallback((background: PageBackground): React.CSSProperties => {
    if (!background || !background.type) {
      return { backgroundColor: '#ffffff' };
    }
    switch (background.type) {
      case 'solid':
        return { backgroundColor: background.color || '#ffffff' };

      case 'gradient':
        if (!background.gradient) return { backgroundColor: '#ffffff' };
        const { gradient } = background;
        const stops = gradient.stops
          .sort((a, b) => a.position - b.position)
          .map(stop => `${stop.color} ${stop.position}%`)
          .join(', ');

        switch (gradient.type) {
          case 'linear':
            return { background: `linear-gradient(${gradient.angle || 0}deg, ${stops})` };
          case 'radial':
            const centerX = gradient.centerX || 50;
            const centerY = gradient.centerY || 50;
            const shape = gradient.shape || 'ellipse';
            return { background: `radial-gradient(${shape} at ${centerX}% ${centerY}%, ${stops})` };
          case 'conic':
            const cCenterX = gradient.centerX || 50;
            const cCenterY = gradient.centerY || 50;
            return { background: `conic-gradient(from ${gradient.angle || 0}deg at ${cCenterX}% ${cCenterY}%, ${stops})` };
          default:
            return { backgroundColor: '#ffffff' };
        }

      case 'split':
        if (!background.split) return { backgroundColor: '#ffffff' };
        const { split } = background;

        const firstBg = split.first.type === 'solid'
          ? split.first.color || '#ffffff'
          : (() => {
              if (!split.first.gradient) return '#ffffff';
              const stops = split.first.gradient.stops
                .sort((a, b) => a.position - b.position)
                .map(stop => `${stop.color} ${stop.position}%`)
                .join(', ');
              switch (split.first.gradient.type) {
                case 'linear':
                  return `linear-gradient(${split.first.gradient.angle || 0}deg, ${stops})`;
                case 'radial':
                  const centerX = split.first.gradient.centerX || 50;
                  const centerY = split.first.gradient.centerY || 50;
                  const shape = split.first.gradient.shape || 'ellipse';
                  return `radial-gradient(${shape} at ${centerX}% ${centerY}%, ${stops})`;
                case 'conic':
                  const cCenterX = split.first.gradient.centerX || 50;
                  const cCenterY = split.first.gradient.centerY || 50;
                  return `conic-gradient(from ${split.first.gradient.angle || 0}deg at ${cCenterX}% ${cCenterY}%, ${stops})`;
                default:
                  return '#ffffff';
              }
            })();

        const secondBg = split.second.type === 'solid'
          ? split.second.color || '#ffffff'
          : (() => {
              if (!split.second.gradient) return '#ffffff';
              const stops = split.second.gradient.stops
                .sort((a, b) => a.position - b.position)
                .map(stop => `${stop.color} ${stop.position}%`)
                .join(', ');
              switch (split.second.gradient.type) {
                case 'linear':
                  return `linear-gradient(${split.second.gradient.angle || 0}deg, ${stops})`;
                case 'radial':
                  const centerX = split.second.gradient.centerX || 50;
                  const centerY = split.second.gradient.centerY || 50;
                  const shape = split.second.gradient.shape || 'ellipse';
                  return `radial-gradient(${shape} at ${centerX}% ${centerY}%, ${stops})`;
                case 'conic':
                  const cCenterX = split.second.gradient.centerX || 50;
                  const cCenterY = split.second.gradient.centerY || 50;
                  return `conic-gradient(from ${split.second.gradient.angle || 0}deg at ${cCenterX}% ${cCenterY}%, ${stops})`;
                default:
                  return '#ffffff';
              }
            })();

        const direction = split.direction === 'horizontal' ? '180deg' : '90deg';
        return { background: `linear-gradient(${direction}, ${firstBg} ${split.position}%, ${secondBg} ${split.position}%)` };

      default:
        return { backgroundColor: '#ffffff' };
    }
  }, []);

  // Process variables in text content
  const processVariables = useCallback((text: string) => {
    if (!text) return text;

    let processedText = text;
    // Ensure variables array exists before processing
    const variables = editorState.variables || [];
    variables.forEach(variable => {
      const regex = new RegExp(`{{${variable.name}}}`, 'g');
      const value = variable.defaultValue || `[${variable.name}]`;
      processedText = processedText.replace(regex, value);
    });

    return processedText;
  }, [editorState.variables]);

  // Insert variable at cursor position
  const insertVariable = useCallback((variableName: string, targetElement?: HTMLElement) => {
    const variableText = `{{${variableName}}}`;

    if (targetElement && targetElement.contentEditable === 'true') {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        range.deleteContents();
        range.insertNode(document.createTextNode(variableText));
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range);

        // Trigger input event to update content
        const event = new Event('input', { bubbles: true });
        targetElement.dispatchEvent(event);
      }
    }
  }, []);

  // Page template handlers
  const handlePageSettingsChange = useCallback((newSettings: PageSettings) => {
    setEditorState(prev => ({
      ...prev,
      pageSettings: newSettings,
      showGrid: newSettings.showGrid,
      // Update current page background color if it changed
      pages: prev.pages.map(page =>
        page.id === prev.currentPageId && prev.pageSettings?.backgroundColor !== newSettings.backgroundColor
          ? {
              ...page,
              background: {
                ...page.background,
                color: newSettings.backgroundColor
              }
            }
          : page
      )
    }));
  }, []);

  const handlePageTemplateChange = useCallback((template: PageTemplate) => {
    const currentPage = getCurrentPage();
    if (!currentPage) return;

    // Update the current page with new template dimensions
    setEditorState(prev => ({
      ...prev,
      selectedPageTemplate: template,
      pages: prev.pages.map(page =>
        page.id === prev.currentPageId
          ? {
              ...page,
              canvasSize: template.dimensions,
              background: {
                ...page.background,
                color: prev.pageSettings?.backgroundColor || '#ffffff'
              }
            }
          : page
      )
    }));

    // Note: saveToHistory will be called after this function is defined
  }, [getCurrentPage]);

  // Show page template selection modal
  const showAddPageModal = useCallback(() => {
    setShowPageTemplateModal(true);
  }, []);

  // Handle template selection from modal
  const handleTemplateSelection = useCallback((template: PageTemplate) => {
    const newPageId = `page-${Date.now()}`;

    const newPage: EditorPage = {
      id: newPageId,
      name: `Page ${editorState.pages.length + 1}`,
      blocks: [],
      canvasSize: template.dimensions,
      background: {
        type: 'solid',
        color: editorState.pageSettings?.backgroundColor || '#ffffff'
      }
    };

    setEditorState(prev => ({
      ...prev,
      pages: [...prev.pages, newPage],
      currentPageId: newPageId,
      selectedBlockId: null,
      selectedPageTemplate: template, // Update selected template
    }));

    setShowPageTemplateModal(false);
  }, [editorState.pages?.length, editorState.pageSettings?.backgroundColor]);

  // Legacy addPage function for backward compatibility
  const addPage = useCallback(() => {
    showAddPageModal();
  }, [showAddPageModal]);

  // Convert TextStyle to CSS - modified to work with global typography
  const getTextStyleCSS = useCallback((textStyle: TextStyle = {}, useGlobalTypography: boolean = true): React.CSSProperties => {
    const style: React.CSSProperties = {
      fontFamily: textStyle.fontFamily || (useGlobalTypography ? undefined : 'Inter, sans-serif'),
      // Always include fontSize if explicitly set in textStyle, regardless of global typography
      fontSize: textStyle.fontSize ? `${textStyle.fontSize}px` : (useGlobalTypography ? undefined : '16px'),
      fontWeight: textStyle.fontWeight || (useGlobalTypography ? undefined : 'normal'),
      fontStyle: textStyle.fontStyle || 'normal',
      textDecoration: textStyle.textDecoration || 'none',
      textAlign: textStyle.textAlign || 'left',
      color: textStyle.color || (useGlobalTypography ? undefined : '#000000'),
      lineHeight: textStyle.lineHeight || (useGlobalTypography ? undefined : 1.4),
      letterSpacing: textStyle.letterSpacing ? `${textStyle.letterSpacing}px` : (useGlobalTypography ? undefined : 'normal'),
    };

    // Remove undefined values to let global typography take precedence, but keep explicitly set values
    const filteredStyle = Object.fromEntries(Object.entries(style).filter(([_, value]) => value !== undefined));

    // Always include fontSize if it's explicitly set in textStyle (this overrides global typography)
    if (textStyle.fontSize) {
      filteredStyle.fontSize = `${textStyle.fontSize}px`;
    }

    return filteredStyle;
  }, []);

  // Update page background
  const updatePageBackground = useCallback((background: PageBackground) => {
    setEditorState(prev => ({
      ...prev,
      pages: prev.pages.map(page =>
        page.id === prev.currentPageId
          ? { ...page, background }
          : page
      )
    }));
  }, []);



  const deletePage = useCallback((pageId: string) => {
    if (editorState.pages.length <= 1) return; // Don't delete the last page

    const newPages = editorState.pages.filter(page => page.id !== pageId);
    const newCurrentPageId = pageId === editorState.currentPageId
      ? newPages[0].id
      : editorState.currentPageId;

    setEditorState(prev => ({
      ...prev,
      pages: newPages,
      currentPageId: newCurrentPageId,
      selectedBlockId: null,
    }));
  }, [editorState.pages, editorState.currentPageId]);

  // Duplicate page
  const duplicatePage = useCallback((pageId: string) => {
    setEditorState(prev => {
      const pageToDuplicate = prev.pages.find(page => page.id === pageId);
      if (!pageToDuplicate) return prev;

      const newPageId = `page-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const duplicatedBlocks = pageToDuplicate.blocks.map(block => ({
        ...block,
        id: `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
      }));

      const duplicatedPage = {
        ...pageToDuplicate,
        id: newPageId,
        name: `Page ${prev.pages.length + 1}`,
        blocks: duplicatedBlocks
      };

      const pageIndex = prev.pages.findIndex(page => page.id === pageId);
      const newPages = [
        ...prev.pages.slice(0, pageIndex + 1),
        duplicatedPage,
        ...prev.pages.slice(pageIndex + 1)
      ];

      return {
        ...prev,
        pages: newPages,
        currentPageId: newPageId,
        selectedBlockId: null
      };
    });
  }, []);

  const switchPage = useCallback((pageId: string) => {
    setEditorState(prev => ({
      ...prev,
      currentPageId: pageId,
      selectedBlockId: null,
    }));
  }, []);

  // Helper function to find which page a block belongs to
  const findPageByBlockId = useCallback((blockId: string): string | null => {
    for (const page of editorState.pages) {
      if (page.blocks.some(block => block.id === blockId)) {
        return page.id;
      }
    }
    return null;
  }, [editorState.pages]);

  const renamePage = useCallback((pageId: string, newName: string) => {
    setEditorState(prev => ({
      ...prev,
      pages: prev.pages.map(page =>
        page.id === pageId ? { ...page, name: newName } : page
      ),
    }));
  }, []);

  // Reorder pages function
  const reorderPages = useCallback((oldIndex: number, newIndex: number) => {
    setEditorState(prev => ({
      ...prev,
      pages: arrayMove(prev.pages, oldIndex, newIndex),
    }));
  }, []);

  // Apply AI-regenerated content to blocks across all pages
  const handleApplyRegeneration = useCallback((regeneratedBlocks: Array<{ blockId: string; newText: string }>) => {
    setEditorState(prev => ({
      ...prev,
      pages: prev.pages.map(page => ({
        ...page,
        blocks: page.blocks.map(block => {
          const regen = regeneratedBlocks.find(r => r.blockId === block.id);
          if (!regen) return block;
          // Apply new text depending on block type
          if (block.type === 'markdown') {
            return { ...block, content: { ...block.content, markdown: regen.newText } };
          }
          return {
            ...block,
            content: {
              ...block.content,
              richText: regen.newText,
              text: regen.newText.replace(/<[^>]*>/g, ''),
            },
          };
        }),
      })),
    }));
  }, []);

  // Scroll the canvas area to bring a page into view
  const scrollToPage = useCallback((pageId: string) => {
    requestAnimationFrame(() => {
      const container = document.querySelector('.template-editor-canvas-container');
      const pageEl = document.querySelector(`[data-page-id="${pageId}"]`);
      if (container && pageEl) {
        const containerRect = container.getBoundingClientRect();
        const pageRect = pageEl.getBoundingClientRect();
        container.scrollBy({ top: pageRect.top - containerRect.top - 24, behavior: 'smooth' });
      }
    });
  }, []);

  // Navigate to a page AND scroll to it
  const navigateToPage = useCallback((pageId: string) => {
    switchPage(pageId);
    scrollToPage(pageId);
  }, [switchPage, scrollToPage]);

  // Switch to AI tab pre-targeted at a page or section from the navigator
  const handleAITriggerFromNavigator = useCallback((preselection: AIPreselection) => {
    setAIPreselection(preselection);
    setSidebarTab('ai');
  }, []);

  // Inline editable page header shown above each page canvas
  const PageHeaderEditable: React.FC<{
    page: EditorPage;
    isActive: boolean;
    pageIndex: number;
    onSwitch: () => void;
    onDuplicate: () => void;
    onDelete: () => void;
    onRename: (newName: string) => void;
    canDelete: boolean;
  }> = ({ page, isActive, pageIndex, onSwitch, onDuplicate, onDelete, onRename, canDelete }) => {
    const [isEditingName, setIsEditingName] = useState(false);
    const [nameVal, setNameVal] = useState(page.name);
    const nameInputRef = React.useRef<HTMLInputElement>(null);

    React.useEffect(() => {
      if (!isEditingName) setNameVal(page.name);
    }, [page.name, isEditingName]);

    React.useEffect(() => {
      if (isEditingName && nameInputRef.current) {
        nameInputRef.current.focus();
        nameInputRef.current.select();
      }
    }, [isEditingName]);

    const commit = () => {
      const trimmed = nameVal.trim() || `Page ${pageIndex + 1}`;
      onRename(trimmed);
      setIsEditingName(false);
    };

    return (
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold text-gray-400 uppercase tracking-wide">P{pageIndex + 1}</span>
          {isEditingName ? (
            <input
              ref={nameInputRef}
              value={nameVal}
              onChange={(e) => setNameVal(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); commit(); }
                if (e.key === 'Escape') { setNameVal(page.name); setIsEditingName(false); }
              }}
              onBlur={commit}
              className="text-base font-semibold text-gray-800 border-b-2 border-blue-400 bg-transparent outline-none px-1 min-w-0 w-48"
            />
          ) : (
            <button
              onDoubleClick={() => setIsEditingName(true)}
              onClick={onSwitch}
              className="text-base font-semibold text-gray-700 hover:text-gray-900 transition-colors group flex items-center gap-1.5"
              title="Double-click to rename"
            >
              {page.name}
              <Edit3 className="w-3.5 h-3.5 text-gray-300 group-hover:text-gray-500 transition-colors" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onSwitch}
            className={`px-3 py-1 text-xs rounded-md font-medium transition-colors ${
              isActive
                ? 'bg-blue-500 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
            }`}
          >
            {isActive ? 'Editing' : 'Edit'}
          </button>
          <button
            onClick={onDuplicate}
            className="px-2.5 py-1 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-colors border border-transparent hover:border-blue-200"
          >
            Duplicate
          </button>
          {canDelete && (
            <button
              onClick={onDelete}
              className="px-2.5 py-1 text-xs text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors border border-transparent hover:border-red-200"
            >
              Delete
            </button>
          )}
        </div>
      </div>
    );
  };

  // Simplified Sortable Page Item Component
  const SortablePageItem: React.FC<{
    pageNumber: number;
    pageName: string;
    isActive: boolean;
    onSwitch: () => void;
    onDuplicate: () => void;
    onDelete: () => void;
    onRename: (newName: string) => void;
    canDelete: boolean;
    pageId: string;
  }> = ({ pageNumber, pageName, isActive, onSwitch, onDuplicate, onDelete, onRename, canDelete, pageId }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: pageId });

    const [isRenamingPage, setIsRenamingPage] = useState(false);
    const [renameValue, setRenameValue] = useState(pageName);
    const renameInputRef = React.useRef<HTMLInputElement>(null);

    // Sync rename value when pageName prop changes
    React.useEffect(() => {
      if (!isRenamingPage) setRenameValue(pageName);
    }, [pageName, isRenamingPage]);

    React.useEffect(() => {
      if (isRenamingPage && renameInputRef.current) {
        renameInputRef.current.focus();
        renameInputRef.current.select();
      }
    }, [isRenamingPage]);

    const commitRename = () => {
      const trimmed = renameValue.trim() || `Page ${pageNumber}`;
      onRename(trimmed);
      setIsRenamingPage(false);
    };

    const cancelRename = () => {
      setRenameValue(pageName);
      setIsRenamingPage(false);
    };

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    };

    return (
      <div
        ref={setNodeRef}
        style={style}
        className={`flex items-center bg-white border rounded-md shadow-sm min-w-0 flex-shrink-0 ${
          isActive ? 'border-blue-400' : 'border-gray-200'
        }`}
      >
        {/* Drag Handle */}
        <div
          {...attributes}
          {...listeners}
          className="flex items-center justify-center w-6 h-8 text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing"
        >
          <GripVertical className="w-3 h-3" />
        </div>

        {/* Page number badge */}
        <button
          onClick={onSwitch}
          className={`flex items-center justify-center w-6 h-6 mx-1 text-xs font-bold rounded-full flex-shrink-0 ${
            isActive ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-500'
          }`}
          title="Switch to this page"
        >
          {pageNumber}
        </button>

        {/* Page name — editable or static */}
        {isRenamingPage ? (
          <input
            ref={renameInputRef}
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); commitRename(); }
              if (e.key === 'Escape') cancelRename();
            }}
            onBlur={commitRename}
            className="px-1 py-0.5 text-xs border border-blue-400 rounded outline-none w-24 min-w-0"
            style={{ maxWidth: '120px' }}
          />
        ) : (
          <button
            onClick={onSwitch}
            onDoubleClick={() => setIsRenamingPage(true)}
            className={`px-2 py-1 text-xs font-medium truncate max-w-[100px] ${
              isActive ? 'text-blue-700' : 'text-gray-700 hover:text-gray-900'
            }`}
            title={`${pageName} — double-click to rename`}
          >
            {pageName}
          </button>
        )}

        {/* Action Buttons */}
        <div className="flex items-center px-1 gap-0.5">
          {!isRenamingPage && (
            <button
              onClick={() => setIsRenamingPage(true)}
              className="p-1 text-gray-400 hover:text-gray-700"
              title="Rename page"
            >
              <Edit3 className="w-3 h-3" />
            </button>
          )}
          <button
            onClick={onDuplicate}
            className="p-1 text-blue-400 hover:text-blue-700"
            title="Duplicate page"
          >
            <Copy className="w-3 h-3" />
          </button>
          {canDelete && (
            <button
              onClick={onDelete}
              className="p-1 text-red-400 hover:text-red-700"
              title="Delete page"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>
    );
  };

  const [draggedBlock, setDraggedBlock] = useState<EditorBlock | null>(null);

  // Drag and drop sensors for page reordering
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle page drag end
  const handlePageDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = editorState.pages.findIndex(page => page.id === active.id);
      const newIndex = editorState.pages.findIndex(page => page.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        reorderPages(oldIndex, newIndex);
      }
    }
  };

  // Add new block
  const addBlock = useCallback((template: typeof ALL_BLOCK_TEMPLATES[0]) => {
    const currentPage = getCurrentPage();
    const newBlock: EditorBlock = {
      id: `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: template.type,
      position: {
        x: Math.random() * (currentPage.canvasSize.width - template.defaultSize.width),
        y: Math.random() * (currentPage.canvasSize.height - template.defaultSize.height)
      },
      size: template.defaultSize,
      content: template.defaultContent,
      style: {
        backgroundColor: 'transparent',
        border: getInlineBorderStyle('1px solid #e5e7eb'),
        borderRadius: '4px',
        padding: '8px',
      },
      zIndex: currentPage.blocks.length + 1,
    };

    setEditorState(prev => ({
      ...prev,
      pages: prev.pages.map(page =>
        page.id === prev.currentPageId
          ? { ...page, blocks: [...page.blocks, newBlock] }
          : page
      ),
      selectedBlockId: newBlock.id,
    }));
  }, [getCurrentPage]);

  // Update block - simple version without history complications
  const updateBlock = useCallback((blockId: string, updates: Partial<EditorBlock>) => {
    setEditorState(prev => ({
      ...prev,
      pages: prev.pages.map(page =>
        page.id === prev.currentPageId
          ? {
              ...page,
              blocks: page.blocks.map(block =>
                block.id === blockId ? { ...block, ...updates } : block
              )
            }
          : page
      ),
    }));
  }, []);

  // Update global typography
  const updateGlobalTypography = useCallback((updates: Partial<GlobalTypography>) => {
    setEditorState(prev => ({
      ...prev,
      globalTypography: { ...prev.globalTypography, ...updates }
    }));
  }, []);

  // Delete block(s) - supports single or multiple blocks
  const deleteBlock = useCallback((blockId?: string) => {
    const blocksToDelete = blockId ? [blockId] : selectedBlockIds;

    if (blocksToDelete.length === 0) return;

    setEditorState(prev => ({
      ...prev,
      pages: prev.pages.map(page =>
        page.id === prev.currentPageId
          ? { ...page, blocks: page.blocks.filter(block => !blocksToDelete.includes(block.id)) }
          : page
      ),
      selectedBlockId: blocksToDelete.includes(prev.selectedBlockId || '') ? null : prev.selectedBlockId,
    }));

    // Clear multi-selection
    setSelectedBlockIds([]);
  }, [selectedBlockIds]);

  // Duplicate block(s) - supports single or multiple blocks
  const duplicateBlocks = useCallback(() => {
    if (selectedBlockIds.length === 0) return;

    setEditorState(prev => {
      const currentPage = prev.pages.find(page => page.id === prev.currentPageId);
      if (!currentPage) return prev;

      const blocksToDuplicate = currentPage.blocks.filter(block => selectedBlockIds.includes(block.id));
      const duplicatedBlocks = blocksToDuplicate.map(block => ({
        ...block,
        id: `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        position: {
          x: block.position.x + 20,
          y: block.position.y + 20
        }
      }));

      const updatedPages = prev.pages.map(page => {
        if (page.id === prev.currentPageId) {
          return {
            ...page,
            blocks: [...page.blocks, ...duplicatedBlocks]
          };
        }
        return page;
      });

      return {
        ...prev,
        pages: updatedPages,
        selectedBlockId: duplicatedBlocks.length > 0 ? duplicatedBlocks[0].id : prev.selectedBlockId
      };
    });

    // Update selection to duplicated blocks
    setTimeout(() => {
      const currentPage = getCurrentPage();
      const duplicatedBlockIds = currentPage.blocks
        .filter(block => block.position.x % 20 === 0 && block.position.y % 20 === 0)
        .slice(-selectedBlockIds.length)
        .map(block => block.id);
      setSelectedBlockIds(duplicatedBlockIds);
    }, 100);
  }, [selectedBlockIds, getCurrentPage]);

  // Select block with multi-selection support
  const selectBlock = useCallback((blockId: string | null, isMultiSelect: boolean = false) => {
    if (blockId === null) {
      // Clear all selections
      setEditorState(prev => ({
        ...prev,
        selectedBlockId: null
      }));
      setSelectedBlockIds([]);
      return;
    }

    // Auto-switch to the page containing the selected block
    const blockPageId = findPageByBlockId(blockId);
    if (blockPageId && blockPageId !== editorState.currentPageId) {
      // Switch to the page containing this block
      setEditorState(prev => ({
        ...prev,
        currentPageId: blockPageId,
        selectedBlockId: blockId
      }));
      setSelectedBlockIds([blockId]);
      return;
    }

    if (isMultiSelect) {
      // Multi-select with Ctrl+click
      setSelectedBlockIds(prev => {
        if (prev.includes(blockId)) {
          // Remove from selection
          const newSelection = prev.filter(id => id !== blockId);
          setEditorState(prevState => ({
            ...prevState,
            selectedBlockId: newSelection.length > 0 ? newSelection[newSelection.length - 1] : null
          }));
          return newSelection;
        } else {
          // Add to selection
          const newSelection = [...prev, blockId];
          setEditorState(prevState => ({
            ...prevState,
            selectedBlockId: blockId
          }));
          return newSelection;
        }
      });
    } else {
      // Single select
      setEditorState(prev => ({
        ...prev,
        selectedBlockId: blockId
      }));
      setSelectedBlockIds([blockId]);
    }
  }, [findPageByBlockId, editorState.currentPageId]);

  // Zoom controls
  const zoomIn = useCallback(() => {
    setEditorState(prev => ({ ...prev, zoom: Math.min(prev.zoom * 1.2, 3) }));
  }, []);

  const zoomOut = useCallback(() => {
    setEditorState(prev => ({ ...prev, zoom: Math.max(prev.zoom / 1.2, 0.1) }));
  }, []);

  // Fit to screen - automatically adjust zoom to fit canvas in viewport
  const fitToScreen = useCallback(() => {
    const currentPage = getCurrentPage();
    const canvasContainer = document.querySelector('.template-editor-canvas-container');

    if (canvasContainer && currentPage) {
      const containerRect = canvasContainer.getBoundingClientRect();
      const containerWidth = containerRect.width - 40; // Account for padding
      const containerHeight = containerRect.height - 40; // Account for padding

      const scaleX = containerWidth / currentPage.canvasSize.width;
      const scaleY = containerHeight / currentPage.canvasSize.height;
      const optimalZoom = Math.min(scaleX, scaleY, 1); // Don't zoom in beyond 100%

      setEditorState(prev => ({ ...prev, zoom: Math.max(optimalZoom, 0.1) }));
    }
  }, [getCurrentPage]);

  // Toggle grid
  const toggleGrid = useCallback(() => {
    setEditorState(prev => ({ ...prev, showGrid: !prev.showGrid }));
  }, []);

  // Toggle preview mode
  const togglePreviewMode = useCallback(() => {
    setEditorState(prev => ({
      ...prev,
      previewMode: !prev.previewMode,
      selectedBlockId: null // Clear selection when entering preview mode
    }));
  }, []);

  // Helper function to get border styles based on preview mode
  const getBorderStyle = useCallback((defaultBorder: string = 'border border-gray-200') => {
    return editorState.previewMode ? '' : defaultBorder;
  }, [editorState.previewMode]);

  // Helper function to get inline border styles based on preview mode
  const getInlineBorderStyle = useCallback((defaultBorder: string = '1px solid #e5e7eb') => {
    return editorState.previewMode ? 'none' : defaultBorder;
  }, [editorState.previewMode]);

  // Helper function to get shadow styles based on preview mode
  const getShadowStyle = useCallback((defaultShadow: string = 'shadow-sm') => {
    return editorState.previewMode ? '' : defaultShadow;
  }, [editorState.previewMode]);

  // Helper function to get ring styles based on preview mode
  const getRingStyle = useCallback((defaultRing: string = '') => {
    return editorState.previewMode ? '' : defaultRing;
  }, [editorState.previewMode]);

  // Helper function to get hover styles based on preview mode
  const getHoverStyle = useCallback((defaultHover: string = '') => {
    return editorState.previewMode ? '' : defaultHover;
  }, [editorState.previewMode]);

  // Handle image upload
  const handleImageUpload = useCallback((blockId: string, fileOrUrl: File | string) => {
    const currentPage = getCurrentPage();
    const currentBlock = currentPage.blocks.find(b => b.id === blockId);

    if (typeof fileOrUrl === 'string') {
      // URL
      const newContent = { ...currentBlock?.content, src: fileOrUrl };
      updateBlock(blockId, {
        content: newContent
      });
    } else {
      // File upload
      const reader = new FileReader();
      reader.onload = (e) => {
        const src = e.target?.result as string;
        const newContent = { ...currentBlock?.content, src };
        updateBlock(blockId, {
          content: newContent
        });
      };
      reader.readAsDataURL(fileOrUrl);
    }
  }, [updateBlock, getCurrentPage]);

  // Render block content
  const renderBlockContent = useCallback((block: EditorBlock) => {
    switch (block.type) {
      case 'text':
        return (
          <div className="w-full h-full" style={{ overflow: 'visible' }}>
            <TextFormattingWrapper
              content={block.content.richText || block.content.text || 'New Text Block'}
              onChange={(content) => updateBlock(block.id, {
                content: {
                  ...block.content,
                  richText: content,
                  text: content.replace(/<[^>]*>/g, '') // Keep plain text for backward compatibility
                }
              })}
              style={{
                ...getTextStyleCSS(block.textStyle),
                width: '100%',
                height: '100%',
                wordWrap: 'break-word',
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
              }}
              placeholder="Enter text..."
              inputType="textarea"
              variables={editorState.variables || []}
              globalTypography={editorState.globalTypography}
              onFontSizeChange={(fontSize) => updateBlock(block.id, {
                textStyle: { ...block.textStyle, fontSize }
              })}
            />
          </div>
        );
      
      case 'image':
        return (
          <div className={`w-full h-full relative ${editorState.previewMode ? '' : 'group'}`}>
            {block.content.src ? (
              <>
                <img
                  src={block.content.src}
                  alt={block.content.alt || 'Image'}
                  className="w-full h-full object-cover rounded"
                />
                {!editorState.previewMode && (
                  <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        updateBlock(block.id, { content: { ...block.content, src: '' } });
                      }}
                      className="px-3 py-1 bg-white text-black rounded text-sm hover:bg-gray-100"
                    >
                      Change Image
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="w-full h-full bg-gray-100 border-2 border-dashed border-gray-300 rounded p-2">
                <ImageUploadComponent
                  onImageSelect={(fileOrUrl) => handleImageUpload(block.id, fileOrUrl)}
                  currentSrc={block.content.src}
                />
              </div>
            )}
          </div>
        );
      
      case 'shape':
        const shapeContent = block.content;
        const fillColor = shapeContent.fillColor || '#3b82f6';
        const strokeColor = shapeContent.strokeColor || '#1e40af';
        const strokeWidth = shapeContent.strokeWidth || 0;
        const shapeType = shapeContent.shapeType || 'rectangle';

        const commonStyles = {
          width: '100%',
          height: '100%',
          backgroundColor: fillColor,
          border: strokeWidth > 0 ? getInlineBorderStyle(`${strokeWidth}px solid ${strokeColor}`) : 'none',
        };

        switch (shapeType) {
          case 'circle':
            return (
              <div
                className="w-full h-full"
                style={{
                  ...commonStyles,
                  borderRadius: '50%',
                }}
              />
            );

          case 'triangle':
            return (
              <div
                className="w-full h-full relative"
                style={{ backgroundColor: 'transparent' }}
              >
                <svg
                  width="100%"
                  height="100%"
                  viewBox="0 0 100 100"
                  className="absolute inset-0"
                >
                  <polygon
                    points="50,10 90,90 10,90"
                    fill={fillColor}
                    stroke={strokeWidth > 0 ? strokeColor : 'none'}
                    strokeWidth={strokeWidth}
                  />
                </svg>
              </div>
            );

          case 'line':
            return (
              <div
                className="w-full h-full relative"
                style={{ backgroundColor: 'transparent' }}
              >
                <svg
                  width="100%"
                  height="100%"
                  viewBox="0 0 100 100"
                  className="absolute inset-0"
                >
                  <line
                    x1="10"
                    y1="50"
                    x2="90"
                    y2="50"
                    stroke={strokeColor}
                    strokeWidth={Math.max(strokeWidth, 2)}
                  />
                </svg>
              </div>
            );

          case 'arrow-right':
            return (
              <div
                className="w-full h-full relative"
                style={{ backgroundColor: 'transparent' }}
              >
                <svg
                  width="100%"
                  height="100%"
                  viewBox="0 0 100 100"
                  className="absolute inset-0"
                >
                  <polygon
                    points="10,30 70,30 70,20 90,50 70,80 70,70 10,70"
                    fill={fillColor}
                    stroke={strokeWidth > 0 ? strokeColor : 'none'}
                    strokeWidth={strokeWidth}
                  />
                </svg>
              </div>
            );

          case 'arrow-left':
            return (
              <div
                className="w-full h-full relative"
                style={{ backgroundColor: 'transparent' }}
              >
                <svg
                  width="100%"
                  height="100%"
                  viewBox="0 0 100 100"
                  className="absolute inset-0"
                >
                  <polygon
                    points="90,30 30,30 30,20 10,50 30,80 30,70 90,70"
                    fill={fillColor}
                    stroke={strokeWidth > 0 ? strokeColor : 'none'}
                    strokeWidth={strokeWidth}
                  />
                </svg>
              </div>
            );

          case 'arrow-up':
            return (
              <div
                className="w-full h-full relative"
                style={{ backgroundColor: 'transparent' }}
              >
                <svg
                  width="100%"
                  height="100%"
                  viewBox="0 0 100 100"
                  className="absolute inset-0"
                >
                  <polygon
                    points="30,90 30,30 20,30 50,10 80,30 70,30 70,90"
                    fill={fillColor}
                    stroke={strokeWidth > 0 ? strokeColor : 'none'}
                    strokeWidth={strokeWidth}
                  />
                </svg>
              </div>
            );

          case 'arrow-down':
            return (
              <div
                className="w-full h-full relative"
                style={{ backgroundColor: 'transparent' }}
              >
                <svg
                  width="100%"
                  height="100%"
                  viewBox="0 0 100 100"
                  className="absolute inset-0"
                >
                  <polygon
                    points="30,10 30,70 20,70 50,90 80,70 70,70 70,10"
                    fill={fillColor}
                    stroke={strokeWidth > 0 ? strokeColor : 'none'}
                    strokeWidth={strokeWidth}
                  />
                </svg>
              </div>
            );

          case 'star':
            return (
              <div
                className="w-full h-full relative"
                style={{ backgroundColor: 'transparent' }}
              >
                <svg
                  width="100%"
                  height="100%"
                  viewBox="0 0 100 100"
                  className="absolute inset-0"
                >
                  <polygon
                    points="50,10 61,35 90,35 69,54 79,85 50,70 21,85 31,54 10,35 39,35"
                    fill={fillColor}
                    stroke={strokeWidth > 0 ? strokeColor : 'none'}
                    strokeWidth={strokeWidth}
                  />
                </svg>
              </div>
            );

          case 'diamond':
            return (
              <div
                className="w-full h-full relative"
                style={{ backgroundColor: 'transparent' }}
              >
                <svg
                  width="100%"
                  height="100%"
                  viewBox="0 0 100 100"
                  className="absolute inset-0"
                >
                  <polygon
                    points="50,10 90,50 50,90 10,50"
                    fill={fillColor}
                    stroke={strokeWidth > 0 ? strokeColor : 'none'}
                    strokeWidth={strokeWidth}
                  />
                </svg>
              </div>
            );

          case 'hexagon':
            return (
              <div
                className="w-full h-full relative"
                style={{ backgroundColor: 'transparent' }}
              >
                <svg
                  width="100%"
                  height="100%"
                  viewBox="0 0 100 100"
                  className="absolute inset-0"
                >
                  <polygon
                    points="25,25 75,25 90,50 75,75 25,75 10,50"
                    fill={fillColor}
                    stroke={strokeWidth > 0 ? strokeColor : 'none'}
                    strokeWidth={strokeWidth}
                  />
                </svg>
              </div>
            );

          case 'heart':
            return (
              <div
                className="w-full h-full relative"
                style={{ backgroundColor: 'transparent' }}
              >
                <svg
                  width="100%"
                  height="100%"
                  viewBox="0 0 100 100"
                  className="absolute inset-0"
                >
                  <path
                    d="M50,85 C50,85 20,65 20,45 C20,35 25,25 35,25 C40,25 45,30 50,35 C55,30 60,25 65,25 C75,25 80,35 80,45 C80,65 50,85 50,85 Z"
                    fill={fillColor}
                    stroke={strokeWidth > 0 ? strokeColor : 'none'}
                    strokeWidth={strokeWidth}
                  />
                </svg>
              </div>
            );

          case 'cross':
            return (
              <div
                className="w-full h-full relative"
                style={{ backgroundColor: 'transparent' }}
              >
                <svg
                  width="100%"
                  height="100%"
                  viewBox="0 0 100 100"
                  className="absolute inset-0"
                >
                  <polygon
                    points="40,10 60,10 60,40 90,40 90,60 60,60 60,90 40,90 40,60 10,60 10,40 40,40"
                    fill={fillColor}
                    stroke={strokeWidth > 0 ? strokeColor : 'none'}
                    strokeWidth={strokeWidth}
                  />
                </svg>
              </div>
            );

          case 'rectangle':
          default:
            return (
              <div
                className="w-full h-full"
                style={commonStyles}
              />
            );
        }
      
      case 'table':
        // Enhanced table rendering with flexible layouts and styling
        const tableContent = block.content;
        const tableRows = tableContent.rows || [];
        const tableColumns = tableContent.columns || [];

        return (
          <div className={`w-full h-full bg-white rounded relative ${editorState.previewMode ? '' : 'group'}`}
               style={{
                 border: getInlineBorderStyle(`${tableContent.tableStyle?.borderWidth || 1}px ${tableContent.tableStyle?.borderStyle || 'solid'} ${tableContent.tableStyle?.borderColor || '#e5e7eb'}`),
                 backgroundColor: tableContent.tableStyle?.backgroundColor || 'white'
               }}>
            {/* Enhanced Table Controls */}
            <div className={`absolute -top-10 left-0 flex space-x-1 opacity-0 ${getHoverStyle('group-hover:opacity-100')} transition-opacity duration-300 bg-white border rounded-lg px-3 py-2 shadow-lg z-20 pointer-events-none ${getHoverStyle('group-hover:pointer-events-auto')}`}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  // Add new row with same number of columns as the last row
                  const lastRow = tableRows[tableRows.length - 1];
                  const numCols = lastRow ? (lastRow.cells || []).length : 3;
                  const newRowId = `row-${Date.now()}`;
                  const newCells = Array.from({ length: numCols }, (_, i) => ({
                    id: `cell-${newRowId}-${i}`,
                    content: `Cell ${tableRows.length + 1},${i + 1}`,
                    colspan: 1,
                    rowspan: 1
                  }));

                  const newRow = {
                    id: newRowId,
                    cells: newCells
                  };

                  updateBlock(block.id, {
                    content: { ...tableContent, rows: [...tableRows, newRow] }
                  });
                }}
                className="text-xs bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600 transition-colors pointer-events-auto"
                title="Add Row"
              >
                +Row
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  // Add column to all rows
                  const updatedRows = tableRows.map((row, rowIndex) => ({
                    ...row,
                    cells: [...(row.cells || []), {
                      id: `cell-${row.id}-${(row.cells || []).length}`,
                      content: `Cell ${rowIndex + 1},${(row.cells || []).length + 1}`,
                      colspan: 1,
                      rowspan: 1
                    }]
                  }));

                  const newColumnId = `col-${Date.now()}`;
                  const updatedColumns = [...tableColumns, {
                    id: newColumnId,
                    width: 120
                  }];

                  updateBlock(block.id, {
                    content: { ...tableContent, rows: updatedRows, columns: updatedColumns }
                  });
                }}
                className="text-xs bg-green-500 text-white px-2 py-1 rounded hover:bg-green-600 transition-colors pointer-events-auto"
                title="Add Column"
              >
                +Col
              </button>

              {/* Advanced Table Editor Button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setAdvancedTableEditor(block.id);
                }}
                className="text-xs bg-purple-500 text-white px-2 py-1 rounded hover:bg-purple-600 transition-colors pointer-events-auto flex items-center"
                title="Advanced Table Editor"
              >
                <Settings className="w-3 h-3" />
              </button>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (tableRows.length > 1) {
                    const updatedRows = tableRows.slice(0, -1);
                    updateBlock(block.id, {
                      content: { ...tableContent, rows: updatedRows }
                    });
                  }
                }}
                className="text-xs bg-red-500 text-white px-2 py-1 rounded hover:bg-red-600 transition-colors pointer-events-auto"
                title="Remove Row"
              >
                -Row
              </button>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  // Remove last column from all rows
                  const updatedRows = tableRows.map(row => ({
                    ...row,
                    cells: (row.cells || []).length > 1 ? (row.cells || []).slice(0, -1) : (row.cells || [])
                  }));

                  const updatedColumns = tableColumns.length > 1 ? tableColumns.slice(0, -1) : tableColumns;

                  updateBlock(block.id, {
                    content: { ...tableContent, rows: updatedRows, columns: updatedColumns }
                  });
                }}
                className="text-xs bg-red-500 text-white px-2 py-1 rounded hover:bg-red-600 transition-colors pointer-events-auto"
                title="Remove Column"
              >
                -Col
              </button>

              {/* Remove Table Button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  deleteBlock(block.id);
                }}
                className="text-xs bg-gray-500 text-white px-2 py-1 rounded hover:bg-gray-600 transition-colors pointer-events-auto"
                title="Remove Table"
              >
                Remove
              </button>
            </div>

            <div className="w-full h-full overflow-auto">
              <table className="w-full border-collapse">
                <tbody>
                  {tableRows.map((row, rowIndex) => (
                    <tr key={row.id} style={row.style ? {
                      backgroundColor: row.style.backgroundColor,
                      color: row.style.textColor,
                      fontWeight: row.style.fontWeight,
                      fontSize: row.style.fontSize ? `${row.style.fontSize}px` : undefined,
                      borderTop: row.style.borderTop,
                      borderBottom: row.style.borderBottom
                    } : undefined}>
                      {(row.cells || []).map((cell, cellIndex) => {
                        const columnStyle = tableColumns[cellIndex]?.style;
                        const isHeader = tableContent.hasHeader && rowIndex === 0;
                        const headerStyle = tableContent.headerStyle || {};

                        // Combine styles: column -> row -> cell -> header (if applicable)
                        const finalStyle = {
                          backgroundColor: cell.style?.backgroundColor ||
                                          row.style?.backgroundColor ||
                                          columnStyle?.backgroundColor ||
                                          (isHeader ? headerStyle.backgroundColor || '#f3f4f6' : '#ffffff'),
                          // Only set color if explicitly defined, let global typography handle defaults
                          color: cell.style?.textColor ||
                                row.style?.textColor ||
                                columnStyle?.textColor ||
                                (isHeader ? headerStyle.textColor : undefined),
                          textAlign: cell.style?.textAlign ||
                                    row.style?.textAlign ||
                                    columnStyle?.textAlign ||
                                    (isHeader ? headerStyle.textAlign || 'left' : 'left'),
                          fontWeight: cell.style?.fontWeight ||
                                     row.style?.fontWeight ||
                                     columnStyle?.fontWeight ||
                                     (isHeader ? headerStyle.fontWeight || 'bold' : 'normal'),
                          fontSize: `${cell.style?.fontSize ||
                                      row.style?.fontSize ||
                                      columnStyle?.fontSize ||
                                      (isHeader ? headerStyle.fontSize || 14 : 14)}px`,
                          width: columnStyle?.width ? `${columnStyle.width}px` : undefined,
                          minWidth: columnStyle?.minWidth ? `${columnStyle.minWidth}px` : '80px',
                          maxWidth: columnStyle?.maxWidth ? `${columnStyle.maxWidth}px` : undefined
                        };

                        return (
                          <td
                            key={cell.id}
                            className="p-0 text-sm min-h-[35px] relative group"
                            colSpan={cell.colspan}
                            rowSpan={cell.rowspan}
                            style={{
                              border: getInlineBorderStyle(`${tableContent.tableStyle?.borderWidth || 1}px ${tableContent.tableStyle?.borderStyle || 'solid'} ${tableContent.tableStyle?.borderColor || '#e5e7eb'}`),
                              backgroundColor: finalStyle.backgroundColor,
                              width: finalStyle.width,
                              minWidth: finalStyle.minWidth,
                              maxWidth: finalStyle.maxWidth
                            }}
                          >
                            <div className="relative">
                              <TextFormattingWrapper
                                content={cell.richContent || cell.content || ''}
                                onChange={(content) => {
                                  // Update cell content in the enhanced structure
                                  const updatedRows = tableRows.map(r =>
                                    r.id === row.id ? {
                                      ...r,
                                      cells: (r.cells || []).map(c =>
                                        c.id === cell.id ? {
                                          ...c,
                                          richContent: content,
                                          content: content.replace(/<[^>]*>/g, '') // Keep plain text for backward compatibility
                                        } : c
                                      )
                                    } : r
                                  );
                                  updateBlock(block.id, {
                                    content: { ...tableContent, rows: updatedRows }
                                  });
                                }}
                                style={{
                                  ...(finalStyle.color && { color: finalStyle.color }),
                                  textAlign: finalStyle.textAlign as any,
                                  fontWeight: finalStyle.fontWeight,
                                  fontSize: finalStyle.fontSize,
                                  width: '100%',
                                  height: '100%',
                                  padding: '8px',
                                  border: 'none',
                                  outline: 'none',
                                  backgroundColor: 'transparent'
                                }}
                                placeholder="Enter text..."
                                inputType="text"
                                className="table-cell-input"
                                variables={editorState.variables || []}
                                globalTypography={editorState.globalTypography}
                                onFontSizeChange={(fontSize) => {
                                  // Update cell font size in the enhanced structure
                                  const updatedRows = tableRows.map(r =>
                                    r.id === row.id ? {
                                      ...r,
                                      cells: (r.cells || []).map(c =>
                                        c.id === cell.id ? {
                                          ...c,
                                          style: { ...c.style, fontSize: `${fontSize}px` }
                                        } : c
                                      )
                                    } : r
                                  );
                                  updateBlock(block.id, {
                                    content: { ...tableContent, rows: updatedRows }
                                  });
                                }}
                              />

                              {/* Colspan indicator for merged cells */}
                              {cell.colspan > 1 && (
                                <div className="absolute top-1 right-1 bg-yellow-400 text-yellow-900 text-xs px-1 rounded font-bold opacity-75">
                                  {cell.colspan}
                                </div>
                              )}

                              {/* Cell selection indicator */}
                              {selectedTableCell === cell.id && (
                                <div className="absolute inset-0 border-2 border-blue-500 pointer-events-none" />
                              )}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      
      case 'placeholder':
        return (
          <div
            className="w-full h-full bg-yellow-100 border border-yellow-300 rounded px-2 py-1 flex items-center justify-center text-sm cursor-pointer hover:bg-yellow-200 transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              // No popup - just select the block for editing in property panel
            }}
          >
            <Hash className="w-4 h-4 mr-1" />
            {`{{${block.content.variable || 'variable'}}}`}
          </div>
        );

      case 'chart':
        return (
          <div className={`w-full h-full bg-white rounded ${getBorderStyle('border border-gray-200')}`}>
            <SimpleChart
              data={block.content.data || [
                { label: 'A', value: 30 },
                { label: 'B', value: 50 },
                { label: 'C', value: 20 }
              ]}
              colors={block.content.colors || ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6']}
              chartType={block.content.chartType || 'bar'}
              onDataChange={(data) => updateBlock(block.id, {
                content: { ...block.content, data }
              })}
              onChartTypeChange={(chartType) => updateBlock(block.id, {
                content: { ...block.content, chartType }
              })}
            />
          </div>
        );

      case 'video':
        return (
          <div className={`w-full h-full bg-black rounded flex items-center justify-center relative ${editorState.previewMode ? '' : 'group'}`}>
            {block.content.src ? (
              <>
                <video
                  src={block.content.src}
                  poster={block.content.poster}
                  controls
                  className="w-full h-full rounded"
                />
                {!editorState.previewMode && (
                  <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const newSrc = prompt('Enter video URL:', block.content.src || '');
                        if (newSrc !== null) {
                          updateBlock(block.id, {
                            content: { ...block.content, src: newSrc }
                          });
                        }
                      }}
                      className="px-3 py-1 bg-white text-black rounded text-sm hover:bg-gray-100"
                    >
                      Change Video
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div
                className="text-white text-center cursor-pointer hover:bg-gray-800 w-full h-full flex flex-col items-center justify-center transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  const src = prompt('Enter video URL:');
                  if (src) {
                    updateBlock(block.id, {
                      content: { ...block.content, src }
                    });
                  }
                }}
              >
                <Video className="w-8 h-8 mx-auto mb-2" />
                <p className="text-sm">Click to add video URL</p>
              </div>
            )}
          </div>
        );

      case 'button':
        return (
          <button
            className={`w-full h-full rounded font-medium transition-colors relative ${editorState.previewMode ? '' : 'group'} ${
              block.content.style === 'primary'
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : block.content.style === 'secondary'
                ? 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
            style={{ fontSize: block.content.size === 'small' ? '12px' : block.content.size === 'large' ? '18px' : '14px' }}
            onClick={(e) => {
              e.stopPropagation();
              // No popup - just select the block for editing in property panel
            }}
          >
            {block.content.text || 'Button'}
            {!editorState.previewMode && (
              <div className="absolute inset-0 bg-black bg-opacity-20 opacity-0 group-hover:opacity-100 transition-opacity rounded flex items-center justify-center">
                <span className="text-xs text-white">Click to edit</span>
              </div>
            )}
          </button>
        );

      case 'divider':
        return (
          <div
            className="w-full h-full flex items-center justify-center"
          >
            <div
              style={{
                width: block.content.orientation === 'vertical' ? block.content.thickness || 2 : '100%',
                height: block.content.orientation === 'vertical' ? '100%' : block.content.thickness || 2,
                backgroundColor: block.content.color || '#e5e7eb',
                borderStyle: block.content.style || 'solid',
              }}
            />
          </div>
        );

      case 'progress':
        return (
          <div className="w-full h-full flex items-center">
            <div className="w-full bg-gray-200 rounded-full" style={{ height: '60%' }}>
              <div
                className="h-full rounded-full transition-all duration-300"
                style={{
                  width: `${Math.min(100, Math.max(0, (block.content.value || 0) / (block.content.max || 100) * 100))}%`,
                  backgroundColor: block.content.color || '#3b82f6'
                }}
              />
            </div>
            {block.content.showLabel && (
              <span className="ml-2 text-sm font-medium">
                {block.content.value || 0}%
              </span>
            )}
          </div>
        );

      case 'list':
        return (
          <div className={`w-full h-full p-2 overflow-auto bg-white rounded ${getBorderStyle('border border-gray-200')}`}>
            <ul className={`space-y-1 ${block.content.listType === 'numbered' ? 'list-decimal' : 'list-disc'} list-inside`}>
              {(block.content.items || ['Item 1', 'Item 2', 'Item 3']).map((item: string, index: number) => (
                <li
                  key={index}
                  className={`text-sm ${editorState.previewMode ? '' : 'hover:bg-gray-100'} p-1 rounded flex items-center ${editorState.previewMode ? '' : 'group'}`}
                >
                  <input
                    type="text"
                    className="flex-1 bg-transparent border-none outline-none text-sm"
                    defaultValue={item}
                    onBlur={(e) => {
                      const newItems = [...(block.content.items || [])];
                      newItems[index] = e.target.value || '';
                      updateBlock(block.id, {
                        content: { ...block.content, items: newItems }
                      });
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.currentTarget.blur();
                      }
                    }}
                  />
                  {!editorState.previewMode && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const newItems = [...(block.content.items || [])];
                        newItems.splice(index, 1);
                        updateBlock(block.id, {
                          content: { ...block.content, items: newItems }
                        });
                      }}
                      className="ml-2 text-red-500 hover:text-red-700 text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      ×
                    </button>
                  )}
                </li>
              ))}
              {!editorState.previewMode && (
                <li
                  className={`text-sm text-gray-400 cursor-pointer ${getHoverStyle('hover:bg-gray-100')} p-1 rounded border-2 border-dashed border-gray-300`}
                  onClick={(e) => {
                    e.stopPropagation();
                    const newItems = [...(block.content.items || []), 'New Item'];
                    updateBlock(block.id, {
                      content: { ...block.content, items: newItems }
                    });
                  }}
                >
                  + Add item
                </li>
              )}
            </ul>
          </div>
        );

      case 'datetime':
        return (
          <div className="w-full h-full flex items-center justify-center text-sm font-mono">
            {block.content.defaultValue === 'current'
              ? new Date().toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit'
                })
              : block.content.format || 'MM/DD/YYYY'
            }
          </div>
        );

      case 'signature':
        return (
          <div className="w-full h-full flex items-end justify-center border-b-2 border-gray-300">
            <span className="text-sm text-gray-500 mb-1">
              {block.content.placeholder || 'Signature'}
            </span>
          </div>
        );

      case 'qrcode':
        return (
          <div
            className={`w-full h-full flex items-center justify-center bg-white rounded ${editorState.previewMode ? '' : 'cursor-pointer'} ${getHoverStyle('hover:bg-gray-50')} transition-colors ${editorState.previewMode ? '' : 'group'} ${getBorderStyle('border border-gray-200')}`}
            onClick={(e) => {
              e.stopPropagation();
              const newValue = prompt('Enter QR code content:', block.content.value || 'https://example.com');
              if (newValue !== null) {
                updateBlock(block.id, {
                  content: { ...block.content, value: newValue }
                });
              }
            }}
          >
            <div className="relative">
              <div
                className="bg-black"
                style={{
                  width: '80px',
                  height: '80px',
                  backgroundImage: `url("data:image/svg+xml,${encodeURIComponent(`
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
                      <rect width="100" height="100" fill="white"/>
                      <rect x="10" y="10" width="80" height="80" fill="black"/>
                      <rect x="20" y="20" width="60" height="60" fill="white"/>
                      <text x="50" y="55" text-anchor="middle" font-size="8" fill="black">QR</text>
                    </svg>
                  `)}")`,
                  backgroundSize: 'contain',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'center'
                }}
              />
              {!editorState.previewMode && (
                <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <span className="text-white text-xs">Click to edit</span>
                </div>
              )}
            </div>
          </div>
        );

      case 'icon':
        return (
          <div className="w-full h-full flex items-center justify-center">
            <div
              style={{
                width: block.content.size || 48,
                height: block.content.size || 48,
                backgroundColor: block.content.color || '#3b82f6',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <span className="text-white font-bold">★</span>
            </div>
          </div>
        );

      case 'heading1':
      case 'heading2':
      case 'heading3':
        const defaultHeadingSize = block.type === 'heading1' ? 32 : block.type === 'heading2' ? 24 : 20;
        return (
          <div className="w-full h-full" style={{ overflow: 'visible' }}>
            <TextFormattingWrapper
              content={block.content.richText || block.content.text || 'Heading'}
              onChange={(content) => updateBlock(block.id, {
                content: {
                  ...block.content,
                  richText: content,
                  text: content.replace(/<[^>]*>/g, '') // Keep plain text for backward compatibility
                }
              })}
              style={{
                ...getTextStyleCSS(block.textStyle),
                fontSize: `${block.textStyle?.fontSize || defaultHeadingSize}px`,
                fontWeight: block.textStyle?.fontWeight || 'bold',
                width: '100%',
                height: '100%',
                wordWrap: 'break-word',
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
              }}
              placeholder={`Enter ${block.type === 'heading1' ? 'heading 1' : block.type === 'heading2' ? 'heading 2' : 'heading 3'}...`}
              inputType="textarea"
              variables={editorState.variables || []}
              globalTypography={editorState.globalTypography}
              onFontSizeChange={(fontSize) => updateBlock(block.id, {
                textStyle: { ...block.textStyle, fontSize }
              })}
            />
          </div>
        );

      case 'paragraph':
        return (
          <div className="w-full h-full" style={{ overflow: 'visible' }}>
            <TextFormattingWrapper
              content={block.content.richText || block.content.text || 'Paragraph text...'}
              onChange={(content) => updateBlock(block.id, {
                content: {
                  ...block.content,
                  richText: content,
                  text: content.replace(/<[^>]*>/g, '') // Keep plain text for backward compatibility
                }
              })}
              style={{
                ...getTextStyleCSS(block.textStyle),
                width: '100%',
                height: '100%',
                lineHeight: '1.6',
                wordWrap: 'break-word',
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
              }}
              placeholder="Enter paragraph text..."
              inputType="textarea"
              variables={editorState.variables || []}
              globalTypography={editorState.globalTypography}
              onFontSizeChange={(fontSize) => updateBlock(block.id, {
                textStyle: { ...block.textStyle, fontSize }
              })}
            />
          </div>
        );



      // Data Visualization Cases
      case 'bar-chart':
        return (
          <div className={`w-full h-full p-4 bg-white rounded ${getBorderStyle('border border-gray-200')}`}>
            <Bar
              data={block.content.data}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  title: {
                    display: !!block.content.title,
                    text: block.content.title
                  },
                  legend: {
                    display: block.content.showLegend !== false
                  }
                },
                scales: {
                  x: {
                    grid: {
                      display: block.content.showGrid !== false
                    }
                  },
                  y: {
                    grid: {
                      display: block.content.showGrid !== false
                    }
                  }
                }
              }}
            />
          </div>
        );

      case 'line-chart':
        return (
          <div className={`w-full h-full p-4 bg-white rounded ${getBorderStyle('border border-gray-200')}`}>
            <Line
              data={block.content.data}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  title: {
                    display: !!block.content.title,
                    text: block.content.title
                  },
                  legend: {
                    display: block.content.showLegend !== false
                  }
                },
                scales: {
                  x: {
                    grid: {
                      display: block.content.showGrid !== false
                    }
                  },
                  y: {
                    grid: {
                      display: block.content.showGrid !== false
                    }
                  }
                }
              }}
            />
          </div>
        );

      case 'pie-chart':
        return (
          <div className={`w-full h-full p-4 bg-white rounded flex items-center justify-center ${getBorderStyle('border border-gray-200')}`}>
            <Pie
              data={block.content.data}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  title: {
                    display: !!block.content.title,
                    text: block.content.title
                  },
                  legend: {
                    display: block.content.showLegend !== false,
                    position: 'bottom'
                  }
                }
              }}
            />
          </div>
        );

      case 'kpi-card':
        return (
          <div className={`w-full h-full p-4 bg-white rounded ${getShadowStyle('shadow-sm')} ${getBorderStyle('border border-gray-200')}`}>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-600">{block.content.title}</h3>
              {block.content.trend && (
                <div className={`flex items-center text-xs ${
                  block.content.trend === 'up' ? 'text-green-600' :
                  block.content.trend === 'down' ? 'text-red-600' : 'text-gray-600'
                }`}>
                  {block.content.trend === 'up' ? '↗' : block.content.trend === 'down' ? '↘' : '→'}
                  {block.content.trendValue}
                </div>
              )}
            </div>
            <div className="text-2xl font-bold" style={{ color: block.content.color }}>
              {block.content.value}
            </div>
            {block.content.subtitle && (
              <div className="text-xs text-gray-500 mt-1">{block.content.subtitle}</div>
            )}
          </div>
        );

      case 'progress-bar':
        return (
          <div className={`w-full h-full p-4 bg-white rounded ${getBorderStyle('border border-gray-200')}`}>
            {block.content.label && (
              <div className="text-sm font-medium text-gray-700 mb-2">{block.content.label}</div>
            )}
            <div className="w-full bg-gray-200 rounded-full" style={{ height: block.content.height || 20 }}>
              <div
                className="rounded-full transition-all duration-300"
                style={{
                  width: `${(block.content.value / block.content.max) * 100}%`,
                  height: '100%',
                  backgroundColor: block.content.color || '#3b82f6'
                }}
              />
            </div>
            {block.content.showPercentage && (
              <div className="text-xs text-gray-600 mt-1 text-right">
                {Math.round((block.content.value / block.content.max) * 100)}%
              </div>
            )}
          </div>
        );

      case 'gauge-chart':
        return (
          <div className={`w-full h-full p-4 bg-white rounded flex flex-col items-center justify-center ${getBorderStyle('border border-gray-200')}`}>
            <div className="relative w-32 h-32">
              <svg viewBox="0 0 100 100" className="w-full h-full transform -rotate-90">
                <circle
                  cx="50"
                  cy="50"
                  r="40"
                  fill="none"
                  stroke="#e5e7eb"
                  strokeWidth="8"
                />
                <circle
                  cx="50"
                  cy="50"
                  r="40"
                  fill="none"
                  stroke={block.content.color || '#22c55e'}
                  strokeWidth="8"
                  strokeDasharray={`${(block.content.value / block.content.max) * 251.2} 251.2`}
                  strokeLinecap="round"
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <div className="text-lg font-bold">{block.content.value}{block.content.unit}</div>
              </div>
            </div>
            {block.content.title && (
              <div className="text-sm font-medium text-gray-700 mt-2">{block.content.title}</div>
            )}
          </div>
        );

      case 'data-table':
        const dataTableContent = block.content;
        const tableStyle = dataTableContent.tableStyle || {};
        const headerStyle = dataTableContent.headerStyle || {};
        const rowStyles = dataTableContent.rowStyles || {};
        const columnStyles = dataTableContent.columnStyles || {};
        const cellStyles = dataTableContent.cellStyles || {};

        // Build table styles
        const tableStyleObj: React.CSSProperties = {
          backgroundColor: tableStyle.backgroundColor || '#ffffff',
          borderColor: tableStyle.borderColor || '#e5e7eb',
          borderWidth: `${tableStyle.borderWidth || 1}px`,
          borderStyle: tableStyle.borderStyle || 'solid',
          borderRadius: `${tableStyle.borderRadius || 0}px`,
        };

        // Build header styles
        const headerStyleObj: React.CSSProperties = {
          backgroundColor: headerStyle.backgroundColor || '#f3f4f6',
          color: headerStyle.textColor || '#374151',
          fontWeight: headerStyle.fontWeight || 'bold',
          fontSize: headerStyle.fontSize ? `${headerStyle.fontSize}px` : '14px',
          textAlign: headerStyle.textAlign || 'left',
        };

        return (
          <div className={`w-full h-full p-4 bg-white rounded overflow-auto ${getBorderStyle('border border-gray-200')}`}>
            <table className="w-full text-sm" style={tableStyleObj}>
              <thead>
                <tr className={getBorderStyle('border-b border-gray-200')}>
                  {dataTableContent.headers.map((header: string, index: number) => {
                    const columnStyle = columnStyles[index] || {};
                    const combinedHeaderStyle: React.CSSProperties = {
                      ...headerStyleObj,
                      backgroundColor: columnStyle.backgroundColor || headerStyleObj.backgroundColor,
                      color: columnStyle.textColor || headerStyleObj.color,
                      fontWeight: columnStyle.fontWeight || headerStyleObj.fontWeight,
                      fontSize: columnStyle.fontSize ? `${columnStyle.fontSize}px` : headerStyleObj.fontSize,
                      textAlign: columnStyle.textAlign || headerStyleObj.textAlign,
                      width: columnStyle.width ? `${columnStyle.width}px` : 'auto',
                      minWidth: columnStyle.minWidth ? `${columnStyle.minWidth}px` : 'auto',
                      maxWidth: columnStyle.maxWidth ? `${columnStyle.maxWidth}px` : 'auto',
                    };

                    return (
                      <th
                        key={index}
                        className="py-2 px-3"
                        style={combinedHeaderStyle}
                      >
                        {header}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {dataTableContent.rows.map((row: string[], rowIndex: number) => {
                  const rowStyle = rowStyles[rowIndex] || {};
                  const isStriped = dataTableContent.striped && rowIndex % 2 === 1;

                  const rowStyleObj: React.CSSProperties = {
                    backgroundColor: rowStyle.backgroundColor || (isStriped ? '#f9fafb' : 'transparent'),
                    color: rowStyle.textColor || '#374151',
                    fontWeight: rowStyle.fontWeight || 'normal',
                    fontSize: rowStyle.fontSize ? `${rowStyle.fontSize}px` : '14px',
                    textAlign: rowStyle.textAlign || 'left',
                    borderTop: rowStyle.borderTop,
                    borderBottom: rowStyle.borderBottom,
                    padding: rowStyle.padding,
                  };

                  return (
                    <tr
                      key={rowIndex}
                      className={getBorderStyle('border-b border-gray-100')}
                      style={rowStyleObj}
                    >
                      {row.map((cell: string, cellIndex: number) => {
                        const cellKey = `${rowIndex}-${cellIndex}`;
                        const cellStyle = cellStyles[cellKey] || {};
                        const columnStyle = columnStyles[cellIndex] || {};

                        const cellStyleObj: React.CSSProperties = {
                          backgroundColor: cellStyle.backgroundColor || columnStyle.backgroundColor || rowStyleObj.backgroundColor,
                          color: cellStyle.textColor || columnStyle.textColor || rowStyleObj.color,
                          fontWeight: cellStyle.fontWeight || columnStyle.fontWeight || rowStyleObj.fontWeight,
                          fontSize: cellStyle.fontSize ? `${cellStyle.fontSize}px` :
                                   columnStyle.fontSize ? `${columnStyle.fontSize}px` :
                                   rowStyleObj.fontSize,
                          textAlign: cellStyle.textAlign || columnStyle.textAlign || rowStyleObj.textAlign,
                          borderTop: cellStyle.borderTop,
                          borderBottom: cellStyle.borderBottom,
                          borderLeft: cellStyle.borderLeft || columnStyle.borderLeft,
                          borderRight: cellStyle.borderRight || columnStyle.borderRight,
                          padding: cellStyle.padding || rowStyleObj.padding || '8px 12px',
                        };

                        return (
                          <td
                            key={cellIndex}
                            className="py-2 px-3"
                            style={cellStyleObj}
                          >
                            {cell}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );

      case 'badge':
        return (
          <div className="w-full h-full flex items-center justify-center">
            <span className={`inline-flex items-center px-3 py-1 text-xs font-medium rounded-full ${
              block.content.variant === 'primary' ? 'bg-blue-100 text-blue-800' :
              block.content.variant === 'secondary' ? 'bg-gray-100 text-gray-800' :
              block.content.variant === 'success' ? 'bg-green-100 text-green-800' :
              block.content.variant === 'warning' ? 'bg-yellow-100 text-yellow-800' :
              block.content.variant === 'danger' ? 'bg-red-100 text-red-800' :
              'bg-blue-100 text-blue-800'
            }`}>
              {block.content.text}
            </span>
          </div>
        );

      case 'quote':
        return (
          <div className="w-full h-full p-4 bg-gray-50 border-l-4 border-blue-500 rounded">
            <blockquote className="text-gray-700 italic mb-2">
              "{block.content.text}"
            </blockquote>
            {block.content.author && (
              <cite className="text-sm text-gray-600 not-italic">
                — {block.content.author}
                {block.content.citation && <span className="text-gray-500">, {block.content.citation}</span>}
              </cite>
            )}
          </div>
        );

      case 'callout':
        return (
          <div className={`w-full h-full p-4 rounded border-l-4 ${
            block.content.type === 'info' ? 'bg-blue-50 border-blue-400' :
            block.content.type === 'warning' ? 'bg-yellow-50 border-yellow-400' :
            block.content.type === 'success' ? 'bg-green-50 border-green-400' :
            block.content.type === 'error' ? 'bg-red-50 border-red-400' :
            'bg-gray-50 border-gray-400'
          }`}>
            {block.content.title && (
              <h4 className="font-medium text-gray-900 mb-1">{block.content.title}</h4>
            )}
            <p className="text-gray-700 text-sm">{block.content.text}</p>
          </div>
        );

      case 'border-frame':
        return (
          <div
            className="w-full h-full"
            style={{
              border: getInlineBorderStyle(`${block.content.borderWidth || 2}px ${block.content.borderStyle || 'solid'} ${block.content.borderColor || '#3b82f6'}`),
              borderRadius: `${block.content.borderRadius || 8}px`,
              padding: `${block.content.padding || 16}px`
            }}
          >
            <div className="w-full h-full flex items-center justify-center text-gray-500 text-sm">
              Frame Content
            </div>
          </div>
        );

      case 'timeline':
        return (
          <div className="w-full h-full p-4 bg-white">
            <div className="space-y-4">
              {(block.content.items || []).map((item: any, index: number) => (
                <div key={item.id || index} className="flex items-start space-x-3">
                  <div className={`w-3 h-3 rounded-full mt-1 ${
                    item.status === 'completed' ? 'bg-green-500' :
                    item.status === 'current' ? 'bg-blue-500' : 'bg-gray-300'
                  }`} />
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-gray-900">{item.title}</h4>
                    {item.description && (
                      <p className="text-xs text-gray-600 mt-1">{item.description}</p>
                    )}
                    {item.date && (
                      <p className="text-xs text-gray-500 mt-1">{item.date}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'accordion':
        return (
          <div className={`w-full h-full p-4 bg-white rounded ${getBorderStyle('border border-gray-200')}`}>
            <div className="space-y-2">
              {(block.content.items || []).map((item: any, index: number) => (
                <div key={item.id || index} className={`rounded ${getBorderStyle('border border-gray-200')}`}>
                  <div className={`px-3 py-2 bg-gray-50 cursor-pointer flex items-center justify-between ${getBorderStyle('border-b border-gray-200')}`}>
                    <span className="text-sm font-medium text-gray-900">{item.title}</span>
                    <ChevronRight className={`w-4 h-4 text-gray-500 transition-transform ${
                      item.isOpen ? 'rotate-90' : ''
                    }`} />
                  </div>
                  {item.isOpen && (
                    <div className="px-3 py-2 text-sm text-gray-700">
                      {item.content}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );

      case 'icon-block':
        const iconMap: { [key: string]: any } = {
          Zap, Star, Heart, CheckCircle, AlertCircle, Info, Settings, User, Mail, Phone, Calendar, Clock
        };
        const IconComponent = iconMap[block.content.iconName] || Zap;
        return (
          <div className="w-full h-full flex items-center justify-center">
            <IconComponent
              size={block.content.size || 48}
              color={block.content.color || '#3b82f6'}
              className={block.content.style === 'filled' ? 'fill-current' : ''}
            />
          </div>
        );

      case 'script':
        return (
          <ScriptBlock
            content={block.content}
            onChange={(content) => updateBlock(block.id, { content })}
            blockId={block.id}
            previewMode={editorState.previewMode}
          />
        );

      case 'markdown':
        return (
          <MarkdownBlock
            content={{ markdown: block.content.markdown || '' }}
            onChange={(content) => updateBlock(block.id, { content })}
            previewMode={editorState.previewMode}
            style={getTextStyleCSS(block.textStyle)}
          />
        );

      case 'section-marker':
        return (
          <SectionMarkerBlock
            content={{
              title: block.content.title || 'New Section',
              color: block.content.color,
              description: block.content.description,
            }}
            onChange={(content) => updateBlock(block.id, { content })}
            previewMode={editorState.previewMode}
            exportHidden={editorState.previewMode}
          />
        );

      default:
        return <div className="w-full h-full flex items-center justify-center text-gray-500">Unknown block type</div>;
    }
  }, [updateBlock, handleImageUpload, editorState.previewMode, getTextStyleCSS]);

  // Save state to history
  const saveToHistory = useCallback((state: EditorState) => {
    setHistory(prev => {
      const newHistory = prev.slice(0, historyIndex + 1);
      newHistory.push(JSON.parse(JSON.stringify(state)));
      // Keep only last 50 states
      return newHistory.slice(-50);
    });
    setHistoryIndex(prev => prev + 1);
  }, [historyIndex]);

  // Undo functionality
  const undo = useCallback(() => {
    if (historyIndex > 0) {
      const previousState = history[historyIndex - 1];
      setEditorState(previousState);
      setHistoryIndex(prev => prev - 1);
      console.log('Undo performed');
    }
  }, [history, historyIndex]);

  // Redo functionality
  const redo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      const nextState = history[historyIndex + 1];
      setEditorState(nextState);
      setHistoryIndex(prev => prev + 1);
      console.log('Redo performed');
    }
  }, [history, historyIndex]);

  // Alignment functions
  const alignLeft = useCallback(() => {
    const blocksToAlign = selectedBlockIds.length > 0 ? selectedBlockIds :
                         editorState.selectedBlockId ? [editorState.selectedBlockId] : [];

    if (blocksToAlign.length === 0) return;

    // Save current state to history
    saveToHistory(editorState);

    const currentPage = getCurrentPage();
    const alignedBlocks = currentPage.blocks.filter(block => blocksToAlign.includes(block.id));

    if (alignedBlocks.length === 0) return;

    // Find the leftmost position (minimum x)
    const minX = Math.min(...alignedBlocks.map(block => block.position.x));

    // Update all selected blocks to align to the left
    blocksToAlign.forEach(blockId => {
      const block = currentPage.blocks.find(b => b.id === blockId);
      if (block) {
        updateBlock(blockId, {
          position: { ...block.position, x: minX }
        });
      }
    });
    console.log(`Aligned ${blocksToAlign.length} blocks to left at x=${minX}`);
  }, [selectedBlockIds, editorState, getCurrentPage, updateBlock, saveToHistory]);

  const alignCenter = useCallback(() => {
    const blocksToAlign = selectedBlockIds.length > 0 ? selectedBlockIds :
                         editorState.selectedBlockId ? [editorState.selectedBlockId] : [];

    if (blocksToAlign.length === 0) return;

    // Save current state to history
    saveToHistory(editorState);

    const currentPage = getCurrentPage();
    const centerX = currentPage.canvasSize.width / 2;

    blocksToAlign.forEach(blockId => {
      const block = currentPage.blocks.find(b => b.id === blockId);
      if (block) {
        const newX = centerX - (block.size.width / 2);
        updateBlock(blockId, {
          position: { ...block.position, x: newX }
        });
      }
    });
    console.log(`Aligned ${blocksToAlign.length} blocks to center`);
  }, [selectedBlockIds, editorState, getCurrentPage, updateBlock, saveToHistory]);

  const alignRight = useCallback(() => {
    const blocksToAlign = selectedBlockIds.length > 0 ? selectedBlockIds :
                         editorState.selectedBlockId ? [editorState.selectedBlockId] : [];

    if (blocksToAlign.length === 0) return;

    // Save current state to history
    saveToHistory(editorState);

    const currentPage = getCurrentPage();
    const alignedBlocks = currentPage.blocks.filter(block => blocksToAlign.includes(block.id));

    if (alignedBlocks.length === 0) return;

    // Find the rightmost position (maximum x + width)
    const maxRight = Math.max(...alignedBlocks.map(block => block.position.x + block.size.width));

    // Update all selected blocks to align to the right
    blocksToAlign.forEach(blockId => {
      const block = currentPage.blocks.find(b => b.id === blockId);
      if (block) {
        const newX = maxRight - block.size.width;
        updateBlock(blockId, {
          position: { ...block.position, x: newX }
        });
      }
    });
    console.log(`Aligned ${blocksToAlign.length} blocks to right at x=${maxRight}`);
  }, [selectedBlockIds, editorState, getCurrentPage, updateBlock, saveToHistory]);

  // Remove ALL gaps: page edge gap + between-block gap + internal block padding
  const removeAllGaps = useCallback(() => {
    const currentPage = getCurrentPage();
    if (!currentPage || currentPage.blocks.length === 0) return;

    saveToHistory(editorState);

    // Work only on visible, non-section-marker blocks
    const blocks = currentPage.blocks.filter(b => b.type !== 'section-marker' && b.visible !== false);
    if (blocks.length === 0) return;

    // 1. Sort blocks by Y position
    const sorted = [...blocks].sort((a, b) => a.position.y - b.position.y);

    // 2. Group into rows — a block joins the current row if its top < max bottom of the row so far
    const rows: typeof blocks[][] = [];
    let currentRow: typeof blocks[] = [sorted[0]];
    let rowMaxBottom = sorted[0].position.y + sorted[0].size.height;

    for (let i = 1; i < sorted.length; i++) {
      const block = sorted[i];
      if (block.position.y < rowMaxBottom) {
        currentRow.push(block);
        rowMaxBottom = Math.max(rowMaxBottom, block.position.y + block.size.height);
      } else {
        rows.push(currentRow);
        currentRow = [block];
        rowMaxBottom = block.position.y + block.size.height;
      }
    }
    rows.push(currentRow);

    // 3. Pack rows top-to-bottom with 0 gap; within each row pack left-to-right with 0 gap
    const newPositions: Record<string, { x: number; y: number }> = {};
    let currentY = 0;

    rows.forEach(row => {
      const sortedRow = [...row].sort((a, b) => a.position.x - b.position.x);
      const rowHeight = Math.max(...sortedRow.map(b => b.size.height));
      let currentX = 0;
      sortedRow.forEach(block => {
        newPositions[block.id] = { x: currentX, y: currentY };
        currentX += block.size.width;
      });
      currentY += rowHeight;
    });

    // 4. Apply new positions and strip internal padding/margin/gap from ALL blocks
    setEditorState(prev => ({
      ...prev,
      pages: prev.pages.map(page =>
        page.id === prev.currentPageId
          ? {
              ...page,
              blocks: page.blocks.map(block => ({
                ...block,
                position: newPositions[block.id] ?? block.position,
                style: {
                  ...block.style,
                  padding: '0',
                  margin: '0',
                  gap: '0',
                },
              })),
            }
          : page
      ),
    }));
  }, [editorState, getCurrentPage, saveToHistory, setEditorState]);

  // Snap to page edge functions
  const snapToPageLeft = useCallback(() => {
    const blocksToSnap = selectedBlockIds.length > 0 ? selectedBlockIds :
                         editorState.selectedBlockId ? [editorState.selectedBlockId] : [];
    if (blocksToSnap.length === 0) return;
    saveToHistory(editorState);
    const currentPage = getCurrentPage();
    blocksToSnap.forEach(blockId => {
      const block = currentPage.blocks.find(b => b.id === blockId);
      if (block) updateBlock(blockId, { position: { ...block.position, x: 0 } });
    });
  }, [selectedBlockIds, editorState, getCurrentPage, updateBlock, saveToHistory]);

  const snapToPageRight = useCallback(() => {
    const blocksToSnap = selectedBlockIds.length > 0 ? selectedBlockIds :
                         editorState.selectedBlockId ? [editorState.selectedBlockId] : [];
    if (blocksToSnap.length === 0) return;
    saveToHistory(editorState);
    const currentPage = getCurrentPage();
    blocksToSnap.forEach(blockId => {
      const block = currentPage.blocks.find(b => b.id === blockId);
      if (block) updateBlock(blockId, { position: { ...block.position, x: currentPage.canvasSize.width - block.size.width } });
    });
  }, [selectedBlockIds, editorState, getCurrentPage, updateBlock, saveToHistory]);

  const snapToPageTop = useCallback(() => {
    const blocksToSnap = selectedBlockIds.length > 0 ? selectedBlockIds :
                         editorState.selectedBlockId ? [editorState.selectedBlockId] : [];
    if (blocksToSnap.length === 0) return;
    saveToHistory(editorState);
    const currentPage = getCurrentPage();
    blocksToSnap.forEach(blockId => {
      const block = currentPage.blocks.find(b => b.id === blockId);
      if (block) updateBlock(blockId, { position: { ...block.position, y: 0 } });
    });
  }, [selectedBlockIds, editorState, getCurrentPage, updateBlock, saveToHistory]);

  const snapToPageBottom = useCallback(() => {
    const blocksToSnap = selectedBlockIds.length > 0 ? selectedBlockIds :
                         editorState.selectedBlockId ? [editorState.selectedBlockId] : [];
    if (blocksToSnap.length === 0) return;
    saveToHistory(editorState);
    const currentPage = getCurrentPage();
    const canvasHeight = typeof currentPage.canvasSize.height === 'number' ? currentPage.canvasSize.height : 1123;
    blocksToSnap.forEach(blockId => {
      const block = currentPage.blocks.find(b => b.id === blockId);
      if (block) updateBlock(blockId, { position: { ...block.position, y: canvasHeight - block.size.height } });
    });
  }, [selectedBlockIds, editorState, getCurrentPage, updateBlock, saveToHistory]);

  // Enhanced copy functionality
  const copySelectedBlocks = useCallback(async () => {
    const blocksToSelect = selectedBlockIds.length > 0 ? selectedBlockIds :
                          editorState.selectedBlockId ? [editorState.selectedBlockId] : [];

    if (blocksToSelect.length === 0) return false;

    const currentPage = getCurrentPage();
    const blocksToCopy = currentPage.blocks.filter(block => blocksToSelect.includes(block.id));

    if (blocksToCopy.length > 0) {
      const success = await clipboardManager.copyBlocks(blocksToCopy, currentPage.id);
      if (success) {
        // Show visual feedback
        console.log(`Copied ${blocksToCopy.length} block(s) to clipboard`);
      }
      return success;
    }
    return false;
  }, [selectedBlockIds, editorState.selectedBlockId, getCurrentPage, clipboardManager]);

  // Enhanced paste functionality
  const pasteBlocks = useCallback(async () => {
    // Save current state to history before pasting
    saveToHistory(editorState);

    const pastedBlocks = await clipboardManager.pasteBlocks();

    if (pastedBlocks && pastedBlocks.length > 0) {
      const currentPage = getCurrentPage();

      // Add pasted blocks to current page
      setEditorState(prev => ({
        ...prev,
        pages: prev.pages.map(page =>
          page.id === prev.currentPageId
            ? { ...page, blocks: [...page.blocks, ...pastedBlocks] }
            : page
        ),
        selectedBlockId: pastedBlocks[pastedBlocks.length - 1].id
      }));

      // Select all pasted blocks
      setSelectedBlockIds(pastedBlocks.map(block => block.id));

      console.log(`Pasted ${pastedBlocks.length} block(s)`);
      return true;
    }
    return false;
  }, [clipboardManager, getCurrentPage, saveToHistory, editorState]);

  // Import functionality
  const importTemplate = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.html,.json';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;

      try {
        const text = await file.text();
        let templateData = null;

        if (file.name.endsWith('.html')) {
          // Parse HTML export
          const parser = new DOMParser();
          const doc = parser.parseFromString(text, 'text/html');
          const dataScript = doc.getElementById('template-editor-data');

          if (dataScript) {
            // This is an exported template with embedded data
            const exportedData = JSON.parse(dataScript.textContent || '{}');
            console.log('🔍 Found exported template data:', exportedData);
            console.log('🔍 Export info:', exportedData.exportInfo);

            // The export format is { exportInfo: {...}, editorState: {...}, metadata: {...} }
            if (exportedData.editorState) {
              templateData = exportedData; // Use the full exported data structure
              console.log(`✅ Importing ${exportedData.exportInfo?.totalPages || 0} pages with ${exportedData.exportInfo?.totalBlocks || 0} blocks`);
            } else {
              // Fallback: maybe it's direct editorState
              templateData = { editorState: exportedData };
              console.log('⚠️ Using fallback import structure');
            }
          } else {
            // This is a regular HTML file - convert it to template format
            console.log('Converting regular HTML file to template format...');

            // Import HTML as script block to avoid edit mode issues
            const body = doc.body || doc.documentElement;
            const htmlContent = body.innerHTML;

            // Extract CSS from style tags
            const styleTags = doc.querySelectorAll('style');
            let cssContent = '';
            styleTags.forEach(style => {
              cssContent += style.textContent + '\n';
            });

            // Create script block so HTML renders properly and doesn't disappear when clicked
            templateData = {
              editorState: {
                pages: [{
                  id: 'imported-page-1',
                  name: 'Imported HTML',
                  blocks: [{
                    id: 'imported-script-block-1',
                    type: 'script',
                    position: { x: 50, y: 50 },
                    size: { width: 700, height: 500 },
                    content: {
                      html: htmlContent,
                      css: cssContent,
                      javascript: ''
                    },
                    style: {},
                    zIndex: 1,
                    visible: true
                  }],
                  canvasSize: { width: 800, height: 800 },
                  background: { type: 'solid', color: '#ffffff' }
                }],
                currentPageId: 'imported-page-1',
                selectedBlockId: null,
                zoom: 1,
                showGrid: false,
                variables: [],
                globalTypography: {
                  fontFamily: 'Inter, sans-serif',
                  fontSize: 16,
                  fontWeight: '400',
                  lineHeight: 1.4,
                  letterSpacing: 0,
                  color: '#000000'
                }
              }
            };

            console.log('✅ Successfully converted HTML to script block - content will render properly and won\'t disappear when clicked!');
          }
        } else if (file.name.endsWith('.json')) {
          // Parse JSON export
          templateData = JSON.parse(text);
        }

        if (templateData && templateData.editorState) {
          console.log('🔄 Processing template data:', templateData);
          console.log('🔄 Editor state from template:', templateData.editorState);

          // Save current state to history
          saveToHistory(editorState);

          // Ensure imported data has proper structure with default values
          const importedState = {
            ...templateData.editorState,
            selectedBlockId: null,
            // Ensure variables array exists - use defaults if empty or missing
            variables: (templateData.editorState.variables && templateData.editorState.variables.length > 0)
              ? templateData.editorState.variables
              : DEFAULT_TEMPLATE_VARIABLES,
            // Ensure globalTypography exists
            globalTypography: templateData.editorState.globalTypography || {
              fontFamily: 'Inter, sans-serif',
              fontSize: 16,
              fontWeight: '400',
              lineHeight: 1.4,
              letterSpacing: 0,
              color: '#000000'
            },
            // Ensure pages array exists and has proper structure - CRITICAL FOR MULTI-PAGE
            pages: (templateData.editorState.pages || []).map((page, pageIndex) => {
              console.log(`📄 Processing page ${pageIndex + 1}: "${page.name}" with ${page.blocks?.length || 0} blocks`);
              return {
                ...page,
                blocks: (page.blocks || []).map((block, blockIndex) => {
                  console.log(`  🧱 Block ${blockIndex + 1}: ${block.type} at (${block.position?.x}, ${block.position?.y})`);
                  return {
                    ...block,
                    // Ensure all required block properties exist
                    position: block.position || { x: 50, y: 50 },
                    size: block.size || { width: 200, height: 100 },
                    zIndex: block.zIndex || 1,
                    visible: block.visible !== false
                  };
                }),
                canvasSize: page.canvasSize || { width: 800, height: 800 },
                background: page.background || { type: 'solid', color: '#ffffff' }
              };
            })
          };

          const totalBlocks = importedState.pages.reduce((total, page) => total + page.blocks.length, 0);
          console.log(`🎯 FINAL IMPORT: ${importedState.pages.length} pages with ${totalBlocks} total blocks`);

          // Detailed logging for verification
          importedState.pages.forEach((page, i) => {
            console.log(`📄 Page ${i + 1}: "${page.name}" - ${page.blocks.length} blocks - Canvas: ${page.canvasSize.width}x${page.canvasSize.height}`);
          });

          // Ensure variables exist in imported state
          const finalImportedState = {
            ...importedState,
            variables: (importedState.variables && importedState.variables.length > 0)
              ? importedState.variables
              : DEFAULT_TEMPLATE_VARIABLES
          };

          // Import the template data
          setEditorState(finalImportedState);
          setSelectedBlockIds([]);

          console.log('Template imported successfully');

          // Show detailed success message
          const successDiv = document.createElement('div');
          successDiv.innerHTML = `Template imported successfully`;
          successDiv.style.cssText = 'position:fixed;top:20px;right:20px;background:#10b981;color:white;padding:12px 20px;border-radius:8px;z-index:10000;box-shadow:0 4px 12px rgba(0,0,0,0.15);font-size:14px;line-height:1.4;';
          document.body.appendChild(successDiv);
          setTimeout(() => {
            if (document.body.contains(successDiv)) {
              document.body.removeChild(successDiv);
            }
          }, 3000);
        } else {
          console.error('Invalid template data structure:', templateData);
          throw new Error('Invalid template file format - missing editorState');
        }
      } catch (error) {
        console.error('Import failed:', error);
        alert(`Import failed: ${error.message}`);
      }
    };
    input.click();
  }, [editorState, saveToHistory]);

  // Context menu handlers
  const handleContextMenu = useCallback((e: React.MouseEvent, blockId?: string) => {
    e.preventDefault();

    // If right-clicking on a block, select it if not already selected
    if (blockId && !selectedBlockIds.includes(blockId) && editorState.selectedBlockId !== blockId) {
      selectBlock(blockId);
    }

    setContextMenu({
      isOpen: true,
      position: { x: e.clientX, y: e.clientY },
      targetBlockId: blockId
    });
  }, [selectedBlockIds, editorState.selectedBlockId, selectBlock]);

  const closeContextMenu = useCallback(() => {
    setContextMenu(prev => ({ ...prev, isOpen: false }));
  }, []);

  const handleContextMenuEdit = useCallback(() => {
    // Focus on the selected block for editing
    if (editorState.selectedBlockId) {
      const block = getCurrentPage().blocks.find(b => b.id === editorState.selectedBlockId);
      if (block && block.type === 'text') {
        // For text blocks, focus the text editor
        const textElement = document.querySelector(`[data-block-id="${block.id}"] [contenteditable]`);
        if (textElement) {
          (textElement as HTMLElement).focus();
        }
      }
    }
  }, [editorState.selectedBlockId, getCurrentPage]);

  const handleContextMenuBringToFront = useCallback(() => {
    const blocksToUpdate = selectedBlockIds.length > 0 ? selectedBlockIds :
                          editorState.selectedBlockId ? [editorState.selectedBlockId] : [];

    if (blocksToUpdate.length > 0) {
      const currentPage = getCurrentPage();
      const maxZIndex = Math.max(...currentPage.blocks.map(b => b.zIndex || 0));

      blocksToUpdate.forEach((blockId, index) => {
        updateBlock(blockId, { zIndex: maxZIndex + index + 1 });
      });
    }
  }, [selectedBlockIds, editorState.selectedBlockId, getCurrentPage, updateBlock]);

  const handleContextMenuSendToBack = useCallback(() => {
    const blocksToUpdate = selectedBlockIds.length > 0 ? selectedBlockIds :
                          editorState.selectedBlockId ? [editorState.selectedBlockId] : [];

    if (blocksToUpdate.length > 0) {
      const currentPage = getCurrentPage();
      const minZIndex = Math.min(...currentPage.blocks.map(b => b.zIndex || 0));

      blocksToUpdate.forEach((blockId, index) => {
        updateBlock(blockId, { zIndex: minZIndex - index - 1 });
      });
    }
  }, [selectedBlockIds, editorState.selectedBlockId, getCurrentPage, updateBlock]);

  // Paste block
  const pasteBlock = useCallback(() => {
    const copiedBlockStr = localStorage.getItem('copiedBlock');
    if (copiedBlockStr) {
      const copiedBlock = JSON.parse(copiedBlockStr);
      const currentPage = getCurrentPage();
      const newBlock = {
        ...copiedBlock,
        id: `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        position: {
          x: copiedBlock.position.x + 20,
          y: copiedBlock.position.y + 20
        },
        zIndex: currentPage.blocks.length + 1
      };
      setEditorState(prev => ({
        ...prev,
        pages: prev.pages.map(page =>
          page.id === prev.currentPageId
            ? { ...page, blocks: [...page.blocks, newBlock] }
            : page
        ),
        selectedBlockId: newBlock.id
      }));
    }
  }, [getCurrentPage]);

  // Duplicate selected block
  const duplicateSelectedBlock = useCallback(() => {
    if (editorState.selectedBlockId) {
      const currentPage = getCurrentPage();
      const block = currentPage.blocks.find(b => b.id === editorState.selectedBlockId);
      if (block) {
        const newBlock = {
          ...block,
          id: `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          position: {
            x: block.position.x + 20,
            y: block.position.y + 20
          },
          zIndex: currentPage.blocks.length + 1
        };
        setEditorState(prev => ({
          ...prev,
          pages: prev.pages.map(page =>
            page.id === prev.currentPageId
              ? { ...page, blocks: [...page.blocks, newBlock] }
              : page
          ),
          selectedBlockId: newBlock.id
        }));
      }
    }
  }, [editorState.selectedBlockId, getCurrentPage]);

  // Export functions
  const exportAsHTML = useCallback(async () => {
    try {
      // Show simple loading indicator
      const loadingDiv = document.createElement('div');
      loadingDiv.innerHTML = `
        <div style="display: flex; align-items: center; gap: 10px;">
          <div style="width: 20px; height: 20px; border: 2px solid #3b82f6; border-top: 2px solid transparent; border-radius: 50%; animation: spin 1s linear infinite;"></div>
          <span>Exporting HTML...</span>
        </div>
        <style>
          @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        </style>
      `;
      loadingDiv.style.cssText = 'position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);background:white;padding:20px;border:1px solid #ccc;border-radius:8px;z-index:10000;box-shadow:0 4px 12px rgba(0,0,0,0.15);';
      document.body.appendChild(loadingDiv);

      // Wait for content to render
      await new Promise(resolve => setTimeout(resolve, 500));

      // Get all stylesheets for perfect CSS preservation
      const allStyles = Array.from(document.styleSheets)
        .map(styleSheet => {
          try {
            return Array.from(styleSheet.cssRules)
              .map(rule => rule.cssText)
              .join('\n');
          } catch (e) {
            return '';
          }
        })
        .join('\n');

      // Start building perfect HTML
      let htmlContent = `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Template Export - Perfect Fidelity</title>
    <style>
        /* Preserve all original styles */
        ${allStyles}

        /* Export-specific styles */
        body {
            margin: 0;
            padding: 20px;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            background: #f5f5f5;
            line-height: 1.5;
        }
        .export-page {
            background: white;
            margin: 20px auto;
            box-shadow: 0 4px 20px rgba(0,0,0,0.1);
            position: relative;
            page-break-after: always;
        }
        .export-page-title {
            position: absolute;
            top: -40px;
            left: 0;
            font-size: 18px;
            font-weight: 600;
            color: #333;
            background: white;
            padding: 8px 16px;
            border-radius: 8px 8px 0 0;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }
        .export-block {
            position: absolute;
            box-sizing: border-box;
        }

        /* Perfect chart styling */
        .export-chart img {
            width: 100%;
            height: 100%;
            object-fit: contain;
        }

        /* Print styles */
        @media print {
            body { background: white; padding: 0; }
            .export-page { margin: 0; box-shadow: none; page-break-after: always; }
            .export-page-title { position: static; margin-bottom: 20px; }
        }
    </style>
</head>
<body>
`;

      // Process each page
      if (!editorState.pages || editorState.pages.length === 0) {
        console.warn('No pages to export');
        return htmlContent + '</body></html>';
      }

      for (let pageIndex = 0; pageIndex < editorState.pages.length; pageIndex++) {
        const page = editorState.pages[pageIndex];

        // Find the actual page element
        const pageElement = document.querySelector(`[data-page-id="${page.id}"]`) as HTMLElement;
        if (!pageElement) {
          console.warn(`Page element not found for page ${page.id}`);
          continue;
        }

        // Generate background CSS for this page
        const backgroundStyle = getBackgroundStyle(page.background);
        const backgroundCSS = Object.entries(backgroundStyle)
          .map(([key, value]) => `${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value}`)
          .join('; ');

        htmlContent += `
    <div class="export-page"
         data-page-id="${page.id}"
         data-page-name="${page.name}"
         style="width: ${page.canvasSize.width}px; height: ${page.canvasSize.height}px; ${backgroundCSS};">
        <div class="export-page-title">Page ${pageIndex + 1}: ${page.name}</div>
`;

        // Clone each block with perfect fidelity
        const blocks = pageElement.querySelectorAll('[data-block-id]');
        console.log(`Found ${blocks.length} blocks in page ${pageIndex + 1}`);

        blocks.forEach((blockElement, blockIndex) => {
          const blockId = blockElement.getAttribute('data-block-id');
          const block = page.blocks.find(b => b.id === blockId);

          if (!block) {
            console.warn(`Block data not found for element ${blockId}`);
            return;
          }

          // Get computed styles
          const computedStyle = window.getComputedStyle(blockElement);
          const styleString = Array.from(computedStyle).map(prop =>
            `${prop}: ${computedStyle.getPropertyValue(prop)}`
          ).join('; ');

          // Clone the element content
          let blockContent = blockElement.innerHTML;

          // Special handling for script blocks - generate content without touching original iframe
          if (block.type === 'script') {
            // Generate rendered content directly from block data (non-destructive to editor)
            const scriptContent = block.content;
            const hasContent = scriptContent.html || scriptContent.css || scriptContent.javascript;
            const scriptBorder = editorState.previewMode ? 'none' : '1px solid #e5e7eb';

            blockContent = `
              <div style="width: 100%; height: 100%; border: ${scriptBorder}; border-radius: 8px; overflow: hidden; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
                <style>
                  ${scriptContent.css || ''}
                </style>
                ${scriptContent.html || (hasContent ? '' : '<div style="text-align: center; color: #666; font-size: 14px;">Script Block Content</div>')}
                <script>
                  try {
                    ${scriptContent.javascript || ''}
                  } catch (error) {
                    console.error('Script error:', error);
                  }
                </script>
              </div>
            `;
          }

          // Replace canvas elements with images
          const canvasElements = blockElement.querySelectorAll('canvas');
          canvasElements.forEach((canvas, canvasIndex) => {
            try {
              if (canvas.width > 0 && canvas.height > 0) {
                const imgData = canvas.toDataURL('image/png', 1.0);
                const imgTag = `<img src="${imgData}" style="width: 100%; height: 100%; object-fit: contain;" alt="Chart ${canvasIndex}" />`;
                blockContent = blockContent.replace(canvas.outerHTML, imgTag);
                console.log(`Replaced canvas in block ${blockIndex} with image`);
              }
            } catch (error) {
              console.warn(`Failed to convert canvas in block ${blockIndex}:`, error);
            }
          });

          htmlContent += `
        <div class="export-block"
             data-block-id="${block.id}"
             data-block-type="${block.type}"
             style="
                position: absolute;
                left: ${block.position.x}px;
                top: ${block.position.y}px;
                width: ${block.size.width}px;
                height: ${block.size.height}px;
                z-index: ${block.zIndex || 1};
                ${styleString}
             ">
            ${blockContent}
        </div>`;
        });

        htmlContent += `
    </div>
`;
      }

      // Add complete editor state as JSON for potential re-import
      const completeEditorData = {
        exportInfo: {
          timestamp: new Date().toISOString(),
          version: '1.0',
          totalPages: editorState.pages.length,
          totalBlocks: editorState.pages.reduce((total, page) => total + page.blocks.length, 0),
          exportType: 'html'
        },
        editorState: {
          pages: editorState.pages,
          currentPageId: editorState.currentPageId,
          zoom: editorState.zoom,
          showGrid: editorState.showGrid
        },
        metadata: {
          userAgent: navigator.userAgent,
          screenResolution: `${screen.width}x${screen.height}`,
          exportedBy: 'Template Editor v1.0'
        }
      };

      // CRITICAL: Add the embedded JSON data for import functionality
      htmlContent += `
    <!-- Template Editor Data for Import - CRITICAL FOR ROUND-TRIP -->
    <script id="template-editor-data" type="application/json">
${JSON.stringify(completeEditorData, null, 2)}
    </script>
</body>
</html>`;

      // Create and download

      const blob = new Blob([htmlContent], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const fileName = currentTemplateName || 'proposal';
      link.download = `${fileName}-${Date.now()}.html`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      // Remove loading indicator
      document.body.removeChild(loadingDiv);

      // Call onExport callback
      onExport?.(editorState, 'html');

      // Show success message
      const successDiv = document.createElement('div');
      successDiv.innerHTML = 'HTML exported successfully';
      successDiv.style.cssText = 'position:fixed;top:20px;right:20px;background:#10b981;color:white;padding:12px 20px;border-radius:8px;z-index:10000;box-shadow:0 4px 12px rgba(0,0,0,0.15);';
      document.body.appendChild(successDiv);
      setTimeout(() => {
        if (document.body.contains(successDiv)) {
          document.body.removeChild(successDiv);
        }
      }, 3000);

      console.log('🎯 PERFECT HTML export completed with 100% block fidelity');

    } catch (error) {
      console.error('Export failed:', error);
      alert(`Export failed: ${error.message}. Please try again.`);

      // Clean up
      const loadingDiv = document.querySelector('div[style*="Creating PERFECT HTML"]');
      if (loadingDiv && document.body.contains(loadingDiv)) {
        document.body.removeChild(loadingDiv);
      }
    }
  }, [editorState, currentTemplateName, onExport]);

  // Handle tender save (for tender contexts)
  const handleTenderSave = useCallback(async () => {
    try {
      if (onSave) {
        // Use the provided onSave callback for tender contexts
        await onSave(editorState);
        console.log('✅ Tender saved successfully');
      } else {
        console.warn('⚠️ No onSave callback provided for tender context');
        alert('Save functionality not configured for this context.');
      }
    } catch (error) {
      console.error('❌ Error saving tender:', error);
      alert('Failed to save tender. Please try again.');
    }
  }, [onSave, editorState]);

  // Open save template modal
  const openSaveModal = useCallback(async () => {
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        alert('Please log in to save templates.');
        return;
      }

      setShowSaveModal(true);
    } catch (error) {
      console.error('Error checking user authentication:', error);
      alert('Authentication error. Please try logging in again.');
    }
  }, []);

  // Handle save template action from modal
  const handleSaveTemplate = useCallback(async (action: 'update' | 'saveAsNew', name: string, description: string) => {
    try {
      setSaveLoading(true);

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Prepare template data
      const templateData: TemplateData = {
        name: name.trim(),
        description: description?.trim() || undefined,
        editorState: {
          pages: editorState.pages,
          currentPageId: editorState.currentPageId,
          zoom: editorState.zoom,
          showGrid: editorState.showGrid
        },
        exportInfo: {
          timestamp: new Date().toISOString(),
          version: '1.0',
          totalPages: editorState.pages.length,
          totalBlocks: editorState.pages.reduce((total, page) => total + page.blocks.length, 0),
          exportType: 'template'
        },
        metadata: {
          userAgent: navigator.userAgent,
          screenResolution: `${screen.width}x${screen.height}`,
          exportedBy: 'Template Editor v1.0'
        }
      };

      let result;

      if (action === 'update' && currentTemplateId) {
        // Update existing template
        result = await templateStorageService.updateTemplate(currentTemplateId, templateData, user.id);
      } else {
        // Save as new template
        result = await templateStorageService.saveTemplate(templateData, user.id);

        // If saving as new, update current template tracking
        if (result.success && result.template) {
          setCurrentTemplateId(result.template.id);
          setCurrentTemplateName(name);
          setCurrentTemplateDescription(description);
        }
      }

      if (result.success) {
        // Show success feedback
        const successDiv = document.createElement('div');
        successDiv.innerHTML = `
          <div style="display: flex; align-items: center; gap: 10px;">
            <span style="color: #10b981;">✅</span>
            <span>Template "${name}" ${action === 'update' ? 'updated' : 'saved'} successfully!</span>
          </div>
        `;
        successDiv.style.cssText = 'position:fixed;top:20px;right:20px;background:#f0fdf4;color:#166534;padding:12px 16px;border:1px solid #bbf7d0;border-radius:8px;z-index:10000;box-shadow:0 4px 12px rgba(0,0,0,0.15);';
        document.body.appendChild(successDiv);

        setTimeout(() => {
          if (document.body.contains(successDiv)) {
            document.body.removeChild(successDiv);
          }
        }, 4000);
      } else {
        throw new Error(result.error || 'Failed to save template');
      }

    } catch (error) {
      console.error('Save template failed:', error);

      // Show error feedback
      const errorDiv = document.createElement('div');
      errorDiv.innerHTML = `
        <div style="display: flex; align-items: center; gap: 10px;">
          <span style="color: #ef4444;">❌</span>
          <span>Failed to save template: ${error instanceof Error ? error.message : 'Unknown error'}</span>
        </div>
      `;
      errorDiv.style.cssText = 'position:fixed;top:20px;right:20px;background:#fef2f2;color:#dc2626;padding:12px 16px;border:1px solid #fecaca;border-radius:8px;z-index:10000;box-shadow:0 4px 12px rgba(0,0,0,0.15);';
      document.body.appendChild(errorDiv);

      setTimeout(() => {
        if (document.body.contains(errorDiv)) {
          document.body.removeChild(errorDiv);
        }
      }, 4000);
    } finally {
      setSaveLoading(false);
    }
  }, [editorState, currentTemplateId]);

  const exportAsJSON = useCallback(() => {
    try {
      const json = JSON.stringify(editorState, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `template-${Date.now()}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed. Please try again.');
    }
  }, [editorState]);

  const exportAsPDF = useCallback(async () => {
    console.log('🚀 PDF Export Started - Capturing Preview DOM');
    
    // Save original state
    const originalPreviewMode = editorState.previewMode;
    const originalZoom = editorState.zoom;
    const originalSelectedBlockId = editorState.selectedBlockId;
    
    try {
      // Enable preview mode to get exact preview rendering
      setEditorState(prev => ({
        ...prev,
        previewMode: true,
        selectedBlockId: null,
        zoom: 1 // Reset zoom to 1 for accurate capture
      }));
      
      // Wait for DOM to update and render
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Find all rendered page elements in the actual DOM (not iframe) - these are the actual preview pages
      const pageElements = document.querySelectorAll('.template-page');
      console.log(`📄 Found ${pageElements.length} page(s) in preview DOM`);
      
      if (pageElements.length === 0) {
        throw new Error('No pages found to export. Please ensure pages are visible.');
      }
      
      // Import html2canvas and jsPDF
      const html2canvas = (await import('html2canvas')).default;
      const { jsPDF } = await import('jspdf');
      
      // Create new PDF document
      const pdfDoc = new jsPDF({
        unit: 'mm',
        format: 'a4',
        orientation: 'portrait',
        compress: true
      });
      
      // Process each page separately - capture the actual rendered preview
      for (let i = 0; i < pageElements.length; i++) {
        const pageElement = pageElements[i] as HTMLElement;
        console.log(`📄 Processing page ${i + 1} of ${pageElements.length}...`);
        
        // Get the page ID from the DOM element
        const pageId = pageElement.getAttribute('data-page-id');
        if (!pageId) {
          console.warn(`Page element ${i} has no data-page-id attribute, skipping...`);
          continue;
        }
        
        // Find the page data from editorState
        const currentPage = editorState.pages.find((p: any) => p.id === pageId);
        if (!currentPage) {
          console.warn(`Page data not found for page ID: ${pageId}, skipping...`);
          continue;
        }
        
        // Scroll page into view for proper rendering
        pageElement.scrollIntoView({ behavior: 'instant', block: 'center' });
        await new Promise(resolve => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => resolve(undefined));
          });
        });
        
        // Add new page (except for first page)
        if (i > 0) {
          pdfDoc.addPage();
        }
        
        // Validate element is ready for capture
        if (!pageElement || !pageElement.offsetWidth || !pageElement.offsetHeight) {
          console.warn(`Page ${i + 1} element is not ready for capture, skipping...`);
          continue;
        }
        
        // Wait a bit more to ensure everything is rendered
        await new Promise(resolve => setTimeout(resolve, 200));
        
        // Capture EXACTLY what's rendered on screen - no manipulation
        // Just hide editor UI elements temporarily via CSS classes
        const originalClasses = pageElement.className;
        pageElement.classList.add('pdf-export-mode');
        
        // Hide selection rings and editor UI via CSS
        const style = document.createElement('style');
        style.id = 'pdf-export-styles';
        style.textContent = `
          .pdf-export-mode .ring-2,
          .pdf-export-mode .ring-blue-500,
          .pdf-export-mode .selected,
          .pdf-export-mode .multi-selected {
            outline: none !important;
            box-shadow: none !important;
            border: none !important;
          }
          .pdf-export-mode[class*="ring"] {
            outline: none !important;
            box-shadow: none !important;
          }
        `;
        document.head.appendChild(style);
        
        // Wait for styles to apply
        await new Promise(resolve => requestAnimationFrame(resolve));
        
        // Capture the actual rendered preview page with exact colors and positioning
        let canvas;
        try {
          // Use simpler html2canvas options without onclone to avoid cloning issues
          // Set foreignObjectRendering to false to prevent iframe cloning issues
          canvas = await html2canvas(pageElement, {
            scale: 2,
            useCORS: true,
            backgroundColor: '#ffffff',
            logging: false,
            allowTaint: false,
            removeContainer: false,
            imageTimeout: 30000,
            windowWidth: pageElement.offsetWidth,
            windowHeight: pageElement.offsetHeight,
            foreignObjectRendering: false, // Critical: Disable foreign object rendering to avoid iframe cloning issues
            ignoreElements: (element) => {
              try {
                if (!element) return false;
                
                // Ignore editor UI elements that shouldn't be in PDF
                if (element.classList) {
                  const classList = element.classList;
                  if (classList.contains('no-print') || 
                      classList.contains('template-editor-canvas-container') ||
                      element.getAttribute('data-editor-ui') === 'true') {
                    return true;
                  }
                }
                
                // Ignore iframes that might cause cloning issues
                if (element.tagName === 'IFRAME') {
                  return true;
                }
                
                // Ignore script tags
                if (element.tagName === 'SCRIPT') {
                  return true;
                }
                
                return false;
              } catch (e) {
                return false;
              }
            }
          });
          
          // Validate canvas was created successfully
          if (!canvas || !canvas.width || !canvas.height) {
            throw new Error('Canvas creation failed - invalid dimensions');
          }
          
        } catch (captureError) {
          console.error(`Error capturing page ${i + 1}:`, captureError);
          
          // Try fallback with even simpler options
          try {
            console.log(`Attempting fallback capture for page ${i + 1}...`);
            canvas = await html2canvas(pageElement, {
              scale: 1.5,
              useCORS: true,
              backgroundColor: '#ffffff',
              logging: false,
              allowTaint: false,
              removeContainer: false,
              imageTimeout: 30000,
              foreignObjectRendering: false,
            });
            
            // Validate fallback canvas
            if (!canvas || !canvas.width || !canvas.height) {
              throw new Error('Fallback canvas creation failed - invalid dimensions');
            }
            
            console.log(`Fallback capture successful for page ${i + 1}`);
          } catch (fallbackError) {
            console.error(`Fallback capture also failed for page ${i + 1}:`, fallbackError);
            // Clean up before skipping
            pageElement.className = originalClasses;
            const exportStyle = document.getElementById('pdf-export-styles');
            if (exportStyle) exportStyle.remove();
            console.warn(`Skipping page ${i + 1} due to capture failure`);
            continue;
          }
        } finally {
          // Restore original classes and remove temporary styles
          pageElement.className = originalClasses;
          const exportStyle = document.getElementById('pdf-export-styles');
          if (exportStyle) exportStyle.remove();
        }
        
        // Validate canvas exists before proceeding
        if (!canvas || !canvas.width || !canvas.height) {
          console.warn(`Invalid canvas for page ${i + 1}, skipping...`);
          continue;
        }
        
        // Convert canvas to image data with high quality
        const imgData = canvas.toDataURL('image/jpeg', 0.98);
        
        // Calculate dimensions to fit A4 (210mm x 297mm)
        const pdfWidth = 210; // A4 width in mm
        const pdfHeight = 297; // A4 height in mm
        const imgWidth = canvas.width;
        const imgHeight = canvas.height;
        
        // Convert pixels to mm (at 96dpi: 1px = 0.264583mm)
        const pxToMm = 0.264583;
        const imgWidthMm = imgWidth * pxToMm;
        const imgHeightMm = imgHeight * pxToMm;
        
        // Calculate scaling to fit page while maintaining aspect ratio
        const widthRatio = pdfWidth / imgWidthMm;
        const heightRatio = pdfHeight / imgHeightMm;
        const ratio = Math.min(widthRatio, heightRatio);
        
        const finalWidth = imgWidthMm * ratio;
        const finalHeight = imgHeightMm * ratio;
        
        // Center image on page
        const xOffset = (pdfWidth - finalWidth) / 2;
        const yOffset = (pdfHeight - finalHeight) / 2;
        
        // Add image to PDF
        pdfDoc.addImage(imgData, 'JPEG', xOffset, yOffset, finalWidth, finalHeight);
      }
      
      // Save PDF
      pdfDoc.save(`${currentTemplateName || 'proposal'}-${Date.now()}.pdf`);
      
      console.log('✅ PDF exported successfully');
      
      // Restore original state
      setEditorState(prev => ({
        ...prev,
        previewMode: originalPreviewMode,
        zoom: originalZoom,
        selectedBlockId: originalSelectedBlockId
      }));
      
      // Call onExport callback
      onExport?.(editorState, 'pdf');
      
      // Show success message
      const successDiv = document.createElement('div');
      successDiv.innerHTML = '✅ PDF exported successfully';
      successDiv.style.cssText = 'position:fixed;top:20px;right:20px;background:#10b981;color:white;padding:12px 20px;border-radius:8px;z-index:10000;box-shadow:0 4px 12px rgba(0,0,0,0.15);font-weight:500;';
      document.body.appendChild(successDiv);
      setTimeout(() => {
        if (document.body.contains(successDiv)) {
          document.body.removeChild(successDiv);
        }
      }, 3000);

    } catch (error) {
      console.error('❌ PDF Export failed:', error);
      
      // Restore original state on error
      setEditorState(prev => ({
        ...prev,
        previewMode: originalPreviewMode,
        zoom: originalZoom,
        selectedBlockId: originalSelectedBlockId
      }));
      
      // Show error message
      const errorDiv = document.createElement('div');
      errorDiv.innerHTML = `❌ Export failed: ${error instanceof Error ? error.message : 'Unknown error'}`;
      errorDiv.style.cssText = 'position:fixed;top:20px;right:20px;background:#ef4444;color:white;padding:12px 20px;border-radius:8px;z-index:10000;box-shadow:0 4px 12px rgba(0,0,0,0.15);font-weight:500;max-width:400px;';
      document.body.appendChild(errorDiv);
      setTimeout(() => {
        if (document.body.contains(errorDiv)) {
          document.body.removeChild(errorDiv);
        }
      }, 5000);
    }
  }, [editorState, currentTemplateName, onExport]);

  // Import JSON
  const importFromJSON = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const json = e.target?.result as string;
            const importedState = JSON.parse(json);

            // Ensure variables exist in imported JSON
            const finalState = {
              ...importedState,
              variables: (importedState.variables && importedState.variables.length > 0)
                ? importedState.variables
                : DEFAULT_TEMPLATE_VARIABLES
            };

            setEditorState(finalState);
          } catch (error) {
            console.error('Import failed:', error);
            alert('Import failed. Please check the file format.');
          }
        };
        reader.readAsText(file);
      }
    };
    input.click();
  }, []);

  // Track export in progress to prevent multiple executions
  const exportInProgressRef = useRef(false);
  const lastExportTimestampRef = useRef<number | null>(null);

  // Handle external export trigger - must be after exportAsPDF and exportAsHTML declarations
  useEffect(() => {
    // Only proceed if exportTrigger exists, has a timestamp, and we haven't processed this timestamp yet
    if (!exportTrigger || !exportTrigger.timestamp) {
      return;
    }

    // Prevent duplicate exports - check if we've already processed this timestamp
    if (lastExportTimestampRef.current === exportTrigger.timestamp) {
      console.log('⚠️ Export already processed for this timestamp, skipping...');
      return;
    }

    // Prevent concurrent exports
    if (exportInProgressRef.current) {
      console.log('⚠️ Export already in progress, skipping...');
      return;
    }

    console.log(`📤 Export triggered: ${exportTrigger.format} at ${exportTrigger.timestamp}`);
    
    // Mark export as in progress and record timestamp
    exportInProgressRef.current = true;
    lastExportTimestampRef.current = exportTrigger.timestamp;
    
    // Add a small delay to ensure state is stable
    const timeoutId = setTimeout(async () => {
      try {
        const { downloadService } = await import('../../services/downloadService');
        if (exportTrigger.format === 'pdf') {
          console.log('🚀 Calling downloadAsPDF...');
          await downloadService.downloadAsPDF(editorState);
        } else if (exportTrigger.format === 'html') {
          console.log('🚀 Calling downloadAsHTML...');
          await downloadService.downloadAsHTML(editorState);
        }
      } catch (error) {
        console.error('❌ Export trigger error:', error);
      } finally {
        // Reset export in progress flag after a delay to allow cleanup
        setTimeout(() => {
          exportInProgressRef.current = false;
        }, 1000);
      }
    }, 100);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [exportTrigger?.timestamp, exportTrigger?.format]); // Removed function dependencies to prevent re-triggering

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle shortcuts when not typing in inputs
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      // Ctrl/Cmd + Z: Undo (requires content level)
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        if (!hasEditorContent) return;
        e.preventDefault();
        undo();
      }

      // Ctrl/Cmd + Y or Ctrl/Cmd + Shift + Z: Redo (requires content level)
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        if (!hasEditorContent) return;
        e.preventDefault();
        redo();
      }

      // Ctrl/Cmd + C: Copy selected blocks (requires content level)
      if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
        if (!hasEditorContent) return;
        e.preventDefault();
        copySelectedBlocks();
      }

      // Ctrl/Cmd + V: Paste blocks (requires full level)
      if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
        if (!hasEditorFull) return;
        e.preventDefault();
        pasteBlocks();
      }

      // Ctrl/Cmd + D: Duplicate selected blocks (requires full level)
      if ((e.ctrlKey || e.metaKey) && e.key === 'd' && selectedBlockIds.length > 0) {
        if (!hasEditorFull) return;
        e.preventDefault();
        duplicateBlocks();
      }

      // Delete key: Delete selected blocks (requires full level)
      if (e.key === 'Delete' && selectedBlockIds.length > 0) {
        if (!hasEditorFull) return;
        e.preventDefault();
        deleteBlock();
      }

      // Ctrl/Cmd + A: Select all blocks on current page (requires layout level)
      if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
        if (!hasEditorLayout) return;
        e.preventDefault();
        const currentPage = getCurrentPage();
        const allBlockIds = currentPage.blocks.map(block => block.id);
        setSelectedBlockIds(allBlockIds);
        if (allBlockIds.length > 0) {
          setEditorState(prev => ({
            ...prev,
            selectedBlockId: allBlockIds[allBlockIds.length - 1]
          }));
        }
      }

      // Escape: Clear selection (always allowed)
      if (e.key === 'Escape') {
        selectBlock(null);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedBlockIds, duplicateBlocks, deleteBlock, getCurrentPage, selectBlock, copySelectedBlocks, pasteBlocks, undo, redo, hasEditorContent, hasEditorFull, hasEditorLayout]);

  // Memoized components
  const Toolbar = useMemo(() => (
    <div className="h-14 bg-white border-b border-gray-200 flex items-center justify-between px-4 relative z-50 flex-shrink-0">
      <div className="flex items-center space-x-1">
        {/* Back button */}
        <button
          onClick={() => { if (window.opener) { window.close(); } else { navigate(-1); } }}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors mr-1"
          title="Back"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back</span>
        </button>
        <div className="w-px h-6 bg-gray-200 mx-1" />
        {/* View controls — always visible regardless of permission level */}
        <button
          onClick={zoomOut}
          className="p-2 hover:bg-gray-100 rounded"
          title="Zoom Out"
        >
          <ZoomOut className="w-4 h-4" />
        </button>
        <span className="text-sm font-medium px-2 min-w-[50px] text-center">
          {Math.round(editorState.zoom * 100)}%
        </span>
        <button
          onClick={zoomIn}
          className="p-2 hover:bg-gray-100 rounded"
          title="Zoom In"
        >
          <ZoomIn className="w-4 h-4" />
        </button>
        <button
          onClick={fitToScreen}
          className="p-2 hover:bg-gray-100 rounded"
          title="Fit to Screen"
        >
          <Maximize2 className="w-4 h-4" />
        </button>
        {!editorState.previewMode && (
          <button
            onClick={toggleGrid}
            className={`p-2 rounded ${editorState.showGrid ? 'bg-blue-100 text-blue-600' : 'hover:bg-gray-100'}`}
            title="Toggle Grid"
          >
            <Grid3X3 className="w-4 h-4" />
          </button>
        )}
        {!editorState.previewMode && (
          <button
            onClick={() => setEditorState(prev => ({ ...prev, showPageNumbers: !(prev as any).showPageNumbers }))}
            className={`p-2 rounded text-xs font-bold ${(editorState as any).showPageNumbers ? 'bg-blue-100 text-blue-600' : 'hover:bg-gray-100 text-gray-600'}`}
            title="Toggle Page Numbers"
          >
            #
          </button>
        )}
        {!editorState.previewMode && hasEditorFull && (
          <button
            onClick={() => setEditorState(prev => ({ ...prev, showTemplatePicker: !(prev as any).showTemplatePicker }))}
            className={`p-2 rounded flex items-center gap-1 text-xs font-medium ${(editorState as any).showTemplatePicker ? 'bg-blue-100 text-blue-600' : 'hover:bg-gray-100 text-gray-600'}`}
            title="Page Layout Templates"
          >
            <LayoutTemplateIcon className="w-4 h-4" />
            <span className="hidden lg:inline">Templates</span>
          </button>
        )}

        {/* Editing tools — gated by permission */}
        {!editorState.previewMode && hasEditorContent && (
          <>
            <div className="w-px h-6 bg-gray-300 mx-2" />
            {/* History */}
            <button
              onClick={undo}
              className={`p-2 hover:bg-gray-100 rounded ${historyIndex <= 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
              title="Undo (Ctrl+Z)"
              disabled={historyIndex <= 0}
            >
              <Undo className="w-4 h-4" />
            </button>
            <button
              onClick={redo}
              className={`p-2 hover:bg-gray-100 rounded ${historyIndex >= history.length - 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
              title="Redo (Ctrl+Y)"
              disabled={historyIndex >= history.length - 1}
            >
              <Redo className="w-4 h-4" />
            </button>

            <div className="w-px h-6 bg-gray-300 mx-2" />

            {/* Clipboard */}
            <button
              onClick={copySelectedBlocks}
              className={`p-2 hover:bg-gray-100 rounded ${!editorState.selectedBlockId && selectedBlockIds.length === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
              title="Copy (Ctrl+C)"
              disabled={!editorState.selectedBlockId && selectedBlockIds.length === 0}
            >
              <Copy className="w-4 h-4" />
            </button>
            <button
              onClick={pasteBlocks}
              className="p-2 hover:bg-gray-100 rounded"
              title="Paste (Ctrl+V)"
            >
              <Clipboard className="w-4 h-4" />
            </button>
            <button
              onClick={duplicateBlocks}
              className={`p-2 hover:bg-gray-100 rounded ${!editorState.selectedBlockId && selectedBlockIds.length === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
              title="Duplicate (Ctrl+D)"
              disabled={!editorState.selectedBlockId && selectedBlockIds.length === 0}
            >
              <Copy className="w-4 h-4" />
            </button>

            <div className="w-px h-6 bg-gray-300 mx-2" />

            {/* Alignment */}
            <button
              onClick={alignLeft}
              className={`p-2 hover:bg-gray-100 rounded ${!editorState.selectedBlockId && selectedBlockIds.length === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
              title="Align Left"
              disabled={!editorState.selectedBlockId && selectedBlockIds.length === 0}
            >
              <AlignLeft className="w-4 h-4" />
            </button>
            <button
              onClick={alignCenter}
              className={`p-2 hover:bg-gray-100 rounded ${!editorState.selectedBlockId && selectedBlockIds.length === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
              title="Align Center"
              disabled={!editorState.selectedBlockId && selectedBlockIds.length === 0}
            >
              <AlignCenter className="w-4 h-4" />
            </button>
            <button
              onClick={alignRight}
              className={`p-2 hover:bg-gray-100 rounded ${!editorState.selectedBlockId && selectedBlockIds.length === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
              title="Align Right"
              disabled={!editorState.selectedBlockId && selectedBlockIds.length === 0}
            >
              <AlignRight className="w-4 h-4" />
            </button>

          </>
        )}
      </div>

      <div className="flex items-center space-x-2">
        {!editorState.previewMode && (
          <>
            <span className="text-sm text-gray-500">
              {getCurrentPage().blocks.length} blocks
            </span>
            <div className="w-px h-6 bg-gray-300" />
          </>
        )}

        {/* Add Page + AI Rewrite — grouped quick-access next to Edit/Preview */}
        {!editorState.previewMode && hasEditorFull && (
          <>
            <button
              onClick={addPage}
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
              title="Add new page"
            >
              <Plus className="w-3.5 h-3.5" />
              Add Page
            </button>
            {/* AI Rewrite toolbar button — hidden
            <button
              onClick={() => setSidebarTab(prev => prev === 'ai' ? 'navigator' : 'ai')}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 text-sm font-medium rounded-lg transition-all ${
                sidebarTab === 'ai'
                  ? 'bg-gradient-to-r from-violet-500 to-blue-500 text-white shadow-md'
                  : 'text-violet-700 bg-violet-50 border border-violet-200 hover:bg-violet-100'
              }`}
              title="AI Content Regeneration"
            >
              <Sparkles className="w-3.5 h-3.5" />
              AI Rewrite
            </button>
            */}
            <div className="w-px h-6 bg-gray-300" />
          </>
        )}

        {/* Preview/Edit Mode Toggle */}
        <div className="flex items-center bg-gray-100 rounded-lg p-1">
          <button
            onClick={togglePreviewMode}
            className={`px-3 py-1 text-sm rounded-md transition-colors ${
              !editorState.previewMode
                ? `bg-white text-gray-900 ${getShadowStyle('shadow-sm')}`
                : 'text-gray-600 hover:text-gray-900'
            }`}
            title="Edit Mode"
          >
            <Edit3 className="w-4 h-4 mr-1 inline" />
            Edit
          </button>
          <button
            onClick={togglePreviewMode}
            className={`px-3 py-1 text-sm rounded-md transition-colors ${
              editorState.previewMode
                ? `bg-white text-gray-900 ${getShadowStyle('shadow-sm')}`
                : 'text-gray-600 hover:text-gray-900'
            }`}
            title="Preview Mode"
          >
            <Eye className="w-4 h-4 mr-1 inline" />
            Preview
          </button>
        </div>

        {!editorState.previewMode && hasEditorFull && (
          <>
            <div className="w-px h-6 bg-gray-300" />
            <button
              onClick={() => setShowPageBackgroundPanel(true)}
              className="p-2 hover:bg-gray-100 rounded"
              title="Page Background"
            >
              <Palette className="w-4 h-4" />
            </button>
            <button
              onClick={() => setShowGlobalTypographyModal(true)}
              className="p-2 hover:bg-gray-100 rounded"
              title="Global Typography"
            >
              <Type className="w-4 h-4" />
            </button>
            <button
              onClick={importTemplate}
              className="p-2 hover:bg-gray-100 rounded"
              title="Import Template (HTML/JSON)"
            >
              <Upload className="w-4 h-4" />
            </button>
          </>
        )}

        <button
          onClick={() => setShowExportDialog(true)}
          className="p-2 hover:bg-gray-100 rounded"
          title="Download (PDF / Word / HTML)"
        >
          <Download className="w-4 h-4" />
        </button>

        {/* Multi-Block Operations */}
        {!editorState.previewMode && hasEditorFull && selectedBlockIds.length > 0 && (
          <>
            <div className="w-px h-6 bg-gray-300" />
            <div className="flex items-center space-x-2 px-2 py-1 bg-blue-50 rounded-lg border border-blue-200">
              <span className="text-xs text-blue-700 font-medium">
                {selectedBlockIds.length} selected
              </span>
              <button
                onClick={duplicateBlocks}
                className="p-1 hover:bg-blue-100 rounded text-blue-600"
                title="Duplicate Selected Blocks"
              >
                <Copy className="w-4 h-4" />
              </button>
              <button
                onClick={() => deleteBlock()}
                className="p-1 hover:bg-red-100 rounded text-red-600"
                title="Delete Selected Blocks"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </>
        )}


        <button
          onClick={context === 'template' ? openSaveModal : handleTenderSave}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 font-medium"
        >
          <Save className="w-4 h-4 mr-2 inline" />
          {context === 'template' ? 'Save Template' : 'Save Tender'}
        </button>
      </div>
    </div>
  ), [
    editorState.zoom,
    editorState.showGrid,
    editorState.selectedBlockId,
    editorState.previewMode,
    historyIndex,
    history.length,
    selectedBlockIds.length,
    sidebarTab,
    getCurrentPage,
    zoomIn,
    zoomOut,
    fitToScreen,
    toggleGrid,
    togglePreviewMode,
    undo,
    redo,
    copySelectedBlocks,
    pasteBlocks,
    duplicateBlocks,
    alignLeft,
    alignCenter,
    alignRight,
    importTemplate,
    openSaveModal,
    editorState.previewMode,
    hasEditorContent,
    hasEditorFull,
    hasEditorLayout,
    navigate
  ]);

  // Enhanced Page Navigation UI with Horizontal Scrolling
  const pageNavigation = useMemo(() => (
    <div className="bg-gray-50 border-b border-gray-200 px-4 py-3">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center space-x-3 min-w-0 flex-1">
          <span className="text-sm font-medium text-gray-700 flex-shrink-0">Pages:</span>
          <div className="flex-1 min-w-0">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handlePageDragEnd}
            >
              <SortableContext
                items={editorState.pages.map(page => page.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="flex items-center space-x-2 overflow-x-auto pb-1" style={{ scrollbarWidth: 'thin' }}>
                  {editorState.pages.map((page, index) => (
                    <SortablePageItem
                      key={page.id}
                      pageNumber={index + 1}
                      pageName={page.name}
                      isActive={page.id === editorState.currentPageId}
                      onSwitch={() => switchPage(page.id)}
                      onDuplicate={() => duplicatePage(page.id)}
                      onDelete={() => deletePage(page.id)}
                      onRename={(newName) => renamePage(page.id, newName)}
                      canDelete={editorState.pages.length > 1}
                      pageId={page.id}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </div>
        </div>
        <button
          onClick={addPage}
          className="px-4 py-2 text-sm bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors font-medium flex-shrink-0"
        >
          <Plus className="w-4 h-4 mr-1 inline" />
          Add Page
        </button>
      </div>
    </div>
  ), [editorState.pages, editorState.currentPageId, switchPage, deletePage, addPage, duplicatePage, renamePage, sensors, handlePageDragEnd]);

  return (
    <div className={`h-full flex flex-col bg-gray-50 ${className || ''}`}>
      {/* CSS Override for Preview Mode - Remove ALL visual borders */}
      {editorState.previewMode && (
        <style>
          {`
            .template-editor-canvas-container .template-editor-block,
            .template-editor-canvas-container .template-editor-block *,
            .template-editor-canvas-container .template-page,
            .template-editor-canvas-container .template-page * {
              border: none !important;
              outline: none !important;
              box-shadow: none !important;
            }
            /* Ensure ALL pages are fully visible in preview mode - no dimming */
            .template-editor-canvas-container .template-page {
              opacity: 1 !important;
            }
            .template-editor-canvas-container .template-editor-block:hover,
            .template-editor-canvas-container .template-editor-block:focus,
            .template-editor-canvas-container .template-editor-block.selected {
              border: none !important;
              outline: none !important;
              box-shadow: none !important;
            }
            /* Hide react-rnd resize handles and borders in preview mode */
            .template-editor-canvas-container .react-resizable-handle,
            .template-editor-canvas-container .react-resizable-handle::before,
            .template-editor-canvas-container .react-resizable-handle::after {
              display: none !important;
              visibility: hidden !important;
              opacity: 0 !important;
            }
            .template-editor-canvas-container .react-draggable,
            .template-editor-canvas-container .react-resizable {
              border: none !important;
              outline: none !important;
            }
            /* Hide any rnd-specific elements */
            .template-editor-canvas-container [class*="rnd"],
            .template-editor-canvas-container [class*="resize"],
            .template-editor-canvas-container [class*="handle"] {
              border: none !important;
              outline: none !important;
              box-shadow: none !important;
            }
            .template-editor-canvas-container [class*="rnd"] [class*="handle"],
            .template-editor-canvas-container [class*="resize-handle"] {
              display: none !important;
              visibility: hidden !important;
              opacity: 0 !important;
            }
          `}
        </style>
      )}

      {/* Conditionally render Toolbar based on config - always visible in preview mode for mode toggle */}
      {(config.showToolbar !== false) && Toolbar}

      <PanelGroup direction="horizontal" className="flex-1">
        {/* Sidebar - Hidden in preview mode and view-only mode */}
        {!editorState.previewMode && hasEditorLayout && (
          <Panel defaultSize={22} minSize={18} maxSize={30}>
          <div className="h-full bg-white border-r border-gray-200 flex flex-col">

            {/* Tab bar */}
            <div className="flex border-b border-gray-200 flex-shrink-0">
              <button
                onClick={() => setSidebarTab('navigator')}
                className={`flex-1 flex items-center justify-center gap-1 py-2.5 text-xs font-medium transition-colors ${
                  sidebarTab === 'navigator'
                    ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Layers className="w-3.5 h-3.5" />
                Navigator
              </button>
              {hasEditorFull && (
                <button
                  onClick={() => setSidebarTab('blocks')}
                  className={`flex-1 flex items-center justify-center gap-1 py-2.5 text-xs font-medium transition-colors ${
                    sidebarTab === 'blocks'
                      ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Square className="w-3.5 h-3.5" />
                  Blocks
                </button>
              )}
              {/* AI sidebar tab — hidden
              {hasEditorFull && (
                <button
                  onClick={() => setSidebarTab('ai')}
                  className={`flex-1 flex items-center justify-center gap-1 py-2.5 text-xs font-medium transition-colors ${
                    sidebarTab === 'ai'
                      ? 'text-violet-600 border-b-2 border-violet-600 bg-violet-50'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  AI
                </button>
              )}
              */}
            </div>

            {/* Tab content */}
            <div className="flex-1 overflow-y-auto min-h-0">
              {sidebarTab === 'navigator' ? (
                <PageNavigatorPanel
                  pages={editorState.pages as any}
                  currentPageId={editorState.currentPageId}
                  onSwitchPage={navigateToPage}
                  onAddPage={addPage}
                  onDuplicatePage={duplicatePage}
                  onDeletePage={deletePage}
                  onRenamePage={renamePage}
                  onDragEnd={handlePageDragEnd}
                  onAITrigger={handleAITriggerFromNavigator}
                  canDeletePage={editorState.pages.length > 1}
                  canEditContent={canEditContent}
                  canEditLayout={canEditLayout}
                  hasEditorFull={hasEditorFull}
                />
              /* AI panel content — hidden
              ) : sidebarTab === 'ai' ? (
                <AIRegenerationPanel
                  inline
                  pages={editorState.pages}
                  onClose={() => setSidebarTab('navigator')}
                  onApply={handleApplyRegeneration}
                  apiBaseUrl={typeof window !== 'undefined' ? (import.meta.env?.VITE_API_URL || 'http://localhost:3001') : 'http://localhost:3001'}
                  preselection={aiPreselection}
                />
              */
              ) : (
              <div className="p-2">
            {/* Page Settings */}
            <div className="mb-4">
              <PageSettingsPanel
                currentSettings={editorState.pageSettings}
                onSettingsChange={handlePageSettingsChange}
                onTemplateChange={handlePageTemplateChange}
                className="mb-2"
              />
            </div>

            {/* Basic Blocks */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Basic Blocks</h3>
              <div className="grid grid-cols-2 gap-2">
                {BASIC_BLOCK_TEMPLATES.map((template) => {
                  const Icon = template.icon;
                  return (
                    <motion.button
                      key={template.id}
                      onClick={() => addBlock(template)}
                      className="p-3 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <Icon className="w-5 h-5 mx-auto mb-1 text-gray-600" />
                      <p className="text-xs text-gray-700">{template.name}</p>
                    </motion.button>
                  );
                })}
              </div>
            </div>

            {/* Advanced Blocks Toggle */}
            <div className="mb-6">
              <button
                onClick={() => setShowAdvancedBlocks(!showAdvancedBlocks)}
                className="w-full flex items-center justify-between p-3 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg hover:from-purple-100 hover:to-blue-100 transition-colors"
              >
                <h3 className="text-sm font-semibold text-purple-900">Advanced Blocks</h3>
                <motion.div
                  animate={{ rotate: showAdvancedBlocks ? 180 : 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <ChevronDown className="w-4 h-4 text-purple-600" />
                </motion.div>
              </button>

              <AnimatePresence>
                {showAdvancedBlocks && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="pt-4 space-y-4">
                      {/* Text & Content Blocks */}
                      <div>
                        <h4 className="text-xs font-medium text-gray-600 mb-2">📝 Text & Content</h4>
                        <div className="grid grid-cols-2 gap-2">
                          {ADVANCED_BLOCK_TEMPLATES.filter(t => ['heading1', 'heading2', 'heading3', 'paragraph', 'quote', 'callout'].includes(t.id)).map((template) => {
                            const Icon = template.icon;
                            return (
                              <motion.button
                                key={template.id}
                                onClick={() => addBlock(template)}
                                className="p-3 border border-gray-200 rounded-lg hover:border-purple-300 hover:bg-purple-50 transition-colors"
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                              >
                                <Icon className="w-5 h-5 mx-auto mb-1 text-purple-600" />
                                <p className="text-xs text-gray-700">{template.name}</p>
                              </motion.button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Data Visualization */}
                      <div>
                        <h4 className="text-xs font-medium text-gray-600 mb-2">📊 Data Visualization</h4>
                        <div className="grid grid-cols-2 gap-2">
                          {ADVANCED_BLOCK_TEMPLATES.filter(t => ['bar-chart', 'line-chart', 'pie-chart', 'kpi-card', 'progress-bar', 'gauge-chart', 'data-table'].includes(t.id)).map((template) => {
                            const Icon = template.icon;
                            return (
                              <motion.button
                                key={template.id}
                                onClick={() => addBlock(template)}
                                className="p-3 border border-gray-200 rounded-lg hover:border-green-300 hover:bg-green-50 transition-colors"
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                              >
                                <Icon className="w-5 h-5 mx-auto mb-1 text-green-600" />
                                <p className="text-xs text-gray-700">{template.name}</p>
                              </motion.button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Design Elements */}
                      <div>
                        <h4 className="text-xs font-medium text-gray-600 mb-2">🎨 Design Elements</h4>
                        <div className="grid grid-cols-2 gap-2">
                          {ADVANCED_BLOCK_TEMPLATES.filter(t => ['badge', 'border-frame', 'timeline', 'accordion', 'icon-block', 'script'].includes(t.id)).map((template) => {
                            const Icon = template.icon;
                            return (
                              <motion.button
                                key={template.id}
                                onClick={() => addBlock(template)}
                                className="p-3 border border-gray-200 rounded-lg hover:border-orange-300 hover:bg-orange-50 transition-colors"
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                              >
                                <Icon className="w-5 h-5 mx-auto mb-1 text-orange-600" />
                                <p className="text-xs text-gray-700">{template.name}</p>
                              </motion.button>
                            );
                          })}
                        </div>
                      </div>

                      {/* AI & Layout Blocks */}
                      <div>
                        <h4 className="text-xs font-medium text-gray-600 mb-2">✨ AI & Layout</h4>
                        <div className="grid grid-cols-2 gap-2">
                          {/* Markdown block */}
                          <motion.button
                            onClick={() => addBlock({
                              id: 'markdown',
                              type: 'markdown' as const,
                              name: 'Markdown',
                              icon: FileCode2,
                              defaultSize: { width: 400, height: 200 },
                              defaultContent: { markdown: '## Section Title\n\nWrite your **markdown** content here.\n\n- Bullet point\n- Another point\n\n> Blockquote example' },
                            })}
                            className="p-3 border border-gray-200 rounded-lg hover:border-violet-300 hover:bg-violet-50 transition-colors"
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                          >
                            <FileCode2 className="w-5 h-5 mx-auto mb-1 text-violet-600" />
                            <p className="text-xs text-gray-700">Markdown</p>
                          </motion.button>

                          {/* Section Divider */}
                          <motion.button
                            onClick={() => addBlock({
                              id: 'section-marker',
                              type: 'section-marker' as const,
                              name: 'Section Divider',
                              icon: Divide,
                              defaultSize: { width: 760, height: 44 },
                              defaultContent: { title: 'New Section', color: '#3b82f6' },
                            })}
                            className="p-3 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors"
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                          >
                            <Divide className="w-5 h-5 mx-auto mb-1 text-blue-600" />
                            <p className="text-xs text-gray-700">Section Divider</p>
                          </motion.button>
                        </div>
                      </div>

                      {/* Advanced Shapes */}
                      <div>
                        <h4 className="text-xs font-medium text-gray-600 mb-2">🔷 Advanced Shapes</h4>
                        <div className="grid grid-cols-2 gap-2">
                          {ADVANCED_SHAPE_TEMPLATES.map((template) => {
                            const Icon = template.icon;
                            return (
                              <motion.button
                                key={template.id}
                                onClick={() => addBlock(template)}
                                className="p-3 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors"
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                              >
                                <Icon className="w-5 h-5 mx-auto mb-1 text-blue-600" />
                                <p className="text-xs text-gray-700">{template.name}</p>
                              </motion.button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Quick Actions */}
            <div className="mt-6 pt-4 border-t border-gray-200">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Quick Actions</h3>
              <div className="space-y-2">
                <button className="w-full flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded">
                  <Copy className="w-4 h-4 mr-2" />
                  Copy Selected
                </button>
                <button className="w-full flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded">
                  <Clipboard className="w-4 h-4 mr-2" />
                  Paste
                </button>
                <button className="w-full flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded">
                  <AlignCenter className="w-4 h-4 mr-2" />
                  Align Center
                </button>
              </div>
            </div>
              </div>
              )}
            </div>
          </div>
        </Panel>
        )}

        {!editorState.previewMode && (
          <PanelResizeHandle className="w-1 bg-gray-300 hover:bg-gray-400 transition-colors" />
        )}

        {/* Canvas Area */}
        <Panel defaultSize={editorState.previewMode ? 100 : 60}>
          <div className="h-full overflow-auto bg-gray-100 p-4 template-editor-canvas-container relative" style={{ isolation: 'isolate' }}>
            {/* All Pages Displayed Vertically */}
            <div className="space-y-8">
              {editorState.pages.map((page, pageIndex) => (
                <div key={page.id} className="relative">
                  {/* Page Header - Hidden in preview mode */}
                  {!editorState.previewMode && (
                    <PageHeaderEditable
                      page={page}
                      isActive={page.id === editorState.currentPageId}
                      pageIndex={pageIndex}
                      onSwitch={() => switchPage(page.id)}
                      onDuplicate={() => duplicatePage(page.id)}
                      onDelete={() => deletePage(page.id)}
                      onRename={(newName) => renamePage(page.id, newName)}
                      canDelete={editorState.pages.length > 1}
                    />
                  )}

                  {/* Grid Background for Current Page */}
                  {editorState.showGrid && page.id === editorState.currentPageId && (
                    <div
                      className="absolute opacity-20 pointer-events-none"
                      style={{
                        width: page.canvasSize.width * editorState.zoom,
                        // Support dynamic height for grid
                        ...(page.canvasSize?.height === 'auto' || editorState.selectedPageTemplate?.isDynamic
                          ? {
                              minHeight: 600 * editorState.zoom,
                              height: 'auto'
                            }
                          : {
                              height: page.canvasSize.height * editorState.zoom
                            }
                        ),
                        backgroundImage: `
                          linear-gradient(to right, #e5e7eb 1px, transparent 1px),
                          linear-gradient(to bottom, #e5e7eb 1px, transparent 1px)
                        `,
                        backgroundSize: `${20 * editorState.zoom}px ${20 * editorState.zoom}px`,
                      }}
                    />
                  )}

                  {/* Page Canvas */}
                  <div
                    className={`relative mx-auto template-editor-canvas template-page ${getShadowStyle('shadow-lg')} ${
                      page.id === editorState.currentPageId ? getRingStyle('ring-2 ring-blue-500') : (editorState.previewMode ? '' : 'opacity-75')
                    }`}
                    data-page-id={page.id}
                    style={{
                      width: (page.canvasSize?.width || 800) * editorState.zoom,
                      // Support dynamic height - if height is 'auto' or page template is dynamic, use minHeight instead
                      ...(page.canvasSize?.height === 'auto' || editorState.selectedPageTemplate?.isDynamic
                        ? {
                            minHeight: 600 * editorState.zoom,
                            height: 'auto'
                          }
                        : {
                            height: (page.canvasSize?.height || 1000) * editorState.zoom
                          }
                      ),
                      ...getBackgroundStyle(page.background || { type: 'solid', color: '#ffffff' }),
                    }}
                    onClick={() => {
                      if (page.id !== editorState.currentPageId) {
                        switchPage(page.id);
                      } else {
                        selectBlock(null);
                      }
                    }}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      // Switch to this page if not current
                      if (page.id !== editorState.currentPageId) {
                        switchPage(page.id);
                      }
                      // Show context menu for canvas (no block selected)
                      handleContextMenu(e);
                    }}
                  >
                    {/* Render Blocks for All Pages */}
                    <AnimatePresence>
                      {page.blocks.map((block) => (
                  <Rnd
                    key={block.id}
                    size={{
                      width: block.size.width * editorState.zoom,
                      height: block.size.height * editorState.zoom,
                    }}
                    position={{
                      x: block.position.x * editorState.zoom,
                      y: block.position.y * editorState.zoom,
                    }}
                    enableUserSelectHack={true}
                    disableDragging={editorState.previewMode}
                    enableResizing={!editorState.previewMode}
                    cancel=".no-drag"
                    onDrag={(e, d) => {
                      // Check if dragging over a different page
                      const pageElements = document.querySelectorAll('.template-page');
                      let targetPageId = page.id;

                      pageElements.forEach((pageEl) => {
                        const rect = pageEl.getBoundingClientRect();
                        if (e.clientX >= rect.left && e.clientX <= rect.right &&
                            e.clientY >= rect.top && e.clientY <= rect.bottom) {
                          const pageId = pageEl.getAttribute('data-page-id');
                          if (pageId && pageId !== page.id) {
                            targetPageId = pageId;
                          }
                        }
                      });

                      // Visual feedback for cross-page drag
                      if (targetPageId !== page.id) {
                        const targetPage = document.querySelector(`[data-page-id="${targetPageId}"]`);
                        if (targetPage) {
                          targetPage.classList.add('drag-over');
                        }
                      }
                    }}
                    onDragStop={(e, d) => {
                      // Remove visual feedback
                      document.querySelectorAll('.template-page').forEach(el => {
                        el.classList.remove('drag-over');
                      });

                      // Check if dropped on a different page
                      const pageElements = document.querySelectorAll('.template-page');
                      let targetPageId = page.id;

                      pageElements.forEach((pageEl) => {
                        const rect = pageEl.getBoundingClientRect();
                        if (e.clientX >= rect.left && e.clientX <= rect.right &&
                            e.clientY >= rect.top && e.clientY <= rect.bottom) {
                          const pageId = pageEl.getAttribute('data-page-id');
                          if (pageId) {
                            targetPageId = pageId;
                          }
                        }
                      });

                      if (targetPageId !== page.id) {
                        // Move block to different page
                        const targetPageRect = document.querySelector(`[data-page-id="${targetPageId}"]`)?.getBoundingClientRect();
                        if (targetPageRect) {
                          const relativeX = (e.clientX - targetPageRect.left) / editorState.zoom;
                          const relativeY = (e.clientY - targetPageRect.top) / editorState.zoom;

                          // Remove from current page and add to target page
                          setEditorState(prev => ({
                            ...prev,
                            pages: prev.pages.map(p => {
                              if (p.id === page.id) {
                                return { ...p, blocks: p.blocks.filter(b => b.id !== block.id) };
                              } else if (p.id === targetPageId) {
                                return {
                                  ...p,
                                  blocks: [...p.blocks, {
                                    ...block,
                                    position: { x: relativeX, y: relativeY }
                                  }]
                                };
                              }
                              return p;
                            })
                          }));
                        }
                      } else {
                        // Normal drag within same page
                        updateBlock(block.id, {
                          position: {
                            x: d.x / editorState.zoom,
                            y: d.y / editorState.zoom
                          }
                        });
                      }
                    }}
                    onResize={(e, direction, ref, delta, position) => {
                      // Simple resize preview - no complex calculations needed
                      // Text will scale naturally with the container
                    }}
                    onResizeStop={(e, direction, ref, delta, position) => {
                      // Simple resize logic - same as other blocks
                      updateBlock(block.id, {
                        size: {
                          width: parseInt(ref.style.width) / editorState.zoom,
                          height: parseInt(ref.style.height) / editorState.zoom,
                        },
                        position: {
                          x: position.x / editorState.zoom,
                          y: position.y / editorState.zoom,
                        },
                      });
                    }}
                    bounds="parent"
                    className={`template-editor-block ${
                      editorState.previewMode
                        ? '' // No borders or hover effects in preview mode
                        : selectedBlockIds.includes(block.id)
                          ? selectedBlockIds.length > 1
                            ? getRingStyle('ring-2 ring-green-500 selected multi-selected')
                            : getRingStyle('ring-2 ring-blue-500 selected')
                          : getRingStyle(getHoverStyle('hover:ring-1 hover:ring-gray-300'))
                    }`}
                    style={{
                      ...(() => {
                        // Exclude transform/rotation from Rnd wrapper — react-rnd uses
                        // transform:translate(x,y) internally for positioning and any
                        // override here would clobber that. Rotation is applied on the
                        // inner div instead.
                        const { transform: _t, rotation: _r, ...rest } = (block.style || {}) as any;
                        return rest;
                      })(),
                      zIndex: block.zIndex,
                      padding: 0,
                      margin: 0,
                    }}
                    onClick={(e) => {
                      if (editorState.previewMode) return; // Disable selection in preview mode

                      const target = e.target as HTMLElement;
                      const isTextInput = target.tagName === 'TEXTAREA' ||
                                         target.tagName === 'INPUT' ||
                                         target.contentEditable === 'true' ||
                                         target.closest('.no-drag');

                      // Only select block if not clicking on text inputs
                      if (!isTextInput) {
                        e.stopPropagation();
                        selectBlock(block.id, e.ctrlKey || e.metaKey);
                      }
                    }}
                    onContextMenu={(e) => {
                      if (editorState.previewMode) return; // Disable context menu in preview mode
                      e.stopPropagation();
                      handleContextMenu(e, block.id);
                    }}
                  >
                    <motion.div
                      initial={false}
                      animate={{ opacity: 1, scale: 1 }}
                      className="w-full h-full"
                      data-block-id={block.id}
                    >
                      {/* Rotation wrapper — separate from motion.div so framer-motion's
                          own transform (scale/opacity) doesn't overwrite rotate() */}
                      <div
                        style={{
                          width: '100%',
                          height: '100%',
                          transform: block.style?.rotation
                            ? `rotate(${block.style.rotation}deg)`
                            : (block.style?.transform || undefined),
                          transformOrigin: 'center center',
                        }}
                      >
                        {renderBlockContent(block)}
                      </div>
                    </motion.div>
                        </Rnd>
                      ))}
                    </AnimatePresence>

                    {/* Page overflow indicator — red dashed stripe + badge when any block exceeds canvas height */}
                    {!editorState.previewMode && (() => {
                      const canvasH = typeof page.canvasSize?.height === 'number' ? page.canvasSize.height : 1123;
                      const hasOverflow = page.blocks.some(b =>
                        b.visible !== false &&
                        (b.position.y + b.size.height) > canvasH
                      );
                      if (!hasOverflow) return null;
                      return (
                        <>
                          {/* Dashed red stripe along the page bottom edge */}
                          <div
                            style={{
                              position: 'absolute',
                              bottom: 0,
                              left: 0,
                              right: 0,
                              height: 3,
                              background:
                                'repeating-linear-gradient(90deg,#ef4444 0,#ef4444 8px,transparent 8px,transparent 16px)',
                              pointerEvents: 'none',
                              zIndex: 9999,
                            }}
                          />
                          {/* Compact warning badge — bottom-right corner */}
                          <div
                            style={{
                              position: 'absolute',
                              bottom: 6,
                              right: 6,
                              background: '#ef4444',
                              color: '#fff',
                              fontSize: 10,
                              lineHeight: '1',
                              padding: '3px 7px',
                              borderRadius: 4,
                              pointerEvents: 'none',
                              zIndex: 10000,
                              fontFamily: 'sans-serif',
                              fontWeight: 600,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 3,
                              boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
                            }}
                          >
                            ⚠ Content overflow
                          </div>
                        </>
                      );
                    })()}

                    {/* Page Number — inside canvas footer, always auto-populated */}
                    {(editorState as any).showPageNumbers && (
                      <div
                        style={{
                          position: 'absolute',
                          bottom: 10 * editorState.zoom,
                          left: 0,
                          right: 0,
                          display: 'flex',
                          justifyContent: 'center',
                          pointerEvents: 'none',
                          zIndex: 9998,
                        }}
                      >
                        <span
                          style={{
                            fontSize: `${10 * editorState.zoom}px`,
                            color: '#9ca3af',
                            fontFamily: 'sans-serif',
                            fontWeight: '500',
                            background: 'rgba(255,255,255,0.85)',
                            padding: `${2 * editorState.zoom}px ${8 * editorState.zoom}px`,
                            borderRadius: '999px',
                            border: '1px solid #e5e7eb',
                            lineHeight: 1.4,
                            userSelect: 'none',
                          }}
                        >
                          {pageIndex + 1} / {editorState.pages.length}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {/* ── Add Page strip — below last page, editor mode only ── */}
              {!editorState.previewMode && (() => {
                const lastPage = editorState.pages[editorState.pages.length - 1];
                const stripWidth = (lastPage?.canvasSize?.width || 800) * editorState.zoom;
                return (
                  <div className="flex justify-center">
                    <button
                      onClick={addPage}
                      style={{ width: stripWidth }}
                      className="flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-dashed border-gray-300 text-gray-400 hover:border-blue-400 hover:text-blue-500 hover:bg-blue-50/60 transition-all duration-150 text-sm font-medium group"
                    >
                      <span className="w-6 h-6 rounded-full border-2 border-current flex items-center justify-center flex-shrink-0 group-hover:bg-blue-500 group-hover:border-blue-500 group-hover:text-white transition-all duration-150">
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                          <path d="M6 2v8M2 6h8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                        </svg>
                      </span>
                      Add Page
                    </button>
                  </div>
                );
              })()}
            </div>
          </div>
        </Panel>

        {!editorState.previewMode && (
          <PanelResizeHandle className="w-1 bg-gray-300 hover:bg-gray-400 transition-colors" />
        )}

        {/* Properties Panel - Hidden in preview mode */}
        {!editorState.previewMode && (
          <Panel defaultSize={20} minSize={15} maxSize={25}>
            <PropertyPanel
              selectedBlock={selectedBlock}
              onUpdateBlock={updateBlock}
              onDeleteBlock={deleteBlock}
              hasEditorContent={hasEditorContent}
              hasEditorFull={hasEditorFull}
            />
          </Panel>
        )}
      </PanelGroup>

      {/* ── Template Picker Modal ──────────────────────────────────────────── */}
      {(editorState as any).showTemplatePicker && !editorState.previewMode && (() => {
        const filtered = tplCategory === 'all'
          ? PAGE_LAYOUT_TEMPLATES
          : PAGE_LAYOUT_TEMPLATES.filter(t => t.category === tplCategory);

        // Detect the current page's dominant accent color for color-matching
        const detectedAccent = (() => {
          const page = getCurrentPage();
          if (!page) return '#3790C5';
          const freq: Record<string, number> = {};
          page.blocks.forEach(block => {
            const s = block.style as Record<string, unknown>;
            const c = block.content as Record<string, unknown>;
            [s?.backgroundColor, s?.color, c?.fillColor].forEach(raw => {
              if (typeof raw !== 'string' || raw.length !== 7 || !raw.startsWith('#')) return;
              const hex = raw.slice(1);
              const r = parseInt(hex.slice(0, 2), 16);
              const g = parseInt(hex.slice(2, 4), 16);
              const b = parseInt(hex.slice(4, 6), 16);
              const max = Math.max(r, g, b), min = Math.min(r, g, b);
              const sat = max === 0 ? 0 : (max - min) / max;
              const brt = max / 255;
              if (sat > 0.2 && brt > 0.2 && brt < 0.9) freq[raw] = (freq[raw] || 0) + 1;
            });
          });
          const sorted = Object.entries(freq).sort((a, b) => b[1] - a[1]);
          return sorted[0]?.[0] ?? '#3790C5';
        })();

        const closePicker = () => {
          setEditorState(prev => ({ ...prev, showTemplatePicker: false } as any));
          setPreviewTemplate(null);
          setConfirmReplace(null);
        };

        const applyTemplate = (tpl: LayoutTemplate, mode: 'add' | 'replace') => {
          saveToHistory(editorState);
          // Recolor template blocks to match detected accent
          const newBlocks = tpl.blocks.map(b => ({
            ...recolorTemplateBlock(b, '#3790C5', detectedAccent),
            id: `block-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
            visible: true,
            locked: false,
          }));
          if (mode === 'add') {
            const newPage = {
              id: `page-${Date.now()}`,
              name: tpl.name,
              blocks: newBlocks,
              canvasSize: { width: 794, height: 1123 },
              background: { type: 'solid' as const, color: '#ffffff' },
            };
            setEditorState(prev => ({ ...prev, pages: [...prev.pages, newPage], currentPageId: newPage.id, showTemplatePicker: false } as any));
          } else {
            setEditorState(prev => ({ ...prev, pages: prev.pages.map(p => p.id === prev.currentPageId ? { ...p, blocks: newBlocks } : p), showTemplatePicker: false } as any));
          }
          setPreviewTemplate(null);
          setConfirmReplace(null);
        };

        const colorChanged = detectedAccent.toLowerCase() !== '#3790c5';

        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" style={{ backdropFilter: 'blur(4px)' }}>
            {/* Backdrop click to close */}
            <div className="absolute inset-0" onClick={closePicker} />

            {/* Modal */}
            <div className="relative w-full max-w-5xl mx-4 bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden" style={{ maxHeight: '90vh' }}>

              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100" style={{ background: 'linear-gradient(135deg, #f0f7ff 0%, #ffffff 100%)' }}>
                <div>
                  <div className="flex items-center gap-2">
                    <LayoutTemplateIcon className="w-5 h-5 text-blue-600" />
                    <h2 className="font-bold text-gray-900 text-lg">Page Templates</h2>
                    {colorChanged && (
                      <div className="flex items-center gap-1.5 bg-blue-50 border border-blue-100 px-2.5 py-1 rounded-full">
                        <div className="w-2.5 h-2.5 rounded-full ring-1 ring-white shadow" style={{ backgroundColor: detectedAccent }} />
                        <span className="text-xs text-blue-700 font-medium">Colors matched</span>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {previewTemplate ? 'Preview template before applying' : 'Click any template to preview, then add or replace'}
                  </p>
                </div>
                <button onClick={closePicker} className="p-2 hover:bg-gray-100 rounded-xl text-gray-400 hover:text-gray-600 transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {previewTemplate ? (
                /* ── Preview Mode ──────────────────────────────────────────── */
                <div className="flex-1 overflow-hidden flex flex-col" style={{ minHeight: 0 }}>
                  {/* Preview sub-header */}
                  <div className="flex items-center gap-3 px-6 py-2.5 border-b border-gray-100 bg-gray-50">
                    <button
                      onClick={() => setPreviewTemplate(null)}
                      className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-800 font-medium transition-colors"
                    >
                      ← All templates
                    </button>
                    <div className="w-px h-4 bg-gray-200" />
                    <span className="font-semibold text-gray-800 text-sm">{previewTemplate.name}</span>
                    <span className="text-gray-400 text-xs hidden md:inline">— {previewTemplate.description}</span>
                  </div>

                  {/* Preview canvas */}
                  <div className="flex-1 overflow-auto flex items-start justify-center bg-gray-100 py-8 px-4" style={{ minHeight: 0 }}>
                    <div className="shadow-2xl rounded overflow-hidden border border-gray-200/50">
                      {renderTemplateSVG(previewTemplate, 340, detectedAccent)}
                    </div>
                  </div>

                  {/* Action bar */}
                  <div className="flex items-center gap-3 px-6 py-4 border-t border-gray-100 bg-white">
                    <button
                      onClick={() => applyTemplate(previewTemplate, 'add')}
                      className="flex-1 py-2.5 bg-blue-600 text-white rounded-xl font-semibold text-sm hover:bg-blue-700 active:scale-[0.98] transition-all shadow-sm"
                    >
                      + Add as New Page
                    </button>
                    <button
                      onClick={() => setConfirmReplace(previewTemplate)}
                      className="flex-1 py-2.5 border-2 border-gray-200 text-gray-700 rounded-xl font-semibold text-sm hover:bg-gray-50 hover:border-gray-300 active:scale-[0.98] transition-all"
                    >
                      Replace Current Page
                    </button>
                  </div>
                </div>
              ) : (
                /* ── Grid Mode ─────────────────────────────────────────────── */
                <div className="flex-1 overflow-hidden flex flex-col" style={{ minHeight: 0 }}>
                  {/* Category pills */}
                  <div className="flex gap-2 px-6 pt-4 pb-3 flex-wrap">
                    {(['all', 'document', 'data', 'design'] as const).map(cat => {
                      const count = cat === 'all' ? PAGE_LAYOUT_TEMPLATES.length : PAGE_LAYOUT_TEMPLATES.filter(t => t.category === cat).length;
                      return (
                        <button
                          key={cat}
                          onClick={() => setTplCategory(cat)}
                          className={`px-4 py-1.5 text-xs rounded-full font-semibold capitalize transition-all ${tplCategory === cat ? 'bg-blue-600 text-white shadow-sm' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                        >
                          {cat === 'all' ? 'All' : cat.charAt(0).toUpperCase() + cat.slice(1)}
                          <span className={`ml-1.5 ${tplCategory === cat ? 'opacity-70' : 'opacity-50'}`}>{count}</span>
                        </button>
                      );
                    })}
                  </div>

                  {/* Template grid */}
                  <div className="flex-1 overflow-y-auto px-6 pb-6">
                    <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))' }}>
                      {filtered.map(tpl => (
                        <div
                          key={tpl.id}
                          onClick={() => setPreviewTemplate(tpl)}
                          className="group cursor-pointer rounded-xl overflow-hidden border-2 border-transparent hover:border-blue-400 bg-white shadow-sm hover:shadow-xl transition-all duration-200"
                        >
                          {/* SVG wireframe thumbnail with A4 aspect ratio */}
                          <div className="relative overflow-hidden bg-gray-50" style={{ aspectRatio: '1 / 1.414' }}>
                            {renderTemplateSVG(tpl, 200, detectedAccent)}
                            {/* Hover overlay */}
                            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-150" style={{ background: 'rgba(55,144,197,0.15)' }}>
                              <span className="bg-blue-600 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg translate-y-2 group-hover:translate-y-0 transition-transform duration-150">
                                Preview
                              </span>
                            </div>
                          </div>
                          {/* Card label */}
                          <div className="px-3 py-2.5 border-t border-gray-100">
                            <p className="text-xs font-bold text-gray-800 truncate leading-tight">{tpl.name}</p>
                            <p className="text-xs text-gray-400 mt-0.5 leading-tight" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{tpl.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Confirm Replace Dialog */}
            {confirmReplace && (
              <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50">
                <div className="bg-white rounded-2xl shadow-2xl p-6 w-96 mx-4">
                  <div className="w-11 h-11 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center mb-4 text-xl">⚠️</div>
                  <h3 className="font-bold text-gray-900 mb-1 text-base">Replace current page?</h3>
                  <p className="text-sm text-gray-500 mb-5 leading-relaxed">
                    All blocks on the current page will be replaced with the <strong className="text-gray-800">{confirmReplace.name}</strong> template. You can undo this with Ctrl+Z.
                  </p>
                  <div className="flex gap-2">
                    <button onClick={() => setConfirmReplace(null)} className="flex-1 px-4 py-2.5 text-sm border-2 border-gray-200 rounded-xl font-medium hover:bg-gray-50 transition-colors">
                      Cancel
                    </button>
                    <button onClick={() => applyTemplate(confirmReplace, 'replace')} className="flex-1 px-4 py-2.5 text-sm bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors shadow-sm">
                      Replace Page
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Advanced Table Controls Modal */}
      {advancedTableEditor && (
        <AdvancedTableControls
          block={getCurrentPage().blocks.find(b => b.id === advancedTableEditor)}
          onUpdateBlock={updateBlock}
          onClose={() => setAdvancedTableEditor(null)}
        />
      )}

      {/* Context Menu */}
      <ContextMenu
        isOpen={contextMenu.isOpen}
        position={contextMenu.position}
        onClose={closeContextMenu}
        onEdit={handleContextMenuEdit}
        onCopy={copySelectedBlocks}
        onPaste={pasteBlocks}
        onDuplicate={duplicateBlocks}
        onDelete={() => deleteBlock()}
        onBringToFront={handleContextMenuBringToFront}
        onSendToBack={handleContextMenuSendToBack}
        onLock={() => {/* TODO: Implement lock functionality */}}
        onUnlock={() => {/* TODO: Implement unlock functionality */}}
        onHide={() => {/* TODO: Implement hide functionality */}}
        onShow={() => {/* TODO: Implement show functionality */}}
        canPaste={clipboardManager.hasClipboardData()}
        isLocked={false}
        isHidden={false}
        selectedCount={selectedBlockIds.length > 0 ? selectedBlockIds.length : (editorState.selectedBlockId ? 1 : 0)}
        hasEditorContent={hasEditorContent}
        hasEditorFull={hasEditorFull}
        hasEditorLayout={hasEditorLayout}
      />

      {/* CSS for drag and drop effects */}
      <style jsx>{`
        .template-page.drag-over {
          box-shadow: 0 0 0 2px #10b981 !important;
          background-color: rgba(16, 185, 129, 0.05) !important;
          transition: all 0.2s ease;
        }

        .template-page {
          transition: all 0.2s ease;
        }
      `}</style>

      {/* Page Background Sidebar */}
      <PageBackgroundSidebar
        background={getCurrentPage().background}
        onChange={updatePageBackground}
        onClose={() => setShowPageBackgroundPanel(false)}
        isOpen={showPageBackgroundPanel}
      />

      {/* Global Typography Modal */}
      <GlobalTypographyModal
        isOpen={showGlobalTypographyModal}
        onClose={() => setShowGlobalTypographyModal(false)}
        globalTypography={editorState.globalTypography || {
          fontFamily: 'Inter, sans-serif',
          fontSize: 16,
          fontWeight: '400',
          lineHeight: 1.4,
          letterSpacing: 0,
          color: '#000000'
        }}
        onChange={updateGlobalTypography}
      />

      {/* Save Template Modal - Only show for template context */}
      {context === 'template' && (
        <SaveTemplateModal
          isOpen={showSaveModal}
          onClose={() => setShowSaveModal(false)}
          onSave={handleSaveTemplate}
          isExistingTemplate={!!currentTemplateId}
          currentTemplateName={currentTemplateName}
          currentTemplateDescription={currentTemplateDescription}
          loading={saveLoading}
        />
      )}

      {/* Page Template Selection Modal */}
      <PageTemplateModal
        isOpen={showPageTemplateModal}
        onClose={() => setShowPageTemplateModal(false)}
        onSelectTemplate={handleTemplateSelection}
      />

      {/* Export Dialog */}
      <ExportDialog
        editorState={editorState}
        isOpen={showExportDialog}
        onClose={() => setShowExportDialog(false)}
        templateName={currentTemplateName || 'template'}
      />

    </div>
  );
});

// Set display name for dev tools
ModernTemplateEditor.displayName = 'ModernTemplateEditor';

export default ModernTemplateEditor;
